import { Component, ViewChild, OnDestroy, ChangeDetectorRef, OnInit, ChangeDetectionStrategy, SimpleChanges, OnChanges, AfterViewInit, ElementRef, TemplateRef, NgZone } from '@angular/core';
import { DatePickerModule } from '@syncfusion/ej2-angular-calendars';
import {
  EventSettingsModel,
  DayService,
  WeekService,
  WorkWeekService,
  MonthService,
  AgendaService,
  ScheduleModule,
  ScheduleComponent,
  TimelineViewsService,
  TimelineMonthService
} from '@syncfusion/ej2-angular-schedule';
import {addDays, addHours, startOfToday, subDays } from 'date-fns';
import { AuthService } from '../../../services/auth/auth.service';
import { filter as rxFilter, switchMap, distinctUntilChanged, takeUntil, debounceTime, retryWhen, tap, delayWhen, finalize, catchError, first, timeout, takeWhile, map, defaultIfEmpty, skip } from 'rxjs/operators';
import { Subject, combineLatest, timer, Observable, of, take, interval, forkJoin } from 'rxjs';
import { loadCldr, L10n } from '@syncfusion/ej2-base';
import daNumberData from '@syncfusion/ej2-cldr-data/main/da/numbers.json';
import daTimeZoneData from '@syncfusion/ej2-cldr-data/main/da/timeZoneNames.json';
import daGregorian from '@syncfusion/ej2-cldr-data/main/da/ca-gregorian.json';
import numberingSystem from '@syncfusion/ej2-cldr-data/supplemental/numberingSystems.json';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TemplateService } from '../../../services/template/template.service';
import { Subscription } from 'rxjs';
import { ComponentStateService } from '../../../services/component-state/component-state.service';
import { FormsModule } from '@angular/forms';
import { EventsService } from '../../../services/events/events.service';
import { ModalService } from '../../../services/modal/modal.service';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { EventDTO } from '../../../dto/event/event.dto';
import {
  normalizeEventIsActive,
  normalizeEventIsShown,
} from '../../../services/component-data-source/data/event/event-active.util';
import {
  buildEventCategoryInheritanceContext,
  EventCategoryInheritanceContext,
  EventCategoryColorsMap,
  normalizeEventCategoryColors,
  resolveEventCategoryBackgroundColor,
  resolveEventCategoryBorderColor,
} from '../../../services/component-data-source/data/event/event-category-color.util';
import { Store } from '@ngrx/store';
import * as DataActions from '../../../ngrx/data/data.actions';
import * as EventsActions from '../../../ngrx/events/events.actions';
import { isEventStartInDateRange } from '../../../ngrx/events/events-calendar-cache.util';
import * as EventsSelectors from '../../../ngrx/events/events.selectors';
import { AppState } from '../../../ngrx/template/template.reducers';
import { ComponentDataFilterService } from '../../../services/component-data-filter/component-data-filter.service';
import { EventsFilter } from '../../../services/component-data-filter/data/events-filter';
import { EventsFilterStateService } from '../../../services/events-filter/events-filter-state.service';
import { EventQueryParams } from '../../../services/events-filter/events-filter.model';
import { DunaEventsFilterComponent } from '../duna-events-filter/duna-events-filter.component';
import { DunaEventsFilterOverlayService } from '../../../services/duna-events-filter-overlay/duna-events-filter-overlay.service';
import { buildNewEventDateDefaults } from '../../../services/component-data-dest/data/add-event';
import {
  DunaCalendarDayEventsDialogComponent,
  DunaCalendarDayEventsDialogData
} from './duna-calendar-day-events-dialog.component';
import {
  eventsCalendarViewToUrlParam,
  parseEventsCalendarViewFromUrl,
} from './events-calendar-view-url.util';
import {
  getEventTypeLabelDa,
  getEventTypeMaterialIcon,
} from '../../../utils/event-type-display.util';
import { shouldShowEventVisibilityLabel } from '../../../utils/event-visibility-display.util';

loadCldr(daNumberData, daTimeZoneData, daGregorian, numberingSystem);

L10n.load({
  'da': {
    'schedule': {
      'day': 'Dag',
      'week': 'Uge',
      'workWeek': 'Arbejdsuge',
      'month': 'Måned',
      'agenda': 'Dagsorden',
      'today': 'I dag',
      'more': 'mere',
      'moreDetails': 'flere detaljer',
      'moreEvents': 'flere events'
      // Add more translations as needed
    }
  }
});

interface TableColumn {
  key: string;
  header: string;
  type: 'text' | 'button' | 'date' | 'boolean';
  buttonText?: string;
  buttonColor?: string;
  variant?: string;
  action?: (row: any) => void;
  transform?: (value: any, row?: any) => any;
}

interface TableSettings {
  columns: TableColumn[];
  pageable?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
  sortable?: boolean;
  filterable?: boolean;
  rowHover?: boolean;
  striped?: boolean;
  dense?: boolean;
  searchable?: boolean;
  defaultSortColumn?: string;
  defaultSortDirection?: 'asc' | 'desc';
}

@Component({
  selector: 'app-duna-events-page',
  standalone: true,
  providers: [
    DayService, 
    WeekService, 
    WorkWeekService, 
    MonthService, 
    AgendaService,
    TimelineViewsService,
    TimelineMonthService
  ],
  imports: [
    ScheduleModule,
    DatePickerModule,
    CommonModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatIconModule,
    MatTooltipModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    RouterModule,
    DunaEventsFilterComponent
  ],
  templateUrl: './duna-events-calendar.component.html',
  styleUrl: './duna-events-calendar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // duna-full-bleed makes the calendar span edge-to-edge so that the Syncfusion scheduler's
  // internal backgrounds (set via ::ng-deep) reach the screen edges rather than being
  // constrained by the global 14px side-padding rule. The calendar's own CSS handles
  // internal spacing via its component styles.
  host: { class: 'duna-full-bleed' }
})
export class DunaEventsCalendarComponent implements OnInit, OnDestroy, AfterViewInit {
  private static instanceCount = 0;
  private instanceId: number;
  private initialized = false;
  private eventCategoryColors: EventCategoryColorsMap = {};
  private eventCategoryColorsOrgId: number | null = null;
  private eventCategoryInheritanceContext: EventCategoryInheritanceContext =
    buildEventCategoryInheritanceContext([]);
  private destroy$ = new Subject<void>();
  
  today = startOfToday();
  viewType: 'calendar' | 'table' = 'calendar';
  currentMode: number = 1;
  calendarScheduleView: 'Day' | 'Week' | 'Month' = 'Month';
  readonly calendarScheduleViewOptions: {
    value: 'Day' | 'Week' | 'Month';
    label: string;
    icon: string;
    tooltip: string;
  }[] = [
    { value: 'Day', label: 'Dag', icon: 'calendar_view_day', tooltip: 'Dagsvisning' },
    { value: 'Week', label: 'Uge', icon: 'calendar_view_week', tooltip: 'Ugevisning' },
    { value: 'Month', label: 'Måned', icon: 'calendar_view_month', tooltip: 'Månedsvisning' }
  ];
  private static readonly SCHEDULE_VIEW_STORAGE_KEY = 'duna-events-calendar-schedule-view';

  @ViewChild('scheduleObj') public scheduleObj!: ScheduleComponent;
  @ViewChild('calendarWrapper') private calendarWrapper?: ElementRef<HTMLElement>;
  // @ViewChild(DynamicTableComponent) public tableComponent!: DynamicTableComponent; // Removed - now using card view
  public tableComponent: any; // Keep for backward compatibility
  @ViewChild('tableContainer') public tableContainer!: ElementRef;
  @ViewChild(DunaEventsFilterComponent) public filterComponent!: DunaEventsFilterComponent;

  public data: EventDTO[] = [];
  private calendarData: any[] = []; // Start with empty array
  public settings: any;
  private eventsDataSource: any;
  public tableDataSource: { items: any[], totalCount: number, pageSize: number, pageIndex: number } = {
    items: [],
    totalCount: 0,
    pageSize: 10,
    pageIndex: 0
  };

  public selectedDate: Date = new Date(); // Start with current month

  public eventSettings: EventSettingsModel = {
    dataSource: [], // Start with empty array to prevent initial data requests
    allowEditing: false,
    allowDeleting: false,
    allowAdding: false,
    fields: {
      id: 'Id',
      subject: 'Subject',
      startTime: 'StartTime',
      endTime: 'EndTime',
      isAllDay: 'IsAllDay',
      resourceId: 'ResourceId'
    }
  } as EventSettingsModel;

  // Make dataSourceKey public
  public readonly dataSourceKey = 'events_calendar_data';

  private currentFilters: any = {};
  private currentEventsFilter: EventsFilter | null = null;

  // Change the subscriptions property definition from Subscription to an array
  private subscriptions: Subscription[] = [];

  // Add a property to track transform calls
  private transformCallCount = 0;

  // Add a property to store the filter subscription
  private filterSubscription: Subscription | null = null;

  // Add a property to track the current data load subscription
  private dataLoadSubscription: Subscription | null = null;
  
  // Add a property to track the filter ready subscription
  private filterReadySubscription: Subscription | null = null;
  private filterStateSubscription: Subscription | null = null;

  // Add debounce timer for data loading
  private dataLoadDebounceTimer: any = null;

  // Add safety timeout for data loading
  private safetyTimeout: any = null;

  // Add loading state
  isInitialLoading = true;
  public isLoadingData = false; // Public flag to show loading indicator during any data loading
  public isFilterLoading = true; // Track filter component loading state
  private isUpdatingCalendar = false; // Flag to prevent concurrent calendar updates
  private isLoadingFreshData = false; // Flag to prevent filter events from overriding fresh data
  private hasAttemptedDataLoad = false; // Flag to prevent infinite reload loops
  private isUserInteracting = false; // Flag to track user-initiated filter changes
  private isLoadInProgress = false; // Flag to prevent duplicate concurrent loads
  private lastLoadParams: string = ''; // Track last load params to prevent duplicate loads
  private lastLoadedDateRange: { startDate: string; endDate: string } | null = null; // Track date range of last loaded data
  private currentExpectedDateRange: { startDate: string; endDate: string } | null = null; // Track expected date range for current view
  private lastExecutedQueryParams: Record<string, unknown> | null = null;
  private pendingForceLoad = false;
  private filterBootstrapInProgress = false;
  private calendarLoadDispatched = false;
  private lastCriteriaKey = '';
  private lastEventCacheFilterKey = '';
  private replaceEventCacheOnNextLoad = true;
  /** Query params of the last store payload actually painted in the UI. */
  private lastAppliedStoreQueryParams: Record<string, unknown> | null = null;
  private hasLoggedEducationalPathsNotReady = false; // Flag to prevent console spam from educational paths logging

  // Add new properties for responsive filter panel
  public isFilterPanelOpen = false;
  public activeFilterCount = 0;
  private keydownHandler?: (event: KeyboardEvent) => void;
  private calendarDayClickHandler?: (event: MouseEvent) => void;
  
  // Date range properties for list view
  public listViewStartDate: string = '';
  public listViewEndDate: string = '';
  private hasInitializedDateRange = false; // Track if date range has been initialized
  private currentCalendarMonth: Date = new Date(); // Start with current month
  private currentCalendarYear: number = new Date().getFullYear();
  public hasLoadedInitialData: boolean = false; // Flag to prevent duplicate initial loads (public for template empty-state)
  /** True only when the last completed load returned 0 events. Prevents "Ingen begivenheder" flashing before data is applied. */
  public lastLoadResultWasEmpty = true;
  /** True while list view waits for data matching its date range (prevents showing stale calendar events). */
  public isListViewRefreshing = false;
  private lastToggleState: boolean | null = null;
  private dateRangeChange$ = new Subject<void>();

  readonly datePresetButtons: { id: string; label: string }[] = (() => {
    const year = new Date().getFullYear();
    return [
      { id: 'thisYear', label: String(year) },
      { id: 'nextYear', label: String(year + 1) },
      { id: '3months', label: '3 mdr' },
      { id: '6months', label: '6 mdr' },
      { id: '12months', label: '12 mdr' },
      { id: 'restOfYear', label: 'Resten af året' },
    ];
  })();

  // Card view properties
  public filteredCardData: any[] = [];
  public paginatedCardData: any[] = [];
  public cardPageIndex: number = 0;
  public cardPageSize: number = 12;
  public Math = Math; // Make Math available in template

  /**
   * Per-org setting events_hide_course_capacity_public: when true, hide capacity UI for course (kursus) events.
   * Keyed by organization id; omitted entry means not loaded yet (counts shown until loaded).
   */
  private hideCourseCapacityByOrgId = new Map<number, boolean>();
  private courseCapacityOrgSettingsFetched = new Set<number>();

  // Initialize default date range for list view (3 months forward)
  private initializeDefaultDateRange(): void {
    // Only initialize if not already initialized (prevents resetting user's custom dates)
    if (this.hasInitializedDateRange) {
      return;
    }
    
    // Get today's date in local timezone
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day
    
    // Calculate 3 months from now
    const threeMonthsFromNow = new Date(today);
    threeMonthsFromNow.setMonth(today.getMonth() + 3);
    threeMonthsFromNow.setHours(23, 59, 59, 999); // Set to end of day
    
    // Format as YYYY-MM-DD strings (ISO date format, local timezone)
    const formatDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    this.listViewStartDate = formatDate(today);
    this.listViewEndDate = formatDate(threeMonthsFromNow);
    this.hasInitializedDateRange = true;
  }
  

  constructor(
    private router: Router, 
    private templateService: TemplateService, 
    private componentStateService: ComponentStateService,
    private eventsService: EventsService,
    private cdr: ChangeDetectorRef,
    private modalService: ModalService,
    private authService: AuthService,
    private componentDataSourceService: ComponentDataSourceService,
    private filterService: ComponentDataFilterService,
    private route: ActivatedRoute,
    private store: Store<AppState>,
    private filterOverlay: DunaEventsFilterOverlayService,
    private dialog: MatDialog,
    private filterState: EventsFilterStateService,
    private ngZone: NgZone
  ) {
    this.instanceId = ++DunaEventsCalendarComponent.instanceCount;
  }

  ngOnInit() {
    
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    this.restoreCalendarScheduleView();
    this.loadEventCategoryColors();

    // Set loading state immediately
    this.isInitialLoading = true;
    this.cdr.detectChanges();

    // Drop global calendar cache so we never paint list-view / previous-page data first.
    this.store.dispatch(EventsActions.clearEventsData());

    // Subscribe to NGRX store for loading state and data
    this.setupStoreSubscriptions();

    // Parse URL parameters immediately to set date range before filter initialization
    this.parseUrlParameters();

    // Add keyboard navigation support
    this.setupKeyboardNavigation();

    // Initialize settings first - make sure this happens before any computed contexts
    this.initializeTableSettings();

    // Register our own data source for the table
    try {
      this.componentDataSourceService.registerDataSource(this.dataSourceKey, {
        name: this.dataSourceKey,
        description: 'Events calendar data source',
        execute: (params: any) => {
          // Return the current data
          return of({
            items: this.data,
            totalCount: this.data.length,
            pageSize: this.settings?.pageSize || 10,
            pageIndex: 0
          });
        }
      });
    } catch (error) {
      console.error(`[DUNA-EVENTS] Error registering data source ${this.dataSourceKey}:`, error);
    }

    // Initialize data source first
    this.eventsDataSource = this.componentDataSourceService.getDataSource('events');
    if (!this.eventsDataSource) {
      console.error(`[DUNA-EVENTS] Events data source not found for instance ${this.instanceId}`);
      this.isInitialLoading = false;
      this.cdr.detectChanges();
      return;
    }

    // Add listener for the filter component's custom event with filtered events
    window.addEventListener('duna-filter-count', this.handleFilterEvent);

    // Add listener for filter ready event
    window.addEventListener('duna-filter-ready', this.handleFilterReadyEvent);
    
    // Add listener for user interaction events
    window.addEventListener('duna-filter-user-interaction', this.handleUserInteractionEvent);

    // CRITICAL: URL dates (startDate/endDate) are ONLY for list view
    // Calendar view should ALWAYS calculate its own dates, regardless of URL parameters
    // The URL dates are stored in listViewStartDate/listViewEndDate for list view use only

    // Wait for filter to be fully ready before loading data
    this.waitForFilterReady();

    this.subscriptions.push(
      this.dateRangeChange$.pipe(debounceTime(400)).subscribe(() => this.applyDateRange())
    );

    const onAuthFilterChange = () => {
      this.lastLoadParams = '';
    };
    window.addEventListener('duna-filter-auth-changed', onAuthFilterChange);
    this.subscriptions.push(new Subscription(() => {
      window.removeEventListener('duna-filter-auth-changed', onAuthFilterChange);
    }));

    this.authService.authStateReady$.pipe(
      rxFilter(Boolean),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.loadEventCategoryColors();
      setTimeout(() => {
        this.bindCalendarDayClickHandler();
        if (this.scheduleObj && this.viewType === 'calendar') {
          this.scheduleObj.dataBind();
        }
      }, 0);
    });
  }

  private setupStoreSubscriptions(): void {
    // Subscribe to loading state from store
    this.store.select(EventsSelectors.selectCalendarLoading).pipe(
      takeUntil(this.destroy$)
    ).subscribe(loading => {
      // Update local loading flag for backward compatibility
      this.isLoadingData = loading;
      
      // Reset load in progress flag when a dispatched load completes
      if (!loading && this.calendarLoadDispatched) {
        this.isLoadInProgress = false;
        this.calendarLoadDispatched = false;
        this.finalizeListViewAfterLoad();
      } else if (!loading) {
        this.isLoadInProgress = false;
      }
      
      // CRITICAL: Clear isInitialLoading when loading completes AND data has been applied
      if (!loading && this.isInitialLoading && this.hasLoadedInitialData) {
        this.isInitialLoading = false;
      }
      this.cdr.detectChanges();
    });

    // Subscribe to data from store
    // CRITICAL: Combine data with lastUpdated timestamp to ensure subscription fires
    // even when data appears the same but was reloaded (different timestamp)
    combineLatest([
      this.store.select(EventsSelectors.selectCalendarData),
      this.store.select(EventsSelectors.selectCalendarLastUpdated),
      this.store.select(EventsSelectors.selectCalendarLastQueryParams),
    ]).pipe(
      takeUntil(this.destroy$),
      distinctUntilChanged((prev, curr) => {
        const [prevData, prevTimestamp] = prev;
        const [currData, currTimestamp] = curr;
        void prev[2];
        void curr[2];

        if (currTimestamp > prevTimestamp) {
          return false;
        }
        
        // CRITICAL: Compare data first, not timestamp
        // Timestamp can change even when data is the same (causing duplicate fires)
        // Only fire if data actually changed
        const prevCount = prevData?.length || 0;
        const currCount = currData?.length || 0;
        
        // If count changed, data definitely changed
        if (prevCount !== currCount) {
          return false; // Not equal, fire subscription
        }
        
        // If count is same, compare by guids to detect actual data changes
        const prevGuids = prevData?.map((e: any) => e?.guid).sort().join(',') || '';
        const currGuids = currData?.map((e: any) => e?.guid).sort().join(',') || '';
        
        const dataChanged = prevGuids !== currGuids;
        if (dataChanged) {
          return false; // Not equal, fire subscription
        }
        
        // Data is the same (same count and same guids)
        // Only skip if we're going from empty to empty (both are 0) OR if we already have data displayed
        // This prevents flicker while still allowing initial load to display
        const isBothEmpty = prevCount === 0 && currCount === 0;
        const isRefreshWithSameData = prevCount > 0 && currCount > 0 && prevGuids === currGuids;
        
        // CRITICAL: Check if we're going from having data to empty FIRST
        // This ensures the calendar clears when filters result in no events
        const hadDataBefore = prevCount > 0;
        const isEmpty = currCount === 0;
        
        // CRITICAL: If going from having data to empty, always update to clear the calendar
        // Also check if component currently has data (this.data) to ensure we clear even if prevCount is 0
        // This handles cases where the store subscription might miss the transition
        if (hadDataBefore && isEmpty) {
          return false; // Not equal, fire subscription to clear calendar
        }
        
        // CRITICAL: Also check if component has data but store is empty (handles edge cases)
        // This ensures we clear even if distinctUntilChanged missed the transition
        if (isEmpty && prevTimestamp > 0 && currTimestamp > 0 && currTimestamp > prevTimestamp) {
          // Store is empty and timestamp increased - this is a new empty result, always update
          return false; // Not equal, fire subscription to clear calendar
        }
        
        // Only skip if we're going from empty to empty (both are 0) AND we were already empty
        // AND timestamps are the same (same empty result, not a new one)
        const wasEmpty = prevCount === 0;
        const listRangeChanged = this.viewType === 'table' && !!this.listViewStartDate && !!this.listViewEndDate &&
          (!this.lastLoadedDateRange ||
            this.lastLoadedDateRange.startDate !== this.listViewStartDate ||
            this.lastLoadedDateRange.endDate !== this.listViewEndDate);
        if (listRangeChanged && isBothEmpty) {
          return false;
        }
        if (isBothEmpty && prevTimestamp > 0 && currTimestamp > 0 && wasEmpty && prevTimestamp === currTimestamp) {
          // Both empty and both have the same timestamp - skip duplicate empty emissions
          return true; // Equal, don't fire subscription
        }
        
        // CRITICAL: Check if date range has changed - if so, always update even if GUIDs are same
        // This handles the case where switching months might result in same events (overlapping date ranges)
        // but we still need to update because the expected date range has changed
        const currentDateRange = this.currentExpectedDateRange;
        const lastDateRange = this.lastLoadedDateRange;
        const dateRangeChanged = currentDateRange && lastDateRange && 
          (currentDateRange.startDate !== lastDateRange.startDate || currentDateRange.endDate !== lastDateRange.endDate);
        
        if (dateRangeChanged) {
          return false; // Not equal, fire subscription to update calendar
        }
        
        // CRITICAL: Only skip refresh with same data if guids are EXACTLY the same AND date range hasn't changed
        // This check must come AFTER the count change check above, so it only applies when count is same
        // If guids are different, data has changed and we must update
        if (isRefreshWithSameData && prevTimestamp > 0 && currTimestamp > 0 && prevTimestamp !== currTimestamp) {
          // Never skip when the view has not yet applied store data (e.g. after bootstrap cleared local arrays)
          if (!this.hasLoadedInitialData || (this.data?.length ?? 0) === 0) {
            return false;
          }
          return true; // Equal, don't fire subscription
        }
        
        // If we get here, it's either:
        // 1. First load (prevTimestamp === 0) with data - always fire
        // 2. Data changed - already handled above
        // 3. Initial state (both null/undefined) - allow first emission
        return false; // Not equal, fire subscription
      }),
      // CRITICAL: Only process emissions with valid timestamps (actual data loads)
      // But allow initial null/undefined emissions to pass through for initial state handling
      rxFilter(([data, lastUpdated]: [any[], number, Record<string, unknown> | null]) => {
        // Allow if timestamp > 0 (valid load) OR if this is initial state (timestamp === 0 and data is null/undefined)
        // This ensures we don't skip the first valid data emission
        return lastUpdated > 0 || (lastUpdated === 0 && (data === null || data === undefined));
      }),
      // CRITICAL: Debounce to prevent rapid successive updates that cause flickering
      // Increased debounce time to reduce flickering when filters change
      debounceTime(150)
    ).subscribe(([data, lastUpdated, storeQueryParams]) => {
      if (
        storeQueryParams &&
        this.lastExecutedQueryParams &&
        !this.storeResponseMatchesExecutedLoad(storeQueryParams)
      ) {
        return;
      }

      if (
        this.viewType === 'calendar' &&
        lastUpdated > 0 &&
        storeQueryParams &&
        !this.loadedRangeCoversVisibleCalendar(storeQueryParams)
      ) {
        return;
      }

      // CRITICAL: Prevent concurrent updates that cause flickering, BUT never drop genuinely new data
      // When store emits while an update is in progress, skip only if payload is the same as current.
      // If payload differs (e.g. new month, new events), allow it so events don't stay missing.
      if (this.isUpdatingCalendar) {
        const incomingSameAsCurrent =
          data != null &&
          Array.isArray(data) &&
          this.data != null &&
          this.data.length === data.length &&
          data.length > 0 &&
          data.map((e: any) => e?.guid).sort().join(',') === (this.data?.map((e: any) => e?.guid).sort().join(',') || '');
        if (incomingSameAsCurrent) {
          return;
        }
        this.isUpdatingCalendar = false;
      }
      
      // CRITICAL: Handle empty data arrays (length 0) as valid data
      // This ensures loading states are cleared even when no events are found
      if (data !== null && data !== undefined && Array.isArray(data)) {
        
        // CRITICAL: Get the current filter to ensure frontend filtering uses the latest state
        // The store data should already be filtered by the backend, but we apply frontend filters too
        const filter = this.filterService.getFilter('events-filter');
        if (filter) {
          filter.getFilter().pipe(take(1)).subscribe((filterValue: EventsFilter) => {
            // Update current filter to ensure frontend filtering is correct
            this.currentEventsFilter = filterValue;
          });
        }
        
        // Process the data the same way as the original code
        // Preprocess the data to add title property
        let processedData = this.preprocessEvents(data);
        if (this.viewType === 'calendar') {
          processedData = this.filterEventsToCalendarVisibleRange(processedData);
        }

        // Apply frontend filters (hideSubEvents only - educational paths and event types are backend filtered)
        const filteredData = this.applyFrontendFilters(processedData);

        // CRITICAL: The data from the store should already be filtered correctly by the backend
        // based on the filters (educational paths, event types, organizations) that were sent in the request.
        // We only apply frontend filters (hideSubEvents) here.
        // The store is the source of truth - always use the data it provides.
        let finalData = filteredData;
        
        // Log which view we're in for debugging

        // CRITICAL: The store is the source of truth - ALWAYS update data when it comes from the store
        // This ensures filters are properly applied and old data is cleared
        // We'll prevent flickering by only updating the calendar display if data actually changed
        
        // CRITICAL: Update the last loaded date range so we can detect range changes
        // and avoid wrongly skipping the next load as "duplicate"
        if (this.viewType === 'calendar' && this.currentExpectedDateRange) {
          this.lastLoadedDateRange = { ...this.currentExpectedDateRange };
        } else if (this.viewType === 'table' && this.listViewStartDate && this.listViewEndDate) {
          this.lastLoadedDateRange = { startDate: this.listViewStartDate, endDate: this.listViewEndDate };
        }
        
        // Compare by guids to detect if data actually changed (for calendar update optimization)
        const currentGuids = this.data?.map((e: any) => e?.guid).sort().join(',') || '';
        const newGuids = finalData?.map((e: any) => e?.guid).sort().join(',') || '';
        const dataChanged = currentGuids !== newGuids || this.data?.length !== finalData.length;
        
        // CRITICAL: Check if calendar data would actually change
        // This prevents flickering when filters change but calendar events remain the same
        let calendarDataWouldChange = false;
        if (this.viewType === 'calendar') {
          // Transform new data to calendar format to compare
          const newCalendarGuids = finalData
            ?.filter((event: EventDTO) => event && event.guid)
            .map((event: EventDTO) => event.guid)
            .sort()
            .join(',') || '';
          const currentCalendarGuids = this.calendarData?.map((e: any) => e?.Id).sort().join(',') || '';
          calendarDataWouldChange = newCalendarGuids !== currentCalendarGuids || 
            (this.calendarData?.length || 0) !== (finalData?.filter((e: EventDTO) => e && e.guid).length || 0);
        }
        
        // CRITICAL: If going from having data to empty (or vice versa), always update
        const goingToEmpty = finalData.length === 0 && this.data && this.data.length > 0;
        const goingFromEmpty = finalData.length > 0 && this.data && this.data.length === 0;
        const mustUpdate = goingToEmpty || goingFromEmpty;
        
        // CRITICAL: ALWAYS update data from store - it's the source of truth
        // This ensures filters are properly applied and old data is cleared
        this.data = finalData;
        if (storeQueryParams) {
          this.lastAppliedStoreQueryParams = { ...storeQueryParams };
        }
        this.refreshEventCategoryInheritanceContext();
        this.lastLoadResultWasEmpty = finalData.length === 0;

        this.loadOrgSettingsForCourseCapacity(finalData);

        // Update card view data - this will update paginatedCardData
        this.updateCardData();
        if (this.viewType === 'table') {
          this.isListViewRefreshing = false;
        }
        this.syncFacetsAfterDisplayUpdate(processedData);

        // CRITICAL: Always update calendar when in calendar view and data came from store
        // The store is the source of truth - we must update to reflect the current state
        // We'll prevent flickering inside updateCalendarData() by checking if data actually changed
        const shouldUpdateCalendar = this.viewType === 'calendar';
        
        // Set flag to prevent concurrent updates
        this.isUpdatingCalendar = true;
        
        try {
          // Update the calendar data (only if in calendar view AND data changed to prevent unnecessary updates)
          if (shouldUpdateCalendar) {
            this.updateCalendarData();
          } else if (this.viewType === 'calendar' && !dataChanged) {
            // Data unchanged - skip calendar update to prevent flicker
          }

          // Update result count in the filter component
          this.updateFilterResultCount(
            this.viewType === 'table' ? this.filteredCardData.length : finalData.length
          );

          // CRITICAL: Clear loading states when data arrives
          // This ensures loading indicators are hidden once data is available
          if (this.isInitialLoading) {
            this.isInitialLoading = false;
            this.hasLoadedInitialData = true;
          }
          // Also ensure isLoadingData is cleared if store loading is false
          // The store loading subscription should handle this, but ensure it here too

          this.cdr.detectChanges();
        } finally {
          // CRITICAL: Reset flag immediately after update completes, not after delay
          // This ensures subsequent updates (e.g., 1->2 events) aren't blocked
          this.isUpdatingCalendar = false;
        }
      } else if (data === null || data === undefined) {
        // Handle null/undefined data - clear loading states
        this.lastLoadResultWasEmpty = true;
        if (this.isInitialLoading) {
          this.isInitialLoading = false;
        }
        this.isLoadingData = false;
        this.cdr.detectChanges();
      }
    });

    // Subscribe to errors from store
    this.store.select(EventsSelectors.selectCalendarError).pipe(
      takeUntil(this.destroy$)
    ).subscribe(error => {
      if (error) {
        console.error('[DUNA-EVENTS] Error from store:', error);
        // CRITICAL: Clear data on error to prevent showing stale/incorrect data
        this.data = [];
        this.calendarData = [];
        this.filteredCardData = [];
        this.paginatedCardData = [];
        this.updateCalendarData(); // Update calendar to clear it
        this.updateCardData(); // Update card data to clear it
        // CRITICAL: Clear loading states on error
        this.lastLoadResultWasEmpty = true;
        this.isInitialLoading = false;
        this.isLoadingData = false;
        this.isListViewRefreshing = false;
        this.hasLoadedInitialData = true; // Mark as attempted even on error to prevent retry loops
        this.updateFilterResultCount(0);
        this.cdr.detectChanges();
      }
    });
  }

  private waitForFilterAndInitialize() {
    // Try to get the filter - if it doesn't exist yet, wait for it
    let filter = this.filterService.getFilter('events-filter');
    
    if (!filter) {
      // Wait for filter to be registered
      const checkFilterInterval = setInterval(() => {
        filter = this.filterService.getFilter('events-filter');
        if (filter) {
          clearInterval(checkFilterInterval);
          this.fallbackInitialization();
        }
      }, 50); // Check every 50ms
      
      // Timeout after 5 seconds
      setTimeout(() => {
        clearInterval(checkFilterInterval);
        if (!filter) {
          console.warn(`[DUNA-EVENTS] Filter not ready after 5 seconds, continuing without filter coordination`);
          // Set up filter subscription first
          this.subscribeToFilterChanges();
          // Fallback to loading without filter coordination
          this.dispatchLoadCalendarEvents();
          this.setupViewSubscriptions();
        }
      }, 5000);
    } else {
      this.fallbackInitialization();
    }
  }

  private waitForFilterReady(): void {
    this.isFilterLoading = true;
    this.filterState.initialize().subscribe({
      next: () => {
        this.isFilterLoading = false;
        this.continueWithFilterFullyReady();
      },
      error: () => {
        this.isFilterLoading = false;
        this.continueWithFilterFullyReady();
      },
    });
  }

  private setupWindowEventFallback() {
    // Fallback: Use window event listener
    
    // Set up window event listener as a fallback
    window.addEventListener('duna-filter-ready', this.handleFilterReadyEvent);
    
    // Also set a timeout to proceed if filter ready event never comes
    // But wait longer and check if filter has organizationIds before proceeding
    setTimeout(() => {
      if (this.isInitialLoading && !this.hasLoadedInitialData) {
        // Check if filter is available and has organizationIds
        const filter = this.filterService.getFilter('events-filter');
        if (filter) {
          try {
            const currentFilter = (filter as any).currentFilterValue || (filter as any).filterValue;
            if (currentFilter?.organizationIds && currentFilter.organizationIds.length > 0) {
              window.removeEventListener('duna-filter-ready', this.handleFilterReadyEvent);
              this.continueWithFilterFullyReady();
              return;
            }
          } catch (e) {
            console.warn('[DUNA-EVENTS] Could not check filter for organizationIds:', e);
          }
        }
        
        // If still no organizationIds, wait a bit more and then proceed anyway
        // The backend should handle empty organizationIds gracefully
        setTimeout(() => {
          if (this.isInitialLoading && !this.hasLoadedInitialData) {
            console.warn(`[DUNA-EVENTS] Filter ready event timeout after 8 seconds total, falling back to initialization (may proceed without organizationIds)`);
            window.removeEventListener('duna-filter-ready', this.handleFilterReadyEvent);
            this.fallbackInitialization();
          }
        }, 5000); // Additional 5 seconds (8 seconds total)
      }
    }, 3000); // Initial 3 second wait
  }

  private handleFilterReadyEvent = (event: any) => {
    if (event && event.detail && event.detail.isReady) {
      
      // Mark filter as no longer loading
      this.isFilterLoading = false;
      
      // Only proceed if we haven't already initialized
      if (this.hasLoadedInitialData) {
        return;
      }
      
      // Remove the event listener since we only need this once
      window.removeEventListener('duna-filter-ready', this.handleFilterReadyEvent);
      
      // Cancel any Observable subscription if it's still active
      if (this.filterReadySubscription) {
        this.filterReadySubscription.unsubscribe();
        this.filterReadySubscription = null;
      }
      
      // Now start the actual initialization
      this.continueWithFilterFullyReady();
    }
  }

  private handleUserInteractionEvent = (event: any) => {
    if (event && event.detail && event.detail.isInteracting !== undefined) {
      this.isUserInteracting = event.detail.isInteracting;
    }
  }

  private continueWithFilterFullyReady() {

    if (this.filterStateSubscription) {
      return;
    }

    if (this.filterState.lifecycle !== 'ready') {
      console.warn('[DUNA-EVENTS] Filter state not ready, initializing');
      this.filterState.initialize().subscribe(() => this.continueWithFilterFullyReady());
      return;
    }

    if (this.filterBootstrapInProgress) {
      return;
    }
    this.filterBootstrapInProgress = true;
    this.store.dispatch(EventsActions.clearEventsData());
    this.replaceEventCacheOnNextLoad = true;
    this.lastEventCacheFilterKey = '';
    this.lastAppliedStoreQueryParams = null;
    
    // CRITICAL: Clear any displayed data so we never show events from a previous load (e.g. wrong/empty org).
    // The store may still hold old data; we start with a blank slate and only show data from our load with correct filter.
    this.data = [];
    this.calendarData = [];
    this.filteredCardData = [];
    this.paginatedCardData = [];
    this.updateCardData();
    if (this.scheduleObj) {
      this.updateCalendarData();
    }
    
    // Get the filter - metadata should be loaded via EventsFilterStateService
    // (lifecycle checked above)
    // CRITICAL: Ensure loading state is set BEFORE waiting for filters
    // This ensures the loading indicator shows during the wait period
    this.isInitialLoading = true;
    this.isLoadingData = true;
    this.cdr.detectChanges();

    // Update filter service with URL dates if they exist
    // CRITICAL: Only update if we're in list view - calendar view should not use filter dates
    // URL dates are for list view only
    if ((this.listViewStartDate || this.listViewEndDate) && this.viewType === 'table') {
      this.updateFilterServiceWithUrlDates();
    } else {
    }
    
    // Set up filter-driven loads via EventsFilterStateService
    this.subscribeToFilterStateChanges();
    this.setupViewSubscriptions();

    if (!this.hasLoadedInitialData) {
      // Initial load is triggered by first resolvedQueryParams$ emission
      this.isInitialLoading = true;
      this.isLoadingData = true;
      this.cdr.detectChanges();
    }
  }

  private subscribeToFilterStateChanges(): void {
    if (this.filterStateSubscription) {
      return;
    }

    this.filterStateSubscription = this.filterState.resolvedQueryParams$.subscribe((baseParams: EventQueryParams) => {
      const params: any = { ...baseParams };

      if (this.viewType === 'calendar') {
        delete params.startDate;
        delete params.endDate;
        const { startDate, endDate } = this.calculateCalendarViewDateRange();
        params.startDate = startDate;
        params.endDate = endDate;
      } else if (this.viewType === 'table') {
        delete params.startDate;
        delete params.endDate;
        if (this.listViewStartDate && this.listViewEndDate) {
          params.startDate = this.listViewStartDate;
          params.endDate = this.listViewEndDate;
        } else if (!this.hasInitializedDateRange) {
          this.initializeDefaultDateRange();
          params.startDate = this.listViewStartDate;
          params.endDate = this.listViewEndDate;
        }
      }

      this.currentFilters = { ...params };
      const criteriaKey = JSON.stringify(this.filterState.criteria);
      if (criteriaKey !== this.lastCriteriaKey) {
        this.lastCriteriaKey = criteriaKey;
        this.lastLoadParams = '';
        this.isLoadInProgress = false;
        this.replaceEventCacheOnNextLoad = true;
      }
      this.currentEventsFilter = this.filterState.criteria as EventsFilter;
      this.dispatchLoadCalendarEvents(params);
    });

    this.subscriptions.push(this.filterStateSubscription);

    const hideSubEventsSub = this.filterState.criteria$.pipe(
      map((c) => c.hideSubEvents),
      distinctUntilChanged()
    ).subscribe(() => {
      if (this.hasLoadedInitialData) {
        this.reapplyLoadedDataAndFacets();
      }
    });
    this.subscriptions.push(hideSubEventsSub);

    const listFacetSub = this.filterState.criteria$.pipe(
      map((c) => c.searchText),
      distinctUntilChanged(),
      debounceTime(400)
    ).subscribe(() => {
      if (this.hasLoadedInitialData && this.viewType === 'table') {
        this.reapplyLoadedDataAndFacets();
      }
    });
    this.subscriptions.push(listFacetSub);
  }

  /** Re-apply client-side filters and facet counts on already-loaded store data. */
  private reapplyLoadedDataAndFacets(): void {
    this.currentEventsFilter = this.filterState.criteria as EventsFilter;
    this.store.select(EventsSelectors.selectCalendarData).pipe(take(1)).subscribe((storeData) => {
      let raw = storeData ?? [];
      if (this.viewType === 'calendar') {
        raw = this.filterEventsToCalendarVisibleRange(raw);
      }
      const processed = this.preprocessEvents(raw);
      const displayed = this.applyFrontendFilters(processed);
      this.data = displayed;
      this.lastLoadResultWasEmpty = displayed.length === 0;
      this.updateCardData();
      this.syncFacetsAfterDisplayUpdate(processed);
      this.updateFilterResultCount(
        this.viewType === 'table' ? this.filteredCardData.length : displayed.length
      );
      if (this.viewType === 'calendar') {
        this.updateCalendarData();
      }
      this.cdr.markForCheck();
    });
  }

  /** Sync facet badges from what the user actually sees (incl. list date range + search). */
  private syncFacetsAfterDisplayUpdate(processedData: any[]): void {
    if (this.viewType === 'table') {
      const sourceForHidden = this.applySearchToEvents(
        this.filterEventsByListDateRange(this.filterGroupCalendarEvents(processedData))
      );
      this.syncFacetCountsFromLoadedEvents(this.filteredCardData, sourceForHidden);
      return;
    }
    const sourceForHidden = this.filterGroupCalendarEvents(processedData);
    this.syncFacetCountsFromLoadedEvents(this.data, sourceForHidden);
  }

  private applySearchToEvents(events: any[]): any[] {
    const searchText = (this.filterState.criteria.searchText || '').trim().toLowerCase();
    if (!searchText) {
      return events;
    }
    return events.filter((event) => this.eventMatchesSearchText(event, searchText));
  }

  private eventMatchesSearchText(event: any, searchText: string): boolean {
    const eventAny = event as any;
    const title = (eventAny.title || eventAny.name || '').toLowerCase();
    const type = this.getEventTypeName(event.event_type).toLowerCase();
    const period = this.formatEventPeriod(event).toLowerCase();
    const location = (
      eventAny.location_name ||
      event.predefined_location?.locations_name ||
      event.manual_location ||
      ''
    ).toLowerCase();
    const organization = (
      eventAny.organization_name ||
      event.organization?.org_name ||
      ''
    ).toLowerCase();
    return (
      title.includes(searchText) ||
      type.includes(searchText) ||
      period.includes(searchText) ||
      location.includes(searchText) ||
      organization.includes(searchText)
    );
  }

  private fallbackInitialization(): void {
    this.isFilterLoading = false;
    this.filterState.initialize().subscribe(() => {
      this.continueWithFilterFullyReady();
    });
  }

  /**
   * Helper to check if date range has changed (calendar or list view).
   * Used to allow loads when only the date range changes so we don't skip as duplicate.
   */
  private hasDateRangeChanged(queryParams: any): boolean {
    const isDateRangeView = this.viewType === 'calendar' || this.viewType === 'table';
    if (!isDateRangeView || !queryParams?.startDate || !queryParams?.endDate) {
      return false;
    }
    const newDateRange = { startDate: queryParams.startDate, endDate: queryParams.endDate };
    if (!this.lastLoadedDateRange) {
      return true;
    }
    return this.lastLoadedDateRange.startDate !== newDateRange.startDate ||
           this.lastLoadedDateRange.endDate !== newDateRange.endDate;
  }

  private dispatchLoadCalendarEvents(params?: any, options?: { force?: boolean }): void {
    if (options?.force) {
      this.pendingForceLoad = true;
      this.isLoadInProgress = false;
      this.lastLoadParams = '';
    }
    // CRITICAL: For calendar view, check date range change early to allow loads even if one is in progress
    // This handles rapid month switching where the previous load hasn't completed yet
    let dateRangeChanged = false;
    if (this.viewType === 'calendar') {
      // Calculate expected date range for calendar view
      const { startDate, endDate } = this.calculateCalendarViewDateRange();
      const newDateRange = { startDate, endDate };
      
      // CRITICAL: Set currentExpectedDateRange immediately so it's available for all subsequent checks
      // This ensures consistent date range tracking throughout the load process
      const previousExpectedRange = this.currentExpectedDateRange;
      this.currentExpectedDateRange = newDateRange;
      
      // Check if date range actually changed
      if (previousExpectedRange) {
        dateRangeChanged = 
          previousExpectedRange.startDate !== newDateRange.startDate ||
          previousExpectedRange.endDate !== newDateRange.endDate;
      } else if (this.lastLoadedDateRange) {
        // Fall back to lastLoadedDateRange if currentExpectedDateRange wasn't set
        dateRangeChanged = 
          this.lastLoadedDateRange.startDate !== newDateRange.startDate ||
          this.lastLoadedDateRange.endDate !== newDateRange.endDate;
      } else {
        // No previous range, this is a new load
        dateRangeChanged = true;
      }
      
      if (dateRangeChanged && this.isLoadInProgress) {
        // Reset the in-progress flag to allow the new load
        this.isLoadInProgress = false;
        // Also clear lastLoadParams to allow the new load
        this.lastLoadParams = '';
      }
    } else if (this.viewType === 'table' && this.listViewStartDate && this.listViewEndDate) {
      // List view: allow new load when list date range changed
      const newListRange = { startDate: this.listViewStartDate, endDate: this.listViewEndDate };
      dateRangeChanged = !this.lastLoadedDateRange ||
        this.lastLoadedDateRange.startDate !== newListRange.startDate ||
        this.lastLoadedDateRange.endDate !== newListRange.endDate;
      if (dateRangeChanged && this.isLoadInProgress) {
        this.isLoadInProgress = false;
        this.lastLoadParams = '';
      }
    }
    
    // CRITICAL: Prevent duplicate concurrent loads UNLESS date range changed or filter forced reload
    if (this.isLoadInProgress && !dateRangeChanged && !this.pendingForceLoad) {
      return;
    }
    
    // Prepare query params first to check for duplicates
    let queryParams: any = {};
    if (params && Object.keys(params).length > 0) {
      queryParams = this.prepareQueryParams(params);
    } else {
      // If no params, get from filter service
      const filter = this.filterService.getFilter('events-filter');
      if (filter) {
        filter.getFilter().pipe(take(1)).subscribe((filterValue: EventsFilter) => {
          const filterParams = this.convertFilterToParams(filterValue || {});
          queryParams = this.prepareQueryParams(filterParams);
          this.dispatchLoadCalendarEventsInternal(queryParams);
        });
        return;
      }
    }
    
    // CRITICAL: Ensure organization_ids are included BEFORE checking for duplicates
    // This prevents dispatching without orgs and then dispatching again with orgs
    const filter = this.filterService.getFilter('events-filter');
    if (!queryParams.organization_ids && filter) {
      // Get orgs synchronously if possible, or wait for async
      filter.getFilter().pipe(take(1)).subscribe((filterValue: EventsFilter) => {
        if (filterValue?.organizationIds && filterValue.organizationIds.length > 0) {
          queryParams.organization_ids = filterValue.organizationIds.join(',');
        }
        // Now check for duplicates and dispatch
        // CRITICAL: Always allow load if date range changed (for calendar view)
        const dateRangeChanged = this.hasDateRangeChanged(queryParams);
        const paramsString = JSON.stringify(queryParams);
        if (paramsString === this.lastLoadParams && !this.isInitialLoading && !dateRangeChanged && !this.pendingForceLoad) {
          this.onDuplicateLoadSkipped();
          return;
        }
        if (dateRangeChanged) {
        }
        this.dispatchLoadCalendarEventsInternal(queryParams);
      });
      return;
    }
    
    // Check if this is a duplicate of the last load
    // CRITICAL: Always allow load if date range changed (for calendar view)
    // Use the early check if available, otherwise check again with queryParams
    if (!dateRangeChanged) {
      dateRangeChanged = this.hasDateRangeChanged(queryParams);
    }
    const paramsString = JSON.stringify(queryParams);
    if (paramsString === this.lastLoadParams && !this.isInitialLoading && !dateRangeChanged && !this.pendingForceLoad) {
      this.onDuplicateLoadSkipped();
      return;
    }
    if (dateRangeChanged) {
    }
    
    this.dispatchLoadCalendarEventsInternal(queryParams);
  }
  
  private dispatchLoadCalendarEventsInternal(queryParams: any): void {
    delete queryParams.hideSubEvents;

    const stateOrgIds = this.filterState.criteria.organizationIds ?? [];
    const allowAllOrganizations = stateOrgIds.length === 0;

    if (!queryParams.organization_ids && stateOrgIds.length > 0) {
      queryParams.organization_ids = stateOrgIds.join(',');
    }

    // Source of truth for path filters is EventsFilterStateService — never re-inject
    // stale educational_path_* from the legacy filter when the user cleared them.
    this.applyEducationalPathParamsFromState(queryParams);

    const filter = this.filterService.getFilter('events-filter');
    
    if (!queryParams.organization_ids && filter && !allowAllOrganizations) {
      // Use getFilter() to get the current filter value properly
      filter.getFilter().pipe(take(1)).subscribe((filterValue: EventsFilter) => {
        const hasOrganizationIds = filterValue?.organizationIds && filterValue.organizationIds.length > 0;
        
        if (hasOrganizationIds && filterValue.organizationIds) {
          // Get organizationIds and add to queryParams
          queryParams.organization_ids = filterValue.organizationIds.join(',');
          this.applyEducationalPathParamsFromState(queryParams);
          
          // CRITICAL: Check for duplicate before dispatching
          // CRITICAL: Always allow load if date range changed (for calendar view)
          const dateRangeChanged = this.hasDateRangeChanged(queryParams);
          const paramsString = JSON.stringify(queryParams);
          if (paramsString === this.lastLoadParams && !this.isInitialLoading && !dateRangeChanged && !this.pendingForceLoad) {
            this.onDuplicateLoadSkipped();
            return;
          }
          if (dateRangeChanged) {
          }
          
          this.executeLoad(queryParams);
        } else {
          const user = this.authService.getCurrentUser();
          const uid = user ? (user as any).organizations_id ?? (user as any).organizationsId : null;
          const userHasOrg = uid != null && uid !== undefined;
          if (userHasOrg && stateOrgIds.length > 0) {
            queryParams.organization_ids = stateOrgIds.join(',');
            this.applyEducationalPathParamsFromState(queryParams);
            this.executeLoad(queryParams);
            return;
          }
          console.warn('[DUNA-EVENTS] OrganizationIds not found in filter, proceeding without org filter');
          this.executeLoad(queryParams);
        }
      });
      return; // CRITICAL: Return here to prevent immediate dispatch without orgs
    } else if (!queryParams.organization_ids && !filter) {
      console.warn('[DUNA-EVENTS] No filter service available, proceeding without organizationIds');
    }
    
    // CRITICAL: Check for duplicate before final dispatch
    // CRITICAL: Always allow load if date range changed (for calendar view)
    const dateRangeChanged = this.hasDateRangeChanged(queryParams);
    const paramsString = JSON.stringify(queryParams);
    if (paramsString === this.lastLoadParams && !this.isInitialLoading && !dateRangeChanged && !this.pendingForceLoad) {
      this.onDuplicateLoadSkipped();
      return;
    }
    if (dateRangeChanged) {
    }
    
    this.executeLoad(queryParams);
  }

  /**
   * Align educational path query params with filter state.
   * Absence of path IDs means "no path filter" — do not resurrect them from legacy cache.
   */
  private applyEducationalPathParamsFromState(queryParams: any): void {
    const criteria = this.filterState.criteria;
    const versionIds = criteria.educationalPathVersionIds ?? [];
    const pathIds = criteria.educationalPathIds ?? [];

    delete queryParams.educational_path_ids;
    delete queryParams.educational_path_version_ids;
    delete queryParams.educationalPathIds;
    delete queryParams.educationalPathVersionIds;

    if (versionIds.length > 0) {
      queryParams.educational_path_version_ids = versionIds.join(',');
    } else if (pathIds.length > 0) {
      queryParams.educational_path_ids = pathIds.join(',');
    }
  }
  
  private executeLoad(queryParams: any): void {
    // CRITICAL: Track expected date range for calendar view
    // This is used to detect if date range changed even when other params are the same
    let dateRangeChanged = false;
    if (this.viewType === 'calendar' && queryParams.startDate && queryParams.endDate) {
      const newDateRange = { startDate: queryParams.startDate, endDate: queryParams.endDate };
      // Check against currentExpectedDateRange first
      dateRangeChanged = !this.currentExpectedDateRange || 
        this.currentExpectedDateRange.startDate !== newDateRange.startDate ||
        this.currentExpectedDateRange.endDate !== newDateRange.endDate;
      
      // Also check against lastLoadedDateRange if we haven't detected a change yet
      // This handles cases where currentExpectedDateRange might not be set yet
      if (!dateRangeChanged) {
        dateRangeChanged = !this.lastLoadedDateRange || 
          this.lastLoadedDateRange.startDate !== newDateRange.startDate ||
          this.lastLoadedDateRange.endDate !== newDateRange.endDate;
      }
      
      if (dateRangeChanged) {
        this.currentExpectedDateRange = newDateRange;
        // Allow load even if one is in progress - date range changed
        if (this.isLoadInProgress) {
          this.isLoadInProgress = false;
        }
      } else {
        this.currentExpectedDateRange = newDateRange;
      }
    } else if (this.viewType === 'table' && queryParams.startDate && queryParams.endDate) {
      // List view: use same "date range changed" logic so we don't skip as duplicate
      dateRangeChanged = this.hasDateRangeChanged(queryParams);
      if (dateRangeChanged && this.isLoadInProgress) {
        this.isLoadInProgress = false;
      }
    }
    
    // CRITICAL: Prevent duplicate concurrent loads UNLESS date range changed or filter forced reload
    if (this.isLoadInProgress && !dateRangeChanged && !this.pendingForceLoad) {
      return;
    }
    
    // CRITICAL: Check for duplicate params BEFORE marking as in progress
    // This prevents duplicate dispatches when async subscriptions add params
    // BUT: Always allow if date range changed (checked above)
    const paramsString = JSON.stringify(queryParams);
    const isDuplicateParams = paramsString === this.lastLoadParams && !this.isInitialLoading;
    const forceLoad = this.pendingForceLoad;
    this.pendingForceLoad = false;
    
    if (isDuplicateParams && !dateRangeChanged && !forceLoad) {
      this.finishListViewLoadState();
      return;
    }
    
    if (dateRangeChanged) {
    }
    
    // Mark as in progress and update last params
    this.isLoadInProgress = true;
    this.calendarLoadDispatched = true;
    delete queryParams.hideSubEvents;
    this.lastLoadParams = paramsString;
    this.lastExecutedQueryParams = { ...queryParams };
    this.lastLoadResultWasEmpty = false; // Don't show "Ingen begivenheder" until this load completes

    if (
      this.viewType === 'calendar' &&
      queryParams['startDate'] &&
      queryParams['endDate'] &&
      !this.loadedRangeCoversVisibleCalendar(this.lastAppliedStoreQueryParams)
    ) {
      this.clearCalendarEventDisplay();
    }

    const replaceCache = this.shouldReplaceEventCacheForLoad(queryParams);
    this.store.dispatch(EventsActions.loadCalendarEvents({ queryParams, replaceCache }));
    
    // Reset load in progress flag after a delay (when loading completes)
    // This is handled by the store subscription which sets isLoadingData = false
    setTimeout(() => {
      // Only reset if loading is actually complete
      if (!this.isLoadingData) {
        this.isLoadInProgress = false;
      }
    }, 1000);
  }

  // This method is no longer needed - organizationIds waiting is now handled in dispatchLoadCalendarEventsInternal

  private waitForAllFiltersAndLoad(filter: any): void {
    // CRITICAL: Wait for filter defaults to be set before loading
    // This ensures organizationIds and educationalPathIds are available if they should be auto-selected
    // NOTE: isInitialLoading and isLoadingData are already set in continueWithFilterFullyReady
    // to ensure loading indicator shows during this wait period
    
    // Helper functions to check filter state
    const hasEducationalPathsLoaded = (filter as any).hasEducationalPathsLoaded ? (filter as any).hasEducationalPathsLoaded() : false;
    const hasSetEducationalPathDefaults = (filter as any).hasSetInitialEducationalPathDefault ? (filter as any).hasSetInitialEducationalPathDefault() : false;
    const hasSetOrgDefaults = (filter as any).hasSetInitialDefaultFilter ? (filter as any).hasSetInitialDefaultFilter() : false;
    
    // Check if user is authenticated (needed to determine if defaults should be set)
    // Use getCurrentUser() to check if user is authenticated
    const currentUser = this.authService.getCurrentUser();
    const isAuthenticated = currentUser !== null && currentUser !== undefined;
    const userOrgId = (currentUser as any)?.organizations_id ?? (currentUser as any)?.organizationsId ?? null;
    
    // Check if educational paths are needed (from URL or should be defaults)
    const urlParams = this.route.snapshot.queryParams;
    const hasEducationalPathInUrl = urlParams['eps'] && urlParams['eps'].length > 0;
    // Educational paths are needed if: in URL OR defaults should be set (but not yet set)
    const needsEducationalPaths = hasEducationalPathInUrl || (!hasSetEducationalPathDefaults && isAuthenticated);
    
    // Check if organizations are needed (from URL or should be defaults)
    const hasOrgInUrl = urlParams['org'] && urlParams['org'].length > 0;
    // Organizations are needed if: in URL OR defaults should be set (but not yet set)
    const needsOrganizations = hasOrgInUrl || (!hasSetOrgDefaults && isAuthenticated);
    
    // Check current state
    // Educational paths are ready when the list is loaded (options not empty). User may have 0 or more selected.
    // - If in URL: just need paths loaded (they're already set from URL)
    // - If not in URL but authenticated: need list loaded AND defaults set — we do not require path IDs in filter
    //   (user may have none selected)
    // - If not authenticated: not needed, so ready
    let educationalPathsReady = false;
    if (!needsEducationalPaths) {
      educationalPathsReady = true; // Not needed
    } else if (hasEducationalPathInUrl) {
      educationalPathsReady = hasEducationalPathsLoaded; // In URL, just need loaded
    } else if (isAuthenticated) {
      educationalPathsReady = hasEducationalPathsLoaded && hasSetEducationalPathDefaults;
    } else {
      educationalPathsReady = true; // Not authenticated, not needed
    }
    
    // Organizations are ready based on actual filter value:
    // - If in URL: require organizationIds present in filter
    //   (URL org names can be present before async name->id resolution finishes)
    // - If not in URL but authenticated:
    //   * user has org -> require organizationIds present in filter
    //   * user has no org -> defaults flag is enough
    // - If not authenticated: not needed, so ready
    let organizationsReady = false;
    if (!needsOrganizations) {
      organizationsReady = true; // Not needed
    } else if (hasOrgInUrl) {
      organizationsReady = false; // Require actual filter value check in polling loop
    } else if (isAuthenticated) {
      // If user has org, require it in the filter value (checked in polling via getFilter()).
      const userHasOrg = userOrgId != null && userOrgId !== undefined;
      organizationsReady = !userHasOrg && hasSetOrgDefaults;
    } else {
      organizationsReady = true; // Not authenticated, not needed
    }
    
    // If either educational paths or organizations are needed but not ready, wait
    if ((needsEducationalPaths && !educationalPathsReady) || (needsOrganizations && !organizationsReady)) {
      
      let attempts = 0;
      const maxAttempts = 100; // 10 seconds (100 * 100ms)
      
      const checkIfReady = () => {
        attempts++;
        
        // Re-check state on each attempt
        const currentHasEducationalPathsLoaded = (filter as any).hasEducationalPathsLoaded ? (filter as any).hasEducationalPathsLoaded() : false;
        const currentHasSetEducationalPathDefaults = (filter as any).hasSetInitialEducationalPathDefault ? (filter as any).hasSetInitialEducationalPathDefault() : false;
        const currentHasSetOrgDefaults = (filter as any).hasSetInitialDefaultFilter ? (filter as any).hasSetInitialDefaultFilter() : false;
        
        // CRITICAL: Check the actual filter value to ensure defaults are applied
        // The flag might be set but the filter value might not be updated yet
        filter.getFilter().pipe(take(1)).subscribe((currentFilterValue: EventsFilter) => {
          // Educational paths are ready based on scenario (same logic as initial check)
          // BUT: Also verify the filter value actually has educational paths if needed
          let currentEducationalPathsReady = false;
          if (!needsEducationalPaths) {
            currentEducationalPathsReady = true; // Not needed
          } else if (hasEducationalPathInUrl) {
            currentEducationalPathsReady = currentHasEducationalPathsLoaded; // In URL, just need loaded
          } else if (isAuthenticated) {
            // Educational paths ready when the list is loaded and defaults are set. We do NOT require path IDs
            // in the filter — user may have none selected. "List should never be empty" = options are loaded.
            currentEducationalPathsReady = currentHasEducationalPathsLoaded && currentHasSetEducationalPathDefaults;
            
            // CRITICAL: Only log if paths are actually needed but not ready
            if (!currentEducationalPathsReady && !this.hasLoggedEducationalPathsNotReady && needsEducationalPaths) {
              this.hasLoggedEducationalPathsNotReady = true;
              // Reset flag after a delay to allow logging again if still not ready
              setTimeout(() => {
                this.hasLoggedEducationalPathsNotReady = false;
              }, 5000); // Only log once every 5 seconds
            }
          } else {
            currentEducationalPathsReady = true; // Not authenticated, not needed
          }
          
          // Organizations are ready based on scenario (same logic as initial check)
          // BUT: Also verify the filter value actually has organizations if needed
          let currentOrganizationsReady = false;
          if (!needsOrganizations) {
            currentOrganizationsReady = true; // Not needed
          } else if (hasOrgInUrl) {
            const hasOrganizationsInFilter = !!(currentFilterValue?.organizationIds && currentFilterValue.organizationIds.length > 0);
            // URL org parameter can exist before org names are converted to ids.
            // Do not proceed until ids are actually present in the shared filter value.
            currentOrganizationsReady = hasOrganizationsInFilter;
          } else if (isAuthenticated) {
            const hasOrganizationsInFilter = !!(currentFilterValue?.organizationIds && currentFilterValue.organizationIds.length > 0);
            const userHasOrg = userOrgId != null && userOrgId !== undefined;
            // Only require organizationIds when the user is actually bound to an organization.
            // For authenticated users without org binding, waiting for org IDs can hang forever.
            currentOrganizationsReady = userHasOrg
              ? hasOrganizationsInFilter
              : currentHasSetOrgDefaults;
            
            // CRITICAL: Only log if orgs are actually needed but not ready
            // Don't log if user simply doesn't have any organizations
            if (!currentOrganizationsReady && needsOrganizations) {
            }
          } else {
            currentOrganizationsReady = true; // Not authenticated, not needed
          }
          
          const allReady = currentEducationalPathsReady && currentOrganizationsReady;
          
          if (allReady) {
            this.proceedWithInitialLoad(filter);
          } else if (attempts >= maxAttempts) {
            // Timeout failsafe: if user has an org but filter still has no org ids,
            // self-heal by injecting the user's org and proceed.
            const userHasOrg = userOrgId != null && userOrgId !== undefined;
            const hasOrganizationsInFilter = !!(currentFilterValue?.organizationIds && currentFilterValue.organizationIds.length > 0);
            if (isAuthenticated && userHasOrg && !hasOrganizationsInFilter) {
              console.warn('[DUNA-EVENTS] Timeout waiting for org ids in filter - applying fallback user org and proceeding', {
                userOrgId,
                attempts
              });
              try {
                const fallbackFilterValue = {
                  ...(currentFilterValue || {}),
                  organizationIds: [userOrgId]
                };
                if (typeof (filter as any).setFilter === 'function') {
                  (filter as any).setFilter(fallbackFilterValue);
                }
                this.currentEventsFilter = fallbackFilterValue as EventsFilter;
              } catch (e) {
                console.warn('[DUNA-EVENTS] Failed to apply fallback org to filter, proceeding with current filter value', e);
              }
              this.proceedWithInitialLoad(filter);
              return;
            }

            // No user-org fallback available: keep polling as before.
            console.warn('[DUNA-EVENTS] Timeout waiting for filter data, continuing to poll (will not load until org/list is ready)', {
              educationalPathsLoaded: currentHasEducationalPathsLoaded,
              educationalPathDefaultsSet: currentHasSetEducationalPathDefaults,
              educationalPathsInFilter: (currentFilterValue?.educationalPathIds?.length ?? 0) > 0,
              orgDefaultsSet: currentHasSetOrgDefaults,
              orgsInFilter: (currentFilterValue?.organizationIds?.length ?? 0) > 0,
              attempts
            });
            setTimeout(() => checkIfReady(), 100);
          } else {
            // Not ready yet, check again after delay
            setTimeout(() => checkIfReady(), 100);
          }
        });
      };
      
      // Start checking
      checkIfReady();
      return;
    }
    
    // All filter data ready, proceed immediately
    this.proceedWithInitialLoad(filter);
  }
  
  private proceedWithInitialLoad(filter: any): void {
    // CRITICAL: Use getFilter() to get the current filter value properly
    // The filter service uses RxJS observables, so we can't access currentFilterValue/filterValue directly
    filter.getFilter().pipe(take(1)).subscribe((filterValue: EventsFilter) => {
      const hasOrganizationIds = filterValue?.organizationIds && filterValue.organizationIds.length > 0;
      const currentUser = this.authService.getCurrentUser();
      const userOrgId = (currentUser as any)?.organizations_id ?? (currentUser as any)?.organizationsId ?? null;
      const userHasOrg = userOrgId != null && userOrgId !== undefined;
      
      // CRITICAL: Do not load if user has an org but filter has no organizationIds - we would show wrong orgs' events
      if (userHasOrg && !hasOrganizationIds) {
        console.warn('[DUNA-EVENTS] User has org but filter has no organizationIds yet - waiting for filter to apply default org');
        this.waitForAllFiltersAndLoad(filter);
        return;
      }
      
      const defaultsFlag = (filter as any).hasSetInitialDefaultFilter;
      const defaultsApplied = typeof defaultsFlag === 'function' ? defaultsFlag() : !!defaultsFlag;
      if (hasOrganizationIds && defaultsApplied) {
      } else if (hasOrganizationIds) {
      } else {
      }
      
      // Ensure loading state is still set (it should already be set, but ensure it)
      this.isInitialLoading = true;
      this.isLoadingData = true;
      this.cdr.detectChanges();
      
      this.currentEventsFilter = filterValue;
    
      // Convert filter to params
      const initialParams = this.convertFilterToParams(filterValue || {});
      
      // Always add date range for calendar view (initial load is always calendar view)
      // CRITICAL: Ignore any dates from filter or URL - calendar view calculates its own dates
      const calendarParams = { ...initialParams };
      delete calendarParams.startDate;
      delete calendarParams.endDate;
      delete calendarParams.dateRange;
      
      // CRITICAL: Also ignore listViewStartDate/listViewEndDate (URL dates) for calendar view
      // These are ONLY for list view, never for calendar view
      // Even if URL has dates, calendar view should calculate its own range
      
      const { startDate, endDate } = this.calculateCalendarViewDateRange();
      calendarParams.startDate = startDate;
      calendarParams.endDate = endDate;
      
      this.dispatchLoadCalendarEvents(calendarParams);
    });
  }

  private prepareQueryParams(params: any): any {
    
    // Path filters must follow EventsFilterStateService (explicit clear = no path params).
    this.applyEducationalPathParamsFromState(params);

    // Ensure organization IDs are included if they exist in the filter state
    // This needs to happen before we modify params for date ranges
    try {
      const stateOrgIds = this.filterState.criteria.organizationIds ?? [];
      if (!params.organizationIds && !params.organization_ids) {
        if (stateOrgIds.length > 0) {
          params.organizationIds = stateOrgIds;
        } else {
        }
      } else {
      }
    } catch (e) {
      console.warn('[DUNA-EVENTS] Could not apply organizationIds from filter state:', e);
    }
    
    // Build parameters with date range based on view type
    if (this.viewType === 'calendar') {
      // Calendar view: calculate date range for 5-week view starting on Monday
      // CRITICAL: ALWAYS override any existing dates to ensure correct calendar range
      // This prevents list view dates (including URL dates) from being used in calendar view
      // Remove any dateRange object that might be present from filter
      delete params.dateRange;
      delete params.startDate; // Explicitly remove any existing startDate (including URL dates)
      delete params.endDate;   // Explicitly remove any existing endDate (including URL dates)
      
      // CRITICAL: Also ignore listViewStartDate/listViewEndDate for calendar view
      // These are ONLY for list view, never for calendar view
      // Even if they came from URL, calendar view should calculate its own dates
      
      // Calculate fresh calendar date range
      const { startDate, endDate } = this.calculateCalendarViewDateRange();
      params.startDate = startDate;
      params.endDate = endDate;
      // CRITICAL: Update expected date range immediately so duplicate checks can use it
      this.currentExpectedDateRange = { startDate, endDate };
      
      // Don't set default organization filter here - let the filter component handle it
      // This prevents overriding user's explicit organization selections
      
    } else if (this.viewType === 'table') {
      // Table view: use user-selected date range or default
      // CRITICAL: For table view, always use listViewStartDate/listViewEndDate if available
      // Don't use dates from filter.dateRange (those are for calendar view)
      // Only use params.startDate/endDate if they were explicitly set for table view
      
      // Remove any dateRange object that might be present from filter (calendar view dates)
      delete params.dateRange;
      
      // If list view dates are set, use them (they take precedence)
      if (this.listViewStartDate && this.listViewEndDate) {
        params.startDate = this.listViewStartDate;
        params.endDate = this.listViewEndDate;
      } else if (!params.startDate || !params.endDate) {
        // Only initialize defaults if not already initialized (prevents resetting user's custom dates)
        if (!this.hasInitializedDateRange) {
          this.initializeDefaultDateRange();
        }
        params.startDate = this.listViewStartDate;
        params.endDate = this.listViewEndDate;
      } else {
      }
    }
    
    // Convert params to queryParams format
    const queryParams: any = {};
    if (params) {
      Object.keys(params).forEach(key => {
        const value = params[key];
        if (value !== undefined && value !== null) {
          // Skip empty strings, empty arrays, and empty objects
          if (value === '' || 
              (Array.isArray(value) && value.length === 0) ||
              (typeof value === 'object' && Object.keys(value).length === 0)) {
            return;
          }
          
          // Convert Date objects to ISO date strings for API compatibility
          if (key === 'startDate' || key === 'endDate') {
            if (value instanceof Date) {
              queryParams[key] = value.toISOString().split('T')[0];
            } else {
              queryParams[key] = value;
            }
          } else if (key === 'organizationIds' && Array.isArray(value)) {
            // Convert organizationIds array to comma-separated string
            queryParams.organization_ids = value.join(',');
          } else if (key === 'educational_path_ids' || key === 'educationalPathIds') {
            // Handle educational path IDs - convert array to comma-separated string if needed
            if (Array.isArray(value)) {
              queryParams.educational_path_ids = value.join(',');
            } else if (typeof value === 'string') {
              queryParams.educational_path_ids = value;
            }
          } else if (key === 'eventTypes' && Array.isArray(value)) {
            // Convert eventTypes array to comma-separated string
            // Backend expects event_types (with underscore) as comma-separated string
            queryParams.event_types = value.join(',');
          } else if (key === 'hideSubEvents') {
            // Client-only — used for reload detection, not sent to GET /events
            return;
          } else {
            queryParams[key] = value;
          }
        }
      });
    }
    
    // Log final params including all filters for debugging
    
    return queryParams;
  }

  private clearCalendarEventDisplay(): void {
    this.data = [];
    this.calendarData = [];
    this.refreshEventCategoryInheritanceContext();
    this.updateCardData();
    if (this.viewType === 'calendar') {
      this.updateCalendarData();
    }
    this.cdr.markForCheck();
  }

  /** Loaded API window must include the full current calendar grid (exclusive end). */
  private dateRangeCovers(
    loadedStart: string,
    loadedEnd: string,
    visibleStart: string,
    visibleEnd: string
  ): boolean {
    return loadedStart <= visibleStart && loadedEnd >= visibleEnd;
  }

  private loadedRangeCoversVisibleCalendar(
    loaded: Record<string, unknown> | null
  ): boolean {
    if (this.viewType !== 'calendar') {
      return true;
    }
    if (!loaded?.['startDate'] || !loaded?.['endDate']) {
      return false;
    }
    const visible = this.calculateCalendarViewDateRange();
    return this.dateRangeCovers(
      String(loaded['startDate']),
      String(loaded['endDate']),
      visible.startDate,
      visible.endDate
    );
  }

  private buildEventCacheFilterKey(queryParams: Record<string, unknown>): string {
    const copy = { ...queryParams };
    delete copy['startDate'];
    delete copy['endDate'];
    delete copy['hideSubEvents'];
    return JSON.stringify(copy, Object.keys(copy).sort());
  }

  private shouldReplaceEventCacheForLoad(queryParams: Record<string, unknown>): boolean {
    const filterKey = this.buildEventCacheFilterKey(queryParams);
    let replace =
      this.replaceEventCacheOnNextLoad ||
      this.viewType === 'table' ||
      filterKey !== this.lastEventCacheFilterKey;

    if (
      this.viewType === 'calendar' &&
      queryParams['startDate'] &&
      queryParams['endDate'] &&
      !this.loadedRangeCoversVisibleCalendar(this.lastAppliedStoreQueryParams)
    ) {
      const visible = this.calculateCalendarViewDateRange();
      if (
        queryParams['startDate'] === visible.startDate &&
        queryParams['endDate'] === visible.endDate
      ) {
        replace = true;
      }
    }

    this.replaceEventCacheOnNextLoad = false;
    this.lastEventCacheFilterKey = filterKey;
    return replace;
  }

  private storeResponseMatchesExecutedLoad(
    storeQueryParams: Record<string, unknown>
  ): boolean {
    const expected = this.lastExecutedQueryParams;
    if (!expected) {
      return true;
    }
    return (
      expected['startDate'] === storeQueryParams['startDate'] &&
      expected['endDate'] === storeQueryParams['endDate']
    );
  }

  /** Show only events that fall in the current calendar grid (cache may hold other months). */
  private filterEventsToCalendarVisibleRange(events: any[]): any[] {
    if (this.viewType !== 'calendar' || !events?.length) {
      return events;
    }
    const { startDate, endDate } = this.calculateCalendarViewDateRange();
    return events.filter((event) =>
      isEventStartInDateRange(event, startDate, endDate)
    );
  }

  /**
   * Date range for the current calendar view (Day, Week or Month).
   * End dates are exclusive (SQL WHERE date < endDate).
   */
  private calculateCalendarViewDateRange(): { startDate: string; endDate: string } {
    const anchor = new Date(this.currentCalendarMonth);
    const view = this.calendarScheduleView;

    if (view === 'Day') {
      const start = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
      const end = new Date(start);
      end.setDate(start.getDate() + 1);
      return {
        startDate: this.formatIsoDate(start),
        endDate: this.formatIsoDate(end)
      };
    }

    if (view === 'Week') {
      const dayOfWeek = anchor.getDay();
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const start = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate() - daysToMonday);
      const end = new Date(start);
      end.setDate(start.getDate() + 7);
      return {
        startDate: this.formatIsoDate(start),
        endDate: this.formatIsoDate(end)
      };
    }

    // Month view: Monday of the week containing the 1st, then 6 weeks (exclusive end)
    const firstDayOfMonth = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const dayOfWeek = firstDayOfMonth.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const calendarStartDate = new Date(firstDayOfMonth);
    calendarStartDate.setDate(firstDayOfMonth.getDate() - daysToSubtract);
    const calendarEndDate = new Date(calendarStartDate);
    calendarEndDate.setDate(calendarStartDate.getDate() + 42);

    const startDateStr = this.formatIsoDate(calendarStartDate);
    const endDateStr = this.formatIsoDate(calendarEndDate);

    return {
      startDate: startDateStr,
      endDate: endDateStr
    };
  }

  private setupViewSubscriptions() {
    // Initialize the store with the dataSourceKey - ensure settings are already initialized
    if (this.settings) {
      // Initialize with empty data to prevent errors about missing data source
      this.store.dispatch(DataActions.loadDataSuccess({
        dataSourceKey: this.dataSourceKey,
        data: {
          items: [],
          totalCount: 0,
          pageSize: this.settings?.pageSize || 10,
          pageIndex: 0
        },
        settings: this.settings
      }));
    }

    // Combine all streams to prevent multiple initializations
    const mode$ = this.templateService.currentMode$.pipe(distinctUntilChanged());
    const toggle$ = this.componentStateService.getState('events_calendar_toggle').pipe(distinctUntilChanged());

    combineLatest([mode$, toggle$]).pipe(
      takeUntil(this.destroy$),
      debounceTime(100) // Add debounce to handle rapid changes
    ).subscribe({
      next: ([mode, state]: [number, boolean]) => {
        this.currentMode = mode as number;
        
        // Auto-close filter panel when switching to desktop mode
        if (mode === 1 && this.isFilterPanelOpen) {
          this.closeFilterPanel();
        }

        const newViewType = state ? 'table' : 'calendar';
        const viewChanged = this.lastToggleState !== null && this.lastToggleState !== state;
        this.lastToggleState = state;
        this.viewType = newViewType;
        
        if (this.scheduleObj) {
          this.updateScheduleView();
          if (this.viewType === 'calendar') {
            this.scheduleObj.dataBind();
            this.scheduleReapplyAllCompactMonthLayouts();
          }
        }
        
        // CRITICAL: If view actually changed, reload data with correct date range
        if (viewChanged) {
          
          if (!this.data || this.data.length === 0) {
            this.isLoadingData = true;
          } else if (newViewType !== 'table') {
            this.isLoadingData = false;
          }
          this.cdr.detectChanges();
          
          // Get current filter values
          const filter = this.filterService.getFilter('events-filter');
          if (filter) {
            filter.getFilter().pipe(take(1)).subscribe((filterValue: EventsFilter) => {
              // Convert filter to params
              const params = this.convertFilterToParams(filterValue || {});
              
              if (newViewType === 'table') {
                
                this.ensureListViewDateRange();
                const needsReload = !this.listViewDateRangeMatchesLoadedData();
                this.prepareListViewTransition(needsReload);
                
                if (!needsReload) {
                  this.reloadFacetsForCurrentView();
                  return;
                }
                
                // Remove any dates from calendar view
                delete params.startDate;
                delete params.endDate;
                delete params.dateRange;
                
                params.startDate = this.listViewStartDate;
                params.endDate = this.listViewEndDate;
                
                this.dispatchLoadCalendarEvents(params);
                this.reloadFacetsForCurrentView();
              } else {
                this.replaceEventCacheOnNextLoad = true;
                this.clearCalendarEventDisplay();

                // Remove any dates from list view
                delete params.startDate;
                delete params.endDate;
                delete params.dateRange;
                
                // Calculate calendar view date range
                const { startDate, endDate } = this.calculateCalendarViewDateRange();
                params.startDate = startDate;
                params.endDate = endDate;
                
                this.dispatchLoadCalendarEvents(params);
                this.reloadFacetsForCurrentView();
              }
            });
          } else {
            // No filter available, use just date range
            if (newViewType === 'table') {
              this.ensureListViewDateRange();
              const needsReload = !this.listViewDateRangeMatchesLoadedData();
              this.prepareListViewTransition(needsReload);
              if (!needsReload) {
                this.reloadFacetsForCurrentView();
                return;
              }
              const params: any = {
                startDate: this.listViewStartDate,
                endDate: this.listViewEndDate
              };
              this.dispatchLoadCalendarEvents(params);
              this.reloadFacetsForCurrentView();
            } else {
              const { startDate, endDate } = this.calculateCalendarViewDateRange();
              const params: any = { startDate, endDate };
              if (newViewType === 'calendar') {
                this.replaceEventCacheOnNextLoad = true;
                this.clearCalendarEventDisplay();
              }
              this.dispatchLoadCalendarEvents(params);
              this.reloadFacetsForCurrentView();
            }
          }
        } else if (newViewType === 'table') {
          // View didn't change but we're in table view - ensure table is initialized
          this.updateTableDataSource();
          setTimeout(() => {
            this.forceTableInitialization();
            this.disableTableSearch();
          }, 100);
        }
        
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        console.error(`[DUNA-EVENTS] Error in state stream for instance ${this.instanceId}:`, error);
      }
    });
  }

  private subscribeToFilterChanges() {
    const filter = this.filterService.getFilter('events-filter');
    if (filter) {
      // Unsubscribe from any existing subscription first
      if (this.filterSubscription) {
        this.filterSubscription.unsubscribe();
      }
      
      // Track the last filter value to avoid duplicate processing
      let lastFilterValueString = '';
      let lastFilterProcessTime = 0;
      let isFirstEmission = true; // Track first emission to skip it
      
      this.filterSubscription = filter.getFilter().pipe(
        // Add debounce to prevent rapid filter changes from causing multiple API calls
        debounceTime(300)
      ).subscribe(filterValue => {
        // Skip the first emission - this is the initial filter state that was already used for initial load
        if (isFirstEmission) {
          isFirstEmission = false;
          return;
        }
        
        
        // CRITICAL: Skip emissions during initial loading to prevent cancelling the initial data load
        if (this.isInitialLoading && !this.hasLoadedInitialData) {
          return;
        }
        
        // Skip processing if this is a duplicate of what we just processed
        // But only if it's not a user interaction (user interactions should always be processed)
        const filterValueString = JSON.stringify(filterValue, Object.keys(filterValue).sort());
        const currentTime = Date.now();
        const timeSinceLastProcess = currentTime - lastFilterProcessTime;
        
        if (filterValueString === lastFilterValueString && !this.isUserInteracting && timeSinceLastProcess < 1000) {
          return;
        }
        
        // If this is a user interaction, always process it even if it's the same value
        if (this.isUserInteracting && filterValueString === lastFilterValueString) {
        }
        
        // Update the last processed filter and timestamp
        lastFilterValueString = filterValueString;
        lastFilterProcessTime = currentTime;
        
        if (filterValue) {
          // Store the current filter value for frontend filtering
          this.currentEventsFilter = filterValue;
          
          // Convert the filter to the format expected by loadData
          const params = this.convertFilterToParams(filterValue);
          
          
          // Always load data if user is interacting, or if filters have actually changed
          const filtersChanged = JSON.stringify(params) !== JSON.stringify(this.currentFilters);
          const shouldLoadData = this.isUserInteracting || filtersChanged;
          
          // FORCE LOAD: If this is a filter subscription (not initial load), always load data
          const isFilterSubscription = !this.isInitialLoading || this.hasLoadedInitialData;
          const forceLoad = isFilterSubscription; // Always force load for filter subscriptions
          
          
          if (shouldLoadData || forceLoad) {
            
            // Update current filters immediately to prevent duplicate comparisons
            this.currentFilters = { ...params };
            
            // Only load data for the currently active view
            if (this.viewType === 'calendar') {
              // CRITICAL: Remove any dates from params for calendar view
              // Calendar view should ALWAYS calculate its own dates, never use URL dates or filter dates
              delete params.startDate;
              delete params.endDate;
              delete params.dateRange;
              this.dispatchLoadCalendarEvents(params);
            } else if (this.viewType === 'table') {
              
              // CRITICAL: For table view, always use listViewStartDate/listViewEndDate
              // Remove any dates that might have come from filter (those are for calendar view)
              delete params.startDate;
              delete params.endDate;
              delete params.dateRange;
              
              // Always use list view dates for table view
              if (this.listViewStartDate && this.listViewEndDate) {
                params.startDate = this.listViewStartDate;
                params.endDate = this.listViewEndDate;
              } else {
                // Initialize default date range if not already set
                if (!this.hasInitializedDateRange) {
                  this.initializeDefaultDateRange();
                }
                params.startDate = this.listViewStartDate;
                params.endDate = this.listViewEndDate;
              }
              
              // Convert Date objects to strings if needed
              if (params.startDate instanceof Date) {
                params.startDate = params.startDate.toISOString().split('T')[0];
              }
              if (params.endDate instanceof Date) {
                params.endDate = params.endDate.toISOString().split('T')[0];
              }
              
              this.dispatchLoadCalendarEvents(params);
              
              // Ensure card data is updated after filter change
              setTimeout(() => {
                this.updateCardData();
                this.cdr.detectChanges();
              }, 100);
            } else {
            }
          } else {
          }
        } else {
        }
      });
      
      // Add the subscription to the list for cleanup
      this.subscriptions.push(this.filterSubscription);
    } else {
      console.warn('[DUNA-EVENTS] Events filter not found');
    }
  }

  private convertFilterToParams(filter: EventsFilter): any {
    const params: any = {};
    let activeCount = 0;
    
    // Add event types - always send them if any are selected
    // Backend needs to know which event types to filter by
    if (filter.eventType && filter.eventType.length > 0) {
      params.eventTypes = filter.eventType;
      // Only count as active filter if not all types are selected
      if (filter.eventType.length < 3) {
        activeCount++;
      }
    }
    
    // Add search text and count if provided
    if (filter.searchText && filter.searchText.trim()) {
      params.search = filter.searchText.trim();
      activeCount++;
    }
    
    // Add organizations and count if provided
    if (filter.organizationIds && filter.organizationIds.length > 0) {
      params.organizationIds = filter.organizationIds;
      activeCount++;
    } else {
    }
    
    // CRITICAL: Do NOT add date range from filter.dateRange here
    // The date range should be set by prepareQueryParams based on the current view type
    // filter.dateRange might contain list view dates, which should not be used for calendar view
    // Only add dates if they're explicitly for the current view type
    // This prevents list view dates from leaking into calendar view
    
    // Note: Dates will be set in prepareQueryParams based on viewType
    // List view dates are stored in this.listViewStartDate/this.listViewEndDate
    // Calendar view dates are calculated in calculateCalendarViewDateRange()
    
    // Add visibility if available and count (visibility is always an array of strings)
    if (filter.visibility && Array.isArray(filter.visibility)) {
      // Check if there are any non-empty items
      const validItems = filter.visibility.filter((v: string) => v !== null && v !== undefined && v !== '');
      if (validItems.length > 0) {
        params.visibility = filter.visibility;
        activeCount++;
      }
    }
    
    // Convert educational path / version IDs
    if (filter.educationalPathVersionIds && filter.educationalPathVersionIds.length > 0) {
      params.educational_path_version_ids = filter.educationalPathVersionIds.join(',');
      activeCount++;
    } else if (filter.educationalPathIds && filter.educationalPathIds.length > 0) {
      params.educational_path_ids = filter.educationalPathIds.join(',');
      activeCount++;
    }

    if (filter.moduleIds && filter.moduleIds.length > 0) {
      params.module_ids = filter.moduleIds.join(',');
      activeCount++;
    }

    if (filter.locationGuids && filter.locationGuids.length > 0) {
      params.location_guids = filter.locationGuids.join(',');
      activeCount++;
    }

    if (filter.hideFullEvents) {
      params.hide_full = 'true';
      activeCount++;
    }

    if (filter.hideParticipatedEvents) {
      params.exclude_participated = 'true';
      activeCount++;
    }

    const df = filter.distanceFilter;
    if (df?.enabled && df.latitude != null && df.longitude != null && df.radiusKm != null && df.radiusKm > 0) {
      params.lat = String(df.latitude);
      params.lng = String(df.longitude);
      params.radius_km = String(df.radiusKm);
      activeCount++;
    }
    
    // Update the active filter count
    this.updateActiveFilterCount(activeCount);
    
    
    return params;
  }

  private convertUrlParamsToFilterParams(urlParams: any): any {
    const params: any = {};
    
    // Convert type parameter (comma-separated values)
    if (urlParams.type) {
      params.eventTypes = urlParams.type.split(',');
    }
    
    // Convert search parameter
    if (urlParams.search) {
      params.search = urlParams.search;
    }
    
    // Convert organization parameter - NOTE: URL stores org NAMES, not IDs
    // Organization IDs should come from the filter service, not parsed from URL
    // The filter component handles converting names to IDs
    // We'll skip parsing org from URL here and rely on the filter service
    // This prevents issues where org names can't be converted to IDs
    if (urlParams.org) {
      // Don't parse org names here - let the filter component handle it
      // The filter service will have the correct organization IDs
    }
    
    // Convert educational path IDs parameter (comma-separated values, using short name "eps")
    if (urlParams.eps) {
      params.educational_path_ids = urlParams.eps;
    }
    
    // Convert date parameters
    if (urlParams.startDate) {
      params.startDate = urlParams.startDate;
    }
    
    if (urlParams.endDate) {
      params.endDate = urlParams.endDate;
    }
    
    return params;
  }

  ngOnDestroy() {
    
    // Clean up any existing tooltips
    this.cleanupTooltips();
    
    // Remove event listeners
    window.removeEventListener('duna-filter-count', this.handleFilterEvent);
    window.removeEventListener('duna-filter-ready', this.handleFilterReadyEvent);
    window.removeEventListener('duna-filter-user-interaction', this.handleUserInteractionEvent);
    
    // Remove keyboard event listener
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
      this.keydownHandler = undefined;
    }
    this.unbindCalendarDayClickHandler();
    
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => {
      if (sub) {
        sub.unsubscribe();
      }
    });
    
    // Cancel any pending data load subscription
    if (this.dataLoadSubscription) {
      this.dataLoadSubscription.unsubscribe();
      this.dataLoadSubscription = null;
    }
    
    // Cancel any pending filter ready subscription
    if (this.filterReadySubscription) {
      this.filterReadySubscription.unsubscribe();
      this.filterReadySubscription = null;
    }
    
    // Clear any pending safety timeout
    if (this.safetyTimeout) {
      clearTimeout(this.safetyTimeout);
      this.safetyTimeout = null;
    }
    
    // Clear any pending debounce timer
    if (this.dataLoadDebounceTimer) {
      clearTimeout(this.dataLoadDebounceTimer);
      this.dataLoadDebounceTimer = null;
    }
    
    // Clean up the store data
    this.store.dispatch(DataActions.clearData({ dataSourceKey: this.dataSourceKey }));
    
    this.destroy$.next();
    this.destroy$.complete();
    
    // Decrement instance count
    DunaEventsCalendarComponent.instanceCount--;
    
  }

  ngAfterViewInit() {
    
    // Debug date picker values
    
    // Add debugging for calendar component
    if (this.scheduleObj) {
      setTimeout(() => this.bindCalendarDayClickHandler(), 0);
    }
    
    // If we're in table view, initialize the table
    if (this.viewType === 'table') {
      
      setTimeout(() => {
        // Check if we have data to display
        if (this.data && this.data.length > 0) {
          this.updateTableDataSource();
        this.forceTableInitialization();
        this.disableTableSearch();
          this.forceTableRefresh();
        } else {
          // Load data if we don't have any
          const params = { ...this.currentFilters };
          params.startDate = this.listViewStartDate;
          params.endDate = this.listViewEndDate;
          this.dispatchLoadCalendarEvents(params);
        }
        
        // Check if the table component has the correct settings
        if (this.tableComponent) {
          if (this.tableComponent.settings && this.tableComponent.settings.columns) {
            const titleColumn = this.tableComponent.settings.columns.find((col: any) => col.key === 'title');
            if (titleColumn) {
            } else {
            }
          }
        } else {
          console.warn('[DUNA-EVENTS] Table component not found in ngAfterViewInit');
        }
      }, 100);
    }
  }

  private updateScheduleView() {
    if (this.scheduleObj) {
      this.scheduleObj.currentView = this.calendarScheduleView;
    }
  }

  onEventRendered(args: any) {
      
      // Get the original event data for advanced styling
      const originalEvent = this.data.find(event => event.guid === args.data.Id);
      
      // Apply comprehensive styling based on event properties
      this.applyEventStyling(args, originalEvent);
      
      // Add tooltip functionality for desktop users
      this.addEventTooltip(args);
  }

  private applyEventStyling(args: any, originalEvent?: EventDTO) {
    const eventData = args.data;
    const element = args.element;

    element.style.setProperty('color', '#ffffff', 'important');
    element.style.setProperty('border-style', 'solid', 'important');
    element.style.setProperty('border-width', '1px', 'important');
    element.style.setProperty('box-sizing', 'border-box', 'important');
    element.style.setProperty('font-weight', '500', 'important');
    element.style.setProperty('font-size', '11px', 'important');
    element.style.setProperty('overflow', 'hidden', 'important');
    element.style.setProperty('border-radius', '2px', 'important');

    const compactAppointmentChrome = !this.shouldShowCalendarAppointmentTypeIcons();

    // Month-view bars are a fixed 20px; day/week appointments keep their time-based height
    if (this.calendarScheduleView === 'Month') {
      element.style.setProperty('height', '20px', 'important');
      element.style.setProperty('min-height', '20px', 'important');
      element.style.setProperty('max-height', '20px', 'important');
      if (compactAppointmentChrome) {
        element.style.setProperty('font-size', '9px', 'important');
        element.style.setProperty('line-height', '16px', 'important');
        element.style.setProperty('padding', '0 1px', 'important');
        element.classList.add('duna-month-compact-chrome');
      } else {
        element.style.setProperty('font-size', '11px', 'important');
        element.style.setProperty('line-height', '18px', 'important');
        element.style.setProperty('padding', '0 4px', 'important');
        element.classList.remove('duna-month-compact-chrome');
      }
      element.style.setProperty('margin', '0', 'important');
      if (compactAppointmentChrome) {
        if (originalEvent?.guid) {
          element.dataset['dunaEventId'] = originalEvent.guid;
        }
        const startTime = eventData?.StartTime
          ? new Date(eventData.StartTime)
          : undefined;
        if (startTime && !Number.isNaN(startTime.getTime())) {
          element.dataset['dunaEventStartMs'] = String(
            this.normalizeCalendarDayStart(startTime)
          );
        }
        this.scheduleCompactMonthLayoutAfterPaint(element, originalEvent, startTime);
      } else {
        element.classList.remove('duna-month-full-width');
        delete element.dataset['dunaEventId'];
        delete element.dataset['dunaEventStartMs'];
      }
    } else {
      element.style.setProperty('line-height', '1.3', 'important');
      element.style.setProperty(
        'padding',
        compactAppointmentChrome ? '2px 6px' : '2px 6px',
        'important'
      );
    }
    
    // Check if event is inactive first
    if (!normalizeEventIsActive(eventData.IsActive ?? eventData.is_active)) {
      this.applyInactiveStyling(element);
      return;
    }
    
    // Check if event is hidden (not shown) - apply special styling
    if (!normalizeEventIsShown(eventData.IsShown ?? eventData.is_shown)) {
      this.applyHiddenStyling(element);
      return;
    }
    
    // Determine event color based on hierarchy and type
    const eventColor = this.getEventColor(originalEvent, eventData);
    
    // Apply the determined color
    element.style.setProperty('background-color', eventColor.background, 'important');
    element.style.setProperty('border-color', eventColor.border, 'important');
    element.style.setProperty('opacity', '1', 'important');
    element.style.setProperty('background-image', 'none', 'important');
    
    // Add visual indicators for sub-events
    if (originalEvent?.parent_event_id || this.isEventSharedSeriesNonAnchor(originalEvent)) {
      this.addSubEventIndicator(element);
    }
    
    // Add event type indicator (desktop only — icons steal space from titles on tablet/mobile)
    if (
      this.shouldShowCalendarAppointmentTypeIcons() &&
      originalEvent?.event_type
    ) {
      this.addEventTypeIndicator(element, originalEvent.event_type);
    }
    
    if (
      this.shouldShowCalendarOrganizationBadge() &&
      originalEvent?.organization?.org_name
    ) {
      this.addOrganizationIndicator(element, originalEvent.organization.org_name);
    }
    
    // Add participation indicator
    this.addParticipationIndicator(element, originalEvent);
  }

  private applyInactiveStyling(element: HTMLElement) {
    element.style.setProperty('background-color', '#8f8f8f', 'important');
    element.style.setProperty('opacity', '0.6', 'important');
    element.style.setProperty('border-color', '#666666', 'important');
    element.style.setProperty('border-style', 'dashed', 'important');
    element.style.setProperty('background-image', 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)', 'important');
  }

  private applyHiddenStyling(element: HTMLElement) {
    element.style.setProperty('background-color', '#ff6b6b', 'important');
    element.style.setProperty('opacity', '0.7', 'important');
    element.style.setProperty('border-color', '#ff5252', 'important');
    element.style.setProperty('border-style', 'dotted', 'important');
    element.style.setProperty('background-image', 'repeating-linear-gradient(90deg, transparent, transparent 3px, rgba(255,255,255,0.2) 3px, rgba(255,255,255,0.2) 6px)', 'important');
    
    // Add a small eye-slash icon to indicate hidden status
    const iconSpan = document.createElement('span');
    iconSpan.innerHTML = '👁️‍🗨️';
    iconSpan.style.setProperty('font-size', '10px', 'important');
    iconSpan.style.setProperty('margin-right', '2px', 'important');
    iconSpan.style.setProperty('opacity', '0.8', 'important');
    
    // Insert the icon at the beginning of the event text
    if (element.firstChild) {
      element.insertBefore(iconSpan, element.firstChild);
    } else {
      element.appendChild(iconSpan);
    }
  }

  private loadEventCategoryColors(): void {
    const user = this.authService.getCurrentUser() as Record<string, unknown> | null;
    const orgId =
      user?.['organizations_id'] ??
      (user?.['user_info'] as Record<string, unknown> | undefined)?.['organizations_id'];
    const n = typeof orgId === 'number' ? orgId : parseInt(String(orgId ?? ''), 10);
    if (!Number.isFinite(n) || n <= 0) {
      return;
    }
    if (this.eventCategoryColorsOrgId === n && Object.keys(this.eventCategoryColors).length > 0) {
      return;
    }
    const obs = this.componentDataSourceService.executeDataSource('org_settings', {
      organizationId: n,
    });
    if (!obs) {
      return;
    }
    obs.pipe(take(1)).subscribe((res: { data?: Record<string, unknown> }) => {
      this.eventCategoryColorsOrgId = n;
      this.eventCategoryColors = normalizeEventCategoryColors(
        res?.data?.['event_category_colors']
      );
      this.refreshCalendarAfterCategoryColorsLoaded();
    });
  }

  /** Re-run eventRendered styling after async category color map loads (e.g. hard refresh). */
  private refreshCalendarAfterCategoryColorsLoaded(): void {
    this.cdr.markForCheck();
    if (this.viewType !== 'calendar' || !this.scheduleObj) {
      return;
    }
    requestAnimationFrame(() => {
      try {
        if (typeof this.scheduleObj.dataBind === 'function') {
          this.scheduleObj.dataBind();
        }
        this.scheduleReapplyAllCompactMonthLayouts();
      } catch {
        /* ignore */
      }
      this.cdr.markForCheck();
    });
  }

  private getEventColor(originalEvent?: EventDTO, eventData?: any): { background: string, border: string } {
    if (!originalEvent) {
      return { background: '#445E91', border: '#445E91' }; // Default blue
    }

    const categoryBg = resolveEventCategoryBackgroundColor(
      originalEvent,
      this.eventCategoryColors,
      this.eventCategoryInheritanceContext
    );
    if (categoryBg) {
      return {
        background: categoryBg,
        border: resolveEventCategoryBorderColor(categoryBg),
      };
    }
    
    // Use subtle color variations based on event type, but keep blue as primary
    const typeColors: Record<string, { primary: string; secondary: string; light: string }> = {
      'education': { primary: '#445E91', secondary: '#5A6FA3', light: '#6B7DB5' },
      'exercise': { primary: '#445E91', secondary: '#5A6FA3', light: '#6B7DB5' },
      'course': { primary: '#445E91', secondary: '#5A6FA3', light: '#6B7DB5' },
      'activity': { primary: '#2e7d32', secondary: '#43a047', light: '#66bb6a' },
    };
    
    const baseColor = typeColors[originalEvent.event_type] || typeColors['course'];
    
    // Sub-events and shared-series extra days use lighter styling
    if (originalEvent.parent_event_id || this.isEventSharedSeriesNonAnchor(originalEvent)) {
      return {
        background: baseColor.light,
        border: '#FFC107' // Yellow border for sub-events
      };
    }
    
    // Main events use primary blue color
    return {
      background: baseColor.primary,
      border: baseColor.primary
    };
  }

  private addSubEventIndicator(element: HTMLElement) {
    // Add a small indicator to show this is a sub-event
    element.style.setProperty('position', 'relative', 'important');
    
    // Add a small dot indicator
    const indicator = document.createElement('div');
    indicator.style.cssText = `
      position: absolute;
      top: -3px;
      right: -3px;
      width: 8px;
      height: 8px;
      background-color: #FFC107;
      border-radius: 50%;
      border: 2px solid #ffffff;
      z-index: 10;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    `;
    indicator.title = 'Underbegivenhed';
    element.appendChild(indicator);
  }

  private addOrganizationIndicator(element: HTMLElement, orgName: string) {
    // Add organization abbreviation as a small badge
    const orgAbbr = this.getOrganizationAbbreviation(orgName);
    
    const badge = document.createElement('div');
    badge.className = 'duna-event-org-indicator';
    badge.textContent = orgAbbr;
    badge.style.cssText = `
      position: absolute;
      bottom: -2px;
      right: -2px;
      background-color: rgba(0, 0, 0, 0.85);
      color: #ffffff;
      font-size: 8px;
      padding: 2px 4px;
      border-radius: 4px;
      line-height: 1;
      font-weight: bold;
      max-width: 50px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      border: 1px solid rgba(255, 255, 255, 0.4);
      z-index: 5;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    `;
    badge.title = `Organisation: ${orgName}`;
    element.appendChild(badge);
  }

  private addParticipationIndicator(element: HTMLElement, originalEvent?: EventDTO) {
    element.classList.remove('duna-event-user-participated');
    element.querySelector('.duna-participation-badge')?.remove();

    if (!originalEvent?.participants || !this.authService.getCurrentUser()?.uuid) {
      return;
    }

    const currentUserUuid = this.authService.getCurrentUser()?.uuid;
    const hasParticipated = originalEvent.participants.some(
      participant => participant.user?.uuid === currentUserUuid
    );

    if (!hasParticipated) {
      return;
    }

    element.classList.add('duna-event-user-participated');

    const existingTitle = element.getAttribute('title');
    if (!existingTitle) {
      element.setAttribute('title', 'Du deltager');
    }

    const badge = document.createElement('span');
    badge.className = 'duna-participation-badge';
    badge.setAttribute('aria-label', 'Du deltager');
    badge.innerHTML = '<i class="material-icons" aria-hidden="true">check</i>';
    element.appendChild(badge);
  }

  private shouldShowCalendarAppointmentTypeIcons(): boolean {
    return this.currentMode === 1;
  }

  /** Org badge only on desktop when filter compares events across 2+ organisations. */
  private shouldShowCalendarOrganizationBadge(): boolean {
    if (!this.shouldShowCalendarAppointmentTypeIcons()) {
      return false;
    }
    const orgIds =
      this.filterState.criteria.organizationIds ??
      this.currentEventsFilter?.organizationIds ??
      [];
    return orgIds.length > 1;
  }

  private compactMonthLayoutTimer: ReturnType<typeof setTimeout> | null = null;

  private scheduleCompactMonthLayoutAfterPaint(
    element: HTMLElement,
    originalEvent?: EventDTO,
    startTime?: Date
  ): void {
    const apply = () =>
      this.applyCompactMonthAppointmentLayout(element, originalEvent, startTime);
    apply();
    requestAnimationFrame(apply);
    setTimeout(apply, 50);
  }

  private normalizeCalendarDayStart(date: Date): number {
    const day = new Date(date);
    day.setHours(0, 0, 0, 0);
    return day.getTime();
  }

  private findMonthWorkCellForStart(startTime?: Date): HTMLElement | null {
    if (!this.scheduleObj?.element || !startTime) {
      return null;
    }
    const dayMs = this.normalizeCalendarDayStart(startTime);
    const root = this.scheduleObj.element;
    return (
      root.querySelector(`.e-month-view .e-work-cells[data-date="${dayMs}"]`) ??
      root.querySelector(`.e-month-view td.e-work-cells[data-date="${dayMs}"]`)
    ) as HTMLElement | null;
  }

  private scheduleReapplyAllCompactMonthLayouts(): void {
    if (this.currentMode === 1 || this.calendarScheduleView !== 'Month') {
      return;
    }
    if (this.compactMonthLayoutTimer !== null) {
      clearTimeout(this.compactMonthLayoutTimer);
    }
    this.compactMonthLayoutTimer = setTimeout(() => {
      this.compactMonthLayoutTimer = null;
      this.reapplyAllCompactMonthAppointmentLayouts();
      setTimeout(() => this.reapplyAllCompactMonthAppointmentLayouts(), 100);
    }, 0);
  }

  private reapplyAllCompactMonthAppointmentLayouts(): void {
    if (!this.scheduleObj?.element || this.currentMode === 1) {
      return;
    }
    const nodes = this.scheduleObj.element.querySelectorAll('.e-month-view .e-appointment');
    nodes.forEach(node => {
      const element = node as HTMLElement;
      const guid = element.dataset['dunaEventId'];
      const originalEvent = guid
        ? this.data.find(event => event.guid === guid)
        : undefined;
      let startTime: Date | undefined;
      const startMs = element.dataset['dunaEventStartMs'];
      if (startMs) {
        const parsed = Number(startMs);
        if (!Number.isNaN(parsed)) {
          startTime = new Date(parsed);
        }
      }
      if (!startTime && guid) {
        const cal = this.calendarData.find((e: { Id?: string }) => e?.Id === guid);
        if (cal?.StartTime) {
          startTime = new Date(cal.StartTime);
        }
      }
      this.applyCompactMonthAppointmentLayout(element, originalEvent, startTime);
    });
  }

  /** Month-view bars: fill day cell in layout px (not getBoundingClientRect — breaks under scale transform). */
  private applyCompactMonthAppointmentLayout(
    element: HTMLElement,
    originalEvent?: EventDTO,
    startTime?: Date
  ): void {
    if (this.isMultiDayMonthSpan(element, originalEvent)) {
      element.classList.remove('duna-month-full-width');
      return;
    }

    element.classList.add('duna-month-full-width');

    const dayCell =
      (element.closest('td.e-work-cells') as HTMLElement | null) ??
      this.findMonthWorkCellForStart(startTime) ??
      (element.closest('td') as HTMLElement | null);

    if (!dayCell) {
      return;
    }

    const { innerWidth } = this.getMonthDayCellInnerMetrics(dayCell);
    if (innerWidth <= 0) {
      return;
    }

    const wrap = element.closest('.e-appointment-wrap') as HTMLElement | null;
    const container = element.closest('.e-appointment-container') as HTMLElement | null;
    for (const host of [wrap, container]) {
      if (!host) {
        continue;
      }
      host.style.setProperty('position', 'relative', 'important');
      host.style.setProperty('width', `${innerWidth}px`, 'important');
      host.style.setProperty('max-width', `${innerWidth}px`, 'important');
      host.style.setProperty('left', '0', 'important');
      host.style.setProperty('margin-left', '0', 'important');
      host.style.setProperty('margin-right', '0', 'important');
      host.style.setProperty('padding-left', '0', 'important');
      host.style.setProperty('padding-right', '0', 'important');
    }

    element.style.setProperty('left', '0', 'important');
    element.style.setProperty('right', 'auto', 'important');
    element.style.setProperty('margin-left', '0', 'important');
    element.style.setProperty('margin-right', '0', 'important');
    element.style.setProperty('width', `${innerWidth}px`, 'important');
    element.style.setProperty('max-width', `${innerWidth}px`, 'important');
  }

  private getMonthDayCellInnerMetrics(dayCell: HTMLElement): { innerWidth: number } {
    const cellStyle = getComputedStyle(dayCell);
    const padLeft = parseFloat(cellStyle.paddingLeft) || 0;
    const padRight = parseFloat(cellStyle.paddingRight) || 0;
    const innerWidth = Math.max(0, dayCell.clientWidth - padLeft - padRight);
    return { innerWidth };
  }

  private eventSpansMultipleCalendarDates(start: Date, end: Date): boolean {
    return (
      start.getFullYear() !== end.getFullYear() ||
      start.getMonth() !== end.getMonth() ||
      start.getDate() !== end.getDate()
    );
  }

  private isMultiDayMonthSpan(element: HTMLElement, originalEvent?: EventDTO): boolean {
    if (element.classList.contains('e-multi-day-appointment')) {
      return true;
    }

    if (!originalEvent || originalEvent.event_duration_type !== 'multi_day') {
      return false;
    }

    const start = new Date(originalEvent.start_date);
    const end = new Date(originalEvent.end_date);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return false;
    }

    return this.eventSpansMultipleCalendarDates(start, end);
  }

  private addEventTypeIndicator(element: HTMLElement, eventType: string) {
    if (element.querySelector('.duna-event-type-indicator')) {
      return;
    }

    const icon = getEventTypeMaterialIcon(eventType);

    const indicator = document.createElement('span');
    indicator.className = 'duna-event-type-indicator';
    indicator.innerHTML = `<i class="material-icons" aria-hidden="true">${icon}</i>`;
    indicator.title = `Type: ${getEventTypeLabelDa(eventType)}`;
    element.appendChild(indicator);
  }

  private getOrganizationAbbreviation(orgName: string): string {
    // Create abbreviation from organization name
    const words = orgName.split(' ');
    if (words.length === 1) {
      // For single words, use the full word if it's 4 characters or less, otherwise truncate to 4
      return words[0].length <= 4 ? words[0].toUpperCase() : words[0].substring(0, 4).toUpperCase();
    }
    // For multiple words, use first letter of each word, max 4 characters
    return words.map(word => word.charAt(0)).join('').substring(0, 4).toUpperCase();
  }

  private addEventTooltip(args: any) {
    const eventElement = args.element;
    const eventData = args.data;
    
    // Find the original event data from our processed data.
    // If not found (e.g. during month-change when schedule renders before our data syncs),
    // build a fallback tooltip from the schedule's eventData so tooltips still work.
    const originalEvent = this.data.find(event => event.guid === eventData.Id);
    
    const dateOptions: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
    const timeOptions: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
    
    let tooltipContent = '';
    
    // Add event title (always from eventData – both paths have it)
    if (eventData.Subject) {
      tooltipContent += `<div class="tooltip-item title-item"><i class="material-icons">event</i><span>${this.escapeHtml(eventData.Subject)}</span></div>`;
    }
    
    // Add inactive status indicator only when event is inactive
    const isActive = eventData.IsActive !== false;
    if (!isActive) {
      const statusIcon = 'cancel';
      const statusText = 'Inaktiv';
      const statusColor = '#F44336';
      tooltipContent += `<div class="tooltip-item status-item"><i class="material-icons" style="color: ${statusColor};">${statusIcon}</i><span style="color: ${statusColor};">${statusText}</span></div>`;
    }
    
    // Add hidden status indicator only when event is hidden
    const isShown = eventData.IsShown !== false;
    if (!isShown) {
      const hiddenIcon = 'visibility_off';
      const hiddenText = 'Skjult for almindelige brugere';
      const hiddenColor = '#FF6B6B';
      tooltipContent += `<div class="tooltip-item hidden-item"><i class="material-icons" style="color: ${hiddenColor};">${hiddenIcon}</i><span style="color: ${hiddenColor};">${hiddenText}</span></div>`;
    }
    
    if (originalEvent) {
      // Full tooltip from original event
      if (originalEvent.parent_event_id || this.isEventSharedSeriesNonAnchor(originalEvent)) {
        const subEventIcon = 'subdirectory_arrow_right';
        const subEventText = 'Ekstra dato i serien';
        const subEventColor = '#FFC107';
        tooltipContent += `<div class="tooltip-item sub-event-item"><i class="material-icons" style="color: ${subEventColor};">${subEventIcon}</i><span style="color: ${subEventColor};">${subEventText}</span></div>`;
      }
      if (originalEvent.event_type) {
        const eventTypeText = this.getEventTypeText(originalEvent.event_type);
        const typeIcon = this.getEventTypeIcon(originalEvent.event_type);
        tooltipContent += `<div class="tooltip-item"><i class="material-icons">${typeIcon}</i><span>${eventTypeText}</span></div>`;
      }
      if (originalEvent.start_date && originalEvent.end_date) {
        const startDate = new Date(originalEvent.start_date);
        const endDate = new Date(originalEvent.end_date);
        const startDateStr = startDate.toLocaleDateString('da-DK', dateOptions);
        const startTimeStr = startDate.toLocaleTimeString('da-DK', timeOptions);
        const endDateStr = endDate.toLocaleDateString('da-DK', dateOptions);
        const endTimeStr = endDate.toLocaleTimeString('da-DK', timeOptions);
        if (startDateStr === endDateStr) {
          tooltipContent += `<div class="tooltip-item"><i class="material-icons">calendar_today</i><span>${startDateStr}</span></div>`;
          tooltipContent += `<div class="tooltip-item"><i class="material-icons">access_time</i><span>${startTimeStr} - ${endTimeStr}</span></div>`;
        } else {
          tooltipContent += `<div class="tooltip-item"><i class="material-icons">calendar_today</i><span>${startDateStr} ${startTimeStr}</span></div>`;
          tooltipContent += `<div class="tooltip-item"><i class="material-icons">event_available</i><span>${endDateStr} ${endTimeStr}</span></div>`;
        }
      }
      if (originalEvent.predefined_location?.locations_name) {
        tooltipContent += `<div class="tooltip-item"><i class="material-icons">location_on</i><span>${this.escapeHtml(originalEvent.predefined_location.locations_name)}</span></div>`;
      } else if (originalEvent.manual_location) {
        tooltipContent += `<div class="tooltip-item"><i class="material-icons">location_on</i><span>${this.escapeHtml(originalEvent.manual_location)}</span></div>`;
      }
      if (originalEvent.predefined_room?.room_name) {
        tooltipContent += `<div class="tooltip-item"><i class="material-icons">meeting_room</i><span>${this.escapeHtml(originalEvent.predefined_room.room_name)}</span></div>`;
      }
      if (originalEvent.organization?.org_name) {
        tooltipContent += `<div class="tooltip-item"><i class="material-icons">business</i><span>${this.escapeHtml(originalEvent.organization.org_name)}</span></div>`;
      }
      tooltipContent += this.buildCapacityTooltipHtml(originalEvent);
      if (originalEvent.description?.long_description?.['da']?.translation_text) {
        const longDesc = originalEvent.description.long_description['da'].translation_text;
        if (longDesc && longDesc !== eventData.Subject && longDesc.length > (eventData.Subject?.length || 0)) {
          tooltipContent += `<strong>Beskrivelse:</strong> ${this.escapeHtml(longDesc)}<br>`;
        }
      }
    } else {
      // Fallback when originalEvent is missing (e.g. schedule rendered during data/period change)
      // Use only Syncfusion eventData so tooltips still show
      if (eventData.EventType) {
        const eventTypeText = this.getEventTypeText(eventData.EventType);
        const typeIcon = this.getEventTypeIcon(eventData.EventType);
        tooltipContent += `<div class="tooltip-item"><i class="material-icons">${typeIcon}</i><span>${eventTypeText}</span></div>`;
      }
      if (eventData.StartTime && eventData.EndTime) {
        const startDate = new Date(eventData.StartTime);
        const endDate = new Date(eventData.EndTime);
        const startDateStr = startDate.toLocaleDateString('da-DK', dateOptions);
        const startTimeStr = startDate.toLocaleTimeString('da-DK', timeOptions);
        const endDateStr = endDate.toLocaleDateString('da-DK', dateOptions);
        const endTimeStr = endDate.toLocaleTimeString('da-DK', timeOptions);
        if (startDateStr === endDateStr) {
          tooltipContent += `<div class="tooltip-item"><i class="material-icons">calendar_today</i><span>${startDateStr}</span></div>`;
          tooltipContent += `<div class="tooltip-item"><i class="material-icons">access_time</i><span>${startTimeStr} - ${endTimeStr}</span></div>`;
        } else {
          tooltipContent += `<div class="tooltip-item"><i class="material-icons">calendar_today</i><span>${startDateStr} ${startTimeStr}</span></div>`;
          tooltipContent += `<div class="tooltip-item"><i class="material-icons">event_available</i><span>${endDateStr} ${endTimeStr}</span></div>`;
        }
      }
      tooltipContent += this.buildCapacityTooltipHtml({
        event_type: eventData.EventType,
        organization: { id: eventData.OrganizationId },
        registered_count: eventData.RegisteredCount,
        manual_location_room_capacity: eventData.MaxParticipants
      });
    }
    
    tooltipContent = tooltipContent.replace(/<br>$/, '');
    eventElement.removeAttribute('title');
    this.addCustomTooltip(eventElement, tooltipContent);
  }

  private buildCapacityTooltipHtml(event: any): string {
    if (!event || !this.showCourseCapacityForEvent(event)) {
      return '';
    }
    const capacity = this.getEventCapacity(event);
    const registered = this.getRegisteredCount(event);
    if (capacity <= 0 && registered <= 0) {
      return '';
    }
    if (capacity > 0) {
      return (
        `<div class="tooltip-item"><i class="material-icons">group</i><span>${registered} / ${capacity} tilmeldte</span></div>` +
        `<div class="tooltip-item"><i class="material-icons">event_seat</i><span>${this.escapeHtml(this.getCapacityStatusText(event))}</span></div>`
      );
    }
    return `<div class="tooltip-item"><i class="material-icons">group</i><span>${registered} tilmeldte</span></div>`;
  }
  
  private escapeHtml(s: string | null | undefined): string {
    if (s == null || s === '') return '';
    const div = document.createElement('div');
    div.textContent = s;
    return div.innerHTML;
  }
  
  private getEventTypeText(eventType: string): string {
    switch(eventType) {
      case 'education': return 'Uddannelse';
      case 'exercise': return 'Øvelse';
      case 'course': return 'Kursus';
      case 'activity': return 'Aktivitet';
      default: return eventType;
    }
  }
  
  public getEventTypeIcon(eventType: string): string {
    return getEventTypeMaterialIcon(eventType);
  }
  
  private stripHtmlTags(html: string): string {
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
  }
  
  private addCustomTooltip(element: HTMLElement, content: string) {
    let tooltip: HTMLElement | null = null;
    let showTimeout: number | null = null;
    let hideTimeout: number | null = null;
    let isMouseOver = false;
    
    // Function to safely remove tooltip
    const removeTooltip = () => {
      if (tooltip) {
        tooltip.style.opacity = '0';
        setTimeout(() => {
          if (tooltip && tooltip.parentNode) {
            try {
              tooltip.parentNode.removeChild(tooltip);
            } catch (e) {
              // Ignore errors if tooltip was already removed
            }
          }
          tooltip = null;
        }, 200);
      }
    };
    
    // Function to clear all timeouts
    const clearTimeouts = () => {
      if (showTimeout) {
        clearTimeout(showTimeout);
        showTimeout = null;
      }
      if (hideTimeout) {
        clearTimeout(hideTimeout);
        hideTimeout = null;
      }
    };
    
    // Mouse enter event - show tooltip after delay
    const mouseEnterHandler = (e: MouseEvent) => {
      // Only show tooltip on desktop (non-touch devices)
      if ('ontouchstart' in window) {
        return;
      }
      
      isMouseOver = true;
      clearTimeouts();
      
      // Add a small delay before showing tooltip to prevent flicker
      showTimeout = window.setTimeout(() => {
        if (!isMouseOver) return; // Don't show if mouse already left
        
        // Remove any existing tooltip first
        removeTooltip();
        
        // Create tooltip element
        tooltip = document.createElement('div');
        tooltip.className = 'duna-event-tooltip';
        tooltip.innerHTML = content;
        
                 // Apply styles directly to ensure they work
         Object.assign(tooltip.style, {
           position: 'absolute',
           background: '#445E91',
           color: '#ffffff',
           padding: '12px 16px',
           borderRadius: '12px',
           fontSize: '13px',
           lineHeight: '1.4',
           maxWidth: '300px',
           zIndex: '10001',
           boxShadow: '0 8px 20px rgba(68, 94, 145, 0.25), 0 4px 12px rgba(0, 0, 0, 0.15)',
           pointerEvents: 'none',
           fontFamily: 'Pathway Extreme, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
           border: '1px solid rgba(255, 255, 255, 0.15)',
           transition: 'opacity 0.2s ease-in-out, transform 0.2s ease-in-out',
           wordWrap: 'break-word',
           opacity: '0',
           transform: 'translateY(4px)'
         });
        
        document.body.appendChild(tooltip);
        
                 // Apply icon styles
         const icons = tooltip.querySelectorAll('.material-icons');
         icons.forEach((icon: any) => {
           Object.assign(icon.style, {
             marginRight: '10px',
             color: '#445E91',
             fontSize: '16px',
             width: '24px',
             height: '24px',
             minWidth: '24px',
             minHeight: '24px',
             display: 'flex',
             alignItems: 'center',
             justifyContent: 'center',
             background: '#D8E2FF',
             borderRadius: '50%',
             lineHeight: '1',
             textAlign: 'center',
             fontFamily: 'Material Icons',
             fontWeight: '400'
           });
         });
        
        // Apply item styles
        const items = tooltip.querySelectorAll('.tooltip-item');
        items.forEach((item: any) => {
          Object.assign(item.style, {
            display: 'flex',
            alignItems: 'center',
            marginBottom: '8px',
            padding: '2px 0'
          });
          
          if (item.classList.contains('title-item')) {
            Object.assign(item.style, {
              marginBottom: '10px',
              paddingBottom: '6px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.2)'
            });
            
            const span = item.querySelector('span');
            if (span) {
              Object.assign(span.style, {
                fontWeight: '600',
                fontSize: '14px',
                color: '#ffffff'
              });
            }
          }
          
          // Style the status item to make it more prominent
          if (item.classList.contains('status-item')) {
            Object.assign(item.style, {
              marginBottom: '8px',
              padding: '4px 0',
              borderLeft: '3px solid rgba(255, 255, 255, 0.3)',
              paddingLeft: '8px'
            });
            
            const span = item.querySelector('span');
            if (span) {
              Object.assign(span.style, {
                fontWeight: '500',
                fontSize: '13px'
              });
            }
          }
          
          const span = item.querySelector('span');
          if (span && !item.classList.contains('title-item')) {
            Object.assign(span.style, {
              flex: '1',
              fontWeight: '400',
              color: 'rgba(255, 255, 255, 0.9)',
              lineHeight: '1.3'
            });
          }
        });
        
        // Position tooltip with better logic
        const positionTooltip = () => {
          if (!tooltip || !element) return;
          
          const rect = element.getBoundingClientRect();
          const tooltipRect = tooltip.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
          
          // Calculate preferred position (above the element, centered)
          let left = rect.left + scrollLeft + (rect.width / 2) - (tooltipRect.width / 2);
          let top = rect.top + scrollTop - tooltipRect.height - 12;
          
          // Adjust horizontal position to stay within viewport
          const padding = 12;
          if (left < padding) {
            left = padding;
          } else if (left + tooltipRect.width > viewportWidth - padding) {
            left = viewportWidth - tooltipRect.width - padding;
          }
          
          // Adjust vertical position if tooltip would be cut off at the top
          if (top < scrollTop + padding) {
            // Position below the element instead
            top = rect.bottom + scrollTop + 12;
          }
          
          // Final check to ensure tooltip stays within viewport
          if (top + tooltipRect.height > scrollTop + viewportHeight - padding) {
            top = scrollTop + viewportHeight - tooltipRect.height - padding;
          }
          
          // Ensure minimum positions
          left = Math.max(padding, left);
          top = Math.max(scrollTop + padding, top);
          
          tooltip.style.left = `${left}px`;
          tooltip.style.top = `${top}px`;
        };
        
        // Position tooltip after a brief delay to ensure it's rendered
        requestAnimationFrame(() => {
          positionTooltip();
          
          // Fade in tooltip
          if (tooltip && isMouseOver) {
            tooltip.style.opacity = '1';
            tooltip.style.transform = 'translateY(0)';
          }
        });
        
      }, 400); // Reduced delay for better responsiveness
    };
    
    // Mouse leave event - hide tooltip
    const mouseLeaveHandler = () => {
      isMouseOver = false;
      clearTimeouts();
      
      if (tooltip) {
        hideTimeout = window.setTimeout(() => {
          removeTooltip();
        }, 150); // Faster hide for better UX
      }
    };
    
    // Add event listeners
    element.addEventListener('mouseenter', mouseEnterHandler);
    element.addEventListener('mouseleave', mouseLeaveHandler);
    
    // Store cleanup function on the element for later removal
    (element as any)._tooltipCleanup = () => {
      clearTimeouts();
      removeTooltip();
      element.removeEventListener('mouseenter', mouseEnterHandler);
      element.removeEventListener('mouseleave', mouseLeaveHandler);
    };
  }

  goToAddEvent(date?: Date) {
    const queryParams: Record<string, string> = {};
    if (date) {
      const defaults = buildNewEventDateDefaults(date);
      queryParams['start_date'] = defaults.start_date;
      queryParams['end_date'] = defaults.end_date;
    }
    this.ngZone.run(() => {
      void this.router.navigate(['/add-event'], { queryParams });
    });
  }

  onCalendarScheduleViewChange(event?: { value?: string }): void {
    const next = event?.value;
    if (next !== 'Day' && next !== 'Week' && next !== 'Month') {
      return;
    }
    if (next === this.calendarScheduleView) {
      return;
    }

    this.calendarScheduleView = next;
    this.persistCalendarScheduleView();
    if (this.scheduleObj) {
      this.scheduleObj.currentView = next;
    }
    this.currentExpectedDateRange = null;
    this.lastLoadParams = '';
    this.replaceEventCacheOnNextLoad = true;
    this.clearCalendarEventDisplay();
    this.dispatchLoadCalendarEvents();
    this.cdr.detectChanges();
  }

  private restoreCalendarScheduleView(): void {
    try {
      const stored = localStorage.getItem(DunaEventsCalendarComponent.SCHEDULE_VIEW_STORAGE_KEY);
      if (stored === 'Day' || stored === 'Week' || stored === 'Month') {
        this.calendarScheduleView = stored;
      }
    } catch {
      this.calendarScheduleView = 'Month';
    }
  }

  private persistCalendarScheduleView(): void {
    try {
      localStorage.setItem(
        DunaEventsCalendarComponent.SCHEDULE_VIEW_STORAGE_KEY,
        this.calendarScheduleView
      );
    } catch {
      // Ignore storage errors (private mode, quota, etc.)
    }
  }

  toggleView(event?: any) {
    
    // Get the new value from the event if available, otherwise toggle
    let newViewType: 'calendar' | 'table';
    if (event && event.value) {
      newViewType = event.value;
    } else {
      // Toggle based on current viewType
      newViewType = this.viewType === 'calendar' ? 'table' : 'calendar';
    }
    
    if (newViewType === this.viewType) {
      return;
    }
    
    
    // Switch UI immediately so the user sees the selected view without delay
    this.viewType = newViewType;
    if (newViewType === 'table') {
      this.ensureListViewDateRange();
      const needsReload = !this.listViewDateRangeMatchesLoadedData();
      this.prepareListViewTransition(needsReload);
    } else {
      this.isListViewRefreshing = false;
    }
    this.cdr.detectChanges();
    
    // State change triggers setupViewSubscriptions() which handles the data reload
    const newState = newViewType === 'table';
    this.componentStateService.setState('events_calendar_toggle', newState);
    this.syncViewToUrl();
    this.reloadFacetsForCurrentView();
  }

  // Refactored data loading methods for better separation of concerns
  
  /**
   * Load data specifically for the list view with date range validation
   */

  /**
   * Common method to execute the actual data loading with proper parameter formatting
   */
  private executeDataLoad(filters?: any): void {
    
    // Clear any existing debounce timer
    if (this.dataLoadDebounceTimer) {
      clearTimeout(this.dataLoadDebounceTimer);
      this.dataLoadDebounceTimer = null;
    }
    
    // Cancel any pending data load subscription
    if (this.dataLoadSubscription) {
      this.dataLoadSubscription.unsubscribe();
      this.dataLoadSubscription = null;
    }
    
    // Clear any pending safety timeout
    if (this.safetyTimeout) {
      clearTimeout(this.safetyTimeout);
      this.safetyTimeout = null;
    }
    
    // Debounce the actual data load execution (300ms delay)
    // This prevents rapid successive calls from overwhelming the backend
    this.dataLoadDebounceTimer = setTimeout(() => {
      this.dataLoadDebounceTimer = null;
      this.executeDataLoadInternal(filters);
    }, 300);
  }

  private executeDataLoadInternal(filters?: any): void {
    
    // CRITICAL: Always require date filters to prevent loading all events
    if (!this.hasValidDateFilters(filters)) {
      console.error('[DUNA-EVENTS] BLOCKED: No valid date filters provided - this would load all events!');
      console.error('[DUNA-EVENTS] Filters received:', filters);
      console.error('[DUNA-EVENTS] Current view type:', this.viewType);
      console.error('[DUNA-EVENTS] Call stack for blocked call:', new Error().stack);
      this.isInitialLoading = false;
      this.isLoadingData = false; // Clear loading state for blocked calls
      this.cdr.detectChanges();
      return;
    }
    
    // Prevent recursive loading
    if (this.isLoadingData) {
      return;
    }
    
    this.isLoadingData = true;
    this.isLoadingFreshData = true; // Prevent filter events from overriding fresh data
    this.hasAttemptedDataLoad = true; // Mark that we've attempted a data load
    
    // Set loading state only if this is the initial load
    if (!this.hasLoadedInitialData) {
      this.isInitialLoading = true;
    }
    
    
    // Always trigger change detection to show loading indicator
    this.cdr.detectChanges();
    
    const startTime = performance.now();
    
    if (!this.eventsDataSource) {
      console.error('[DUNA-EVENTS] Events data source not initialized');
      this.isInitialLoading = false;
      this.isLoadingData = false; // Clear loading state for missing data source
      this.cdr.detectChanges();
      return;
    }
    
    // Save current filters
    this.currentFilters = filters || {};
    
    // Convert filters to queryParams format (not filters array)
    const queryParams: any = {};
    if (this.currentFilters) {
      Object.keys(this.currentFilters).forEach(key => {
        const value = this.currentFilters[key];
        if (value !== undefined && value !== null) {
          // Skip empty strings, empty arrays, and empty objects
          if (value === '' || 
              (Array.isArray(value) && value.length === 0) ||
              (typeof value === 'object' && Object.keys(value).length === 0)) {
            return;
          }
          
          // Convert Date objects to ISO date strings for API compatibility
          if (key === 'startDate' || key === 'endDate') {
            if (value instanceof Date) {
              queryParams[key] = value.toISOString().split('T')[0];
            } else {
              queryParams[key] = value;
            }
          } else if (key === 'organizationIds' && Array.isArray(value)) {
            // Convert organizationIds array to comma-separated string
            queryParams.organization_ids = value.join(',');
          } else if (key === 'educational_path_ids' || key === 'educationalPathIds') {
            // Handle educational path IDs - convert array to comma-separated string if needed
            if (Array.isArray(value)) {
              queryParams.educational_path_ids = value.join(',');
            } else if (typeof value === 'string') {
              queryParams.educational_path_ids = value;
            }
          } else {
            queryParams[key] = value;
          }
        }
      });
    }
    
    // CRITICAL: If organizationIds is missing from filters but exists in filter service, add it
    // This ensures organization filter is included even on initial load before filter component has parsed URL
    if (!queryParams.organization_ids && !this.currentFilters?.organizationIds) {
      const filter = this.filterService.getFilter('events-filter');
      if (filter) {
        try {
          const currentFilter = (filter as any).currentFilterValue || (filter as any).filterValue;
          if (currentFilter?.organizationIds && currentFilter.organizationIds.length > 0) {
            queryParams.organization_ids = currentFilter.organizationIds.join(',');
          }
        } catch (e) {
          console.warn('[DUNA-EVENTS] executeDataLoadInternal: Could not get organizationIds from filter service:', e);
        }
      }
    }
    
    // Log queryParams before sending to help debug

    // Cancel any pending data load subscription
    if (this.dataLoadSubscription) {
      this.dataLoadSubscription.unsubscribe();
      this.dataLoadSubscription = null;
    }

    // Clear any existing safety timeout
    if (this.safetyTimeout) {
      clearTimeout(this.safetyTimeout);
      this.safetyTimeout = null;
    }

    // Execute the data source with queryParams (not filters array)
    
    
    // Track if next was called to detect if Observable completes without emitting
    let nextWasCalled = false;
    
    const observable = this.eventsDataSource.execute({
      dataSourceKey: 'events',
      queryParams: queryParams  // Use queryParams instead of filters
    });
    
    if (!observable) {
      console.error('[DUNA-EVENTS] Observable is null or undefined!');
      this.isInitialLoading = false;
      this.isLoadingData = false;
      this.cdr.detectChanges();
      return;
    }
    
    
    this.dataLoadSubscription = observable.subscribe({
      next: (data: any[]) => {
        nextWasCalled = true;
        try {
          const dataLoadTime = performance.now() - startTime;
          
          // Check if data is valid
          if (!data || !Array.isArray(data)) {
            console.error('[DUNA-EVENTS] Invalid data received:', data);
            this.updateFilterResultCount(0);
            this.isInitialLoading = false;
            this.isLoadingData = false; // Clear loading state for invalid data
            this.cdr.detectChanges();
            return;
          }
        
        // Log the first item for debugging
        if (data.length > 0) {
          
          // Check if the first item has a description
          if (data[0].description) {
          } else {
          }
        }
        
        // Preprocess the data to add title property
        const preprocessStart = performance.now();
        const processedData = this.preprocessEvents(data);
        const preprocessTime = performance.now() - preprocessStart;
        
        // Apply frontend filters (hideSubEvents, educationalPathIds)
        const filteredData = this.applyFrontendFilters(processedData);
        
        this.data = filteredData;

        this.loadOrgSettingsForCourseCapacity(filteredData);
        
        // Update card view data
        this.updateCardData();
        
        // Update organizations in the filter component with the loaded events
        // Use setTimeout to ensure the filter component is fully initialized
        setTimeout(() => {
          this.updateFilterOrganizations(data);
        }, 100);
        
        // Update the calendar data
        const calendarUpdateStart = performance.now();
        this.updateCalendarData();
        const calendarUpdateTime = performance.now() - calendarUpdateStart;
        
        // Ensure card data is updated if we're in table view (we're using card view, not table)
        if (this.viewType === 'table') {
          // Just ensure change detection runs to display the cards
          this.cdr.detectChanges();
        }
        
        // Update result count in the filter component
        this.updateFilterResultCount(data.length);
        
        // Log loading state for debugging
        
        // Complete loading state on first successful data load (including empty results)
        if (this.isInitialLoading) {
          this.completeInitialLoading();
          this.hasLoadedInitialData = true; // Mark that initial data has been loaded
        }
        
        // Clear loading state immediately for both empty and non-empty results
        this.isLoadingData = false;
        
        // Clear the safety timeout since data loaded successfully
        if (this.safetyTimeout) {
          clearTimeout(this.safetyTimeout);
          this.safetyTimeout = null;
        }
        
        // Force change detection to ensure UI updates immediately
        this.cdr.detectChanges();
        
        // Additional safety: ensure loading state is completely cleared after a short delay
        setTimeout(() => {
          if (this.isLoadingData || this.isInitialLoading) {
            console.warn('[DUNA-EVENTS] Safety: Force clearing loading state after data load');
            this.isLoadingData = false;
            this.isInitialLoading = false;
        this.cdr.detectChanges();
          }
        }, 100);
        
        
        // Delay clearing the fresh data flag to prevent filter events from overriding
        setTimeout(() => {
          this.isLoadingFreshData = false;
        }, 500);
        } catch (error) {
          console.error('[DUNA-EVENTS] Error in next handler while processing data:', error);
          // Ensure loading state is cleared even on error
          this.isInitialLoading = false;
          this.isLoadingData = false;
          if (this.safetyTimeout) {
            clearTimeout(this.safetyTimeout);
            this.safetyTimeout = null;
          }
          this.cdr.detectChanges();
        }
      },
      error: (error: any) => {
        console.error('[DUNA-EVENTS] Error loading data:', error);
        this.updateFilterResultCount(0);
        this.isInitialLoading = false;
        this.isLoadingData = false; // Clear loading state
        this.isLoadingFreshData = false; // Allow filter events again
        
        // Clear the safety timeout on error
        if (this.safetyTimeout) {
          clearTimeout(this.safetyTimeout);
          this.safetyTimeout = null;
        }
        
        this.cdr.detectChanges();
      },
      complete: () => {
        
        // Clear the safety timeout when subscription completes (do this first)
        if (this.safetyTimeout) {
          clearTimeout(this.safetyTimeout);
          this.safetyTimeout = null;
        }
        
        // If Observable completed without calling next, that's a problem
        if (!nextWasCalled) {
          console.error('[DUNA-EVENTS] WARNING: Observable completed without emitting any data! This may indicate an issue with the data source.');
          // Clear loading state even if no data was received
          this.isInitialLoading = false;
          this.isLoadingData = false;
          this.isLoadingFreshData = false;
          this.updateFilterResultCount(0);
          this.cdr.detectChanges();
          return; // Exit early since we've handled the error case
        }
        
        // Reset loading flag when complete
        this.isLoadingData = false;
        this.isLoadingFreshData = false;
        
        // Safety check: ensure initial loading is cleared if still active
        if (this.isInitialLoading) {
          this.isInitialLoading = false;
          this.cdr.detectChanges();
        }
        
        // Additional safety: ensure both loading flags are cleared
        if (this.isLoadingData || this.isInitialLoading) {
          console.warn('[DUNA-EVENTS] Safety: Force clearing all loading states in complete handler');
          this.isLoadingData = false;
          this.isInitialLoading = false;
          this.isLoadingFreshData = false;
          this.cdr.detectChanges();
        }
        
      }
    });
    
    // Add the subscription to the array
    if (this.dataLoadSubscription) {
      this.subscriptions.push(this.dataLoadSubscription);
    }
    
    // Safety timeout to ensure loading state is cleared even if something goes wrong
    // Store as class property so it can be properly cleared in handlers
    // Store subscription reference for timeout check
    const subscriptionRef = this.dataLoadSubscription;
    this.safetyTimeout = setTimeout(() => {
      // Only clear if loading state is still active AND subscription is still the same (not cancelled/replaced)
      if ((this.isLoadingData || this.isInitialLoading) && this.dataLoadSubscription === subscriptionRef) {
        console.warn('[DUNA-EVENTS] Safety timeout: Force clearing loading state after 10 seconds');
        console.warn('[DUNA-EVENTS] Timeout state - isInitialLoading:', this.isInitialLoading, 'isLoadingData:', this.isLoadingData, 'hasLoadedInitialData:', this.hasLoadedInitialData);
        
        // Try to unsubscribe if subscription is still active
        if (this.dataLoadSubscription && !this.dataLoadSubscription.closed) {
          console.warn('[DUNA-EVENTS] Unsubscribing from data load subscription due to timeout');
          this.dataLoadSubscription.unsubscribe();
        }
        
        this.isLoadingData = false;
        this.isInitialLoading = false;
        this.isLoadingFreshData = false;
        this.safetyTimeout = null; // Clear the timeout reference
        this.updateFilterResultCount(0); // Update count to 0 on timeout
        this.cdr.detectChanges();
      } else {
        // Loading state was already cleared or subscription was replaced
      }
    }, 10000); // 10 second timeout
  }
  
  // Add a method to preprocess events and set the title property
  private preprocessEvents(events: any[]): any[] {
    if (!events || !Array.isArray(events)) {
      return [];
    }
    
    return events.map(event => {
      if (!event) return event;
      
      // Create a new event object with all the original properties
      const processedEvent = { ...event };
      
      // Extract title using the optimized helper method
      const title = this.extractEventTitle(event);
      
      // Set the title property directly on the event
      (processedEvent as any).title = title;
      
      // Set an empty periode property to ensure the column key exists
      (processedEvent as any).periode = '';
      
      
      return processedEvent;
    });
  }
  
  // Apply frontend filters (hideSubEvents, educationalPathIds)
  private isEventSharedSeriesNonAnchor(event?: any): boolean {
    if (!event) {
      return false;
    }
    if (event.series_share_participants !== true) {
      return false;
    }
    if (event.parent_event_id) {
      return true;
    }
    if (!event.linked_event_group_id) {
      return false;
    }
    return event.series_primary === false;
  }

  private buildLinkedGroupAnchorIds(events: any[]): Set<string | number> {
    const groups = new Map<string, any[]>();
    for (const event of events) {
      const groupId = event.linked_event_group_id;
      if (!groupId) {
        continue;
      }
      if (!groups.has(groupId)) {
        groups.set(groupId, []);
      }
      groups.get(groupId)!.push(event);
    }

    const anchorIds = new Set<string | number>();
    for (const members of groups.values()) {
      const explicitAnchor = members.find((e) => e.series_primary === true);
      if (explicitAnchor) {
        anchorIds.add(explicitAnchor.event_id ?? explicitAnchor.guid);
        continue;
      }
      const sorted = [...members].sort(
        (a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
      );
      const anchor = sorted[0];
      if (anchor) {
        anchorIds.add(anchor.event_id ?? anchor.guid);
      }
    }
    return anchorIds;
  }

  private isSharedSeriesNonAnchor(event: any, anchorIds: Set<string | number>): boolean {
    if (event.series_share_participants !== true) {
      return false;
    }
    if (event.parent_event_id) {
      return true;
    }
    if (!event.linked_event_group_id) {
      return false;
    }
    if (event.series_primary === true) {
      return false;
    }
    if (event.series_primary === false) {
      return true;
    }
    const id = event.event_id ?? event.guid;
    return !anchorIds.has(id);
  }

  private shouldHideSeriesExtraDay(
    event: any,
    anchorIds: Set<string | number>,
    currentUserId: number | null
  ): boolean {
    const hideAsSubEvent = !!event.parent_event_id || this.isSharedSeriesNonAnchor(event, anchorIds);
    if (!hideAsSubEvent) {
      return false;
    }
    if (currentUserId && event.participants && Array.isArray(event.participants)) {
      return !event.participants.some(
        (p: any) => p.user_id === currentUserId || p.users_id === currentUserId
      );
    }
    return true;
  }

  private applyFrontendFilters(events: any[]): any[] {
    if (!events || !Array.isArray(events)) {
      return [];
    }

    let filteredEvents = this.filterGroupCalendarEvents(events);
    const currentFilter = this.currentEventsFilter;
    if (!currentFilter) {
      return filteredEvents;
    }

    if (currentFilter.hideSubEvents === true) {
      const currentUser = this.authService.getCurrentUser();
      const currentUserId = currentUser?.id || null;
      const anchorIds = this.buildLinkedGroupAnchorIds(filteredEvents);

      filteredEvents = filteredEvents.filter(
        (event) => !this.shouldHideSeriesExtraDay(event, anchorIds, currentUserId)
      );
    }

    return filteredEvents;
  }

  private filterGroupCalendarEvents(events: any[]): any[] {
    if (!events?.length) {
      return [];
    }
    return events.filter(
      (event) => event.user_group_id == null || Number(event.user_group_id) <= 0
    );
  }

  /** Derive facet badges from the same events the user actually sees. */
  private syncFacetCountsFromLoadedEvents(displayedEvents: any[], sourceForHiddenCounts: any[]): void {
    const eventTypes: Record<string, number> = {};
    const organizations: Record<string, number> = {};
    const educationalPaths: Record<string, number> = {};
    const modules: Record<string, number> = {};
    const locations: Record<string, number> = {};

    for (const event of displayedEvents) {
      if (event.event_type) {
        const t = String(event.event_type);
        eventTypes[t] = (eventTypes[t] ?? 0) + 1;
      }
      const orgId = event.organizations_id ?? event.organization?.id;
      if (orgId != null) {
        const key = String(orgId);
        organizations[key] = (organizations[key] ?? 0) + 1;
      }
      const locGuid = event.predefined_location_guid ?? event.predefined_location?.guid;
      if (locGuid) {
        const key = String(locGuid);
        locations[key] = (locations[key] ?? 0) + 1;
      }
      if (Array.isArray(event.items)) {
        for (const item of event.items) {
          if (item.deleted) {
            continue;
          }
          const type = String(item.item_type || '').toLowerCase();
          if (type === 'modules' || type === 'module') {
            const id = String(item.item_id);
            modules[id] = (modules[id] ?? 0) + 1;
          } else if (type === 'educationalpaths' || type === 'educationalpath') {
            const id = String(item.item_id);
            educationalPaths[id] = (educationalPaths[id] ?? 0) + 1;
          }
        }
      }
    }

    const currentUser = this.authService.getCurrentUser();
    const userId = currentUser?.id ?? null;
    const anchorIds = this.buildLinkedGroupAnchorIds(sourceForHiddenCounts);

    let subEvents = 0;
    let fullEvents = 0;
    let participated = 0;
    for (const event of sourceForHiddenCounts) {
      if (this.shouldHideSeriesExtraDay(event, anchorIds, userId)) {
        subEvents++;
      }
      if (this.isUserParticipatingInEvent(event)) {
        participated++;
      }
      if (this.isEventAtCapacity(event)) {
        fullEvents++;
      }
    }

    this.filterState.setFacetCounts({
      total: displayedEvents.length,
      eventTypes,
      organizations,
      educationalPaths,
      modules,
      locations,
      hiddenCounts: { subEvents, fullEvents, participated },
    });
  }

  private isUserParticipatingInEvent(event: any): boolean {
    const uuid = this.authService.getCurrentUser()?.uuid;
    if (!uuid || !Array.isArray(event?.participants)) {
      return false;
    }
    return event.participants.some((p: any) => {
      if (p.role === 'instructor') {
        return false;
      }
      const st = p.status != null ? String(p.status).toLowerCase() : '';
      if (st === 'waitlist' || st === 'not_participant' || st === 'cancellation' || st === 'cancelled') {
        return false;
      }
      return p.user?.uuid === uuid;
    });
  }

  private isEventAtCapacity(event: any): boolean {
    const capacity = this.getEventCapacity(event);
    if (capacity <= 0) {
      return false;
    }
    return this.getRegisteredCount(event) >= capacity;
  }

  private initializeTableSettings(filters?: any): void {
    // Create table settings with the provided filters
    const tableSettings = {
      columns: [
        { 
          key: 'title', 
          name: 'title', 
          header: 'Titel',
          displayName: 'Titel', 
          type: 'text',
          transform: (value: any) => {
            // Increment the counter
            this.transformCallCount++;
            
            
            // If the value is already a string, just return it
            if (typeof value === 'string') {
              return value;
            }
            
            // If the value is null or undefined, return default
            if (!value) {
              return 'Unnamed Event';
            }
            
            // If the value has a title property, use it
            if (value.title) {
              return value.title;
            }
            
            // If the value is an event object, use the optimized helper
            if (typeof value === 'object') {
              return this.extractEventTitle(value);
            }
            
            // If we couldn't find a title, return a default
            return 'Unnamed Event';
          }
        },
        { 
          key: 'periode', 
          name: 'periode', 
          header: 'Periode',
          displayName: 'Periode', 
          type: 'text',
          transform: (value: any, row?: any) => {
            // The dynamic table might pass the value directly or the entire row
            // We need to work with the event object to get start_date and end_date
            const event = row || value;
            
            if (!event) return '';
            
            try {
              const startDate = event.start_date ? new Date(event.start_date) : null;
              const endDate = event.end_date ? new Date(event.end_date) : null;
              
              if (!startDate || isNaN(startDate.getTime())) return '';
              
              const dateOptions: Intl.DateTimeFormatOptions = { 
                day: '2-digit', 
                month: '2-digit', 
                year: 'numeric' 
              };
              const timeOptions: Intl.DateTimeFormatOptions = { 
                hour: '2-digit', 
                minute: '2-digit' 
              };
              
              const startDateStr = startDate.toLocaleDateString('da-DK', dateOptions);
              const startTimeStr = startDate.toLocaleTimeString('da-DK', timeOptions);
              
              if (!endDate || isNaN(endDate.getTime()) || startDate.getTime() === endDate.getTime()) {
                return `${startDateStr} ${startTimeStr}`;
              }
              
              const endDateStr = endDate.toLocaleDateString('da-DK', dateOptions);
              const endTimeStr = endDate.toLocaleTimeString('da-DK', timeOptions);
              
              // Check if it's the same date
              if (startDateStr === endDateStr) {
                return `${startDateStr} ${startTimeStr} - ${endTimeStr}`;
              } else {
                return `${startDateStr} ${startTimeStr} - ${endDateStr} ${endTimeStr}`;
              }
            } catch (error) {
              console.error('[DUNA-EVENTS] Error formatting date range:', error);
              return '';
            }
          }
        },
        { 
          key: 'event_type', 
          name: 'event_type', 
          header: 'Type',
          displayName: 'Type', 
          type: 'text',
          transform: (value: any, row?: any) => {
            
            // Hide event type for "no events" row
            if (row && row.isNoEventsRow) {
              return '';
            }
            
            // If the value is already a string, handle it directly
            if (typeof value === 'string') {
              switch(value) {
                case 'education': return 'Uddannelse';
                case 'exercise': return 'Øvelse';
                case 'course': return 'Kursus';
                default: return value;
              }
            }
            
            // If the value is null or undefined, return empty string
            if (!value) {
              return '';
            }
            
            // If the value is an event object
            if (typeof value === 'object') {
              if (value.event_type) {
                switch(value.event_type) {
                  case 'education': return 'Uddannelse';
                  case 'exercise': return 'Øvelse';
                  case 'course': return 'Kursus';
                  default: return value.event_type;
                }
              }
            }
            
            return '';
          }
        },
        {
          key: 'actions',
          header: '',
          displayName: '',
          type: 'button',
          buttonText: 'Vis',
          buttonColor: 'primary',
          variant: 'raised',
          action: (event: any) => {
            // Don't show action button for "no events" row
            if (event && event.isNoEventsRow) {
              return false; // Return false to prevent button rendering
            }
            if (event && event.guid) {
              this.openCalendarEventModal(event.guid);
            }
            return true; // Return true for normal events
          },
          transform: (value: any, row?: any) => {
            // Hide action button for "no events" row
            if (row && row.isNoEventsRow) {
              return { type: 'text', value: '' }; // Return text type instead of button
            }
            return value;
          }
        }
      ],
      enablePagination: true,
      enableSorting: true,
      enableFiltering: true,
      enableSearch: false,
      pageSize: 10,
      filters: []
    };
    
    // Set the settings property first
    this.settings = tableSettings;
    
    // Then dispatch the action to update the store
    try {
      this.store.dispatch(DataActions.loadDataSuccess({
        dataSourceKey: this.dataSourceKey,
        data: {
          items: [],
          totalCount: 0,
          pageSize: 10,
          pageIndex: 0
        },
        settings: tableSettings
      }));
    } catch (error) {
      console.error('[DUNA-EVENTS] Error dispatching settings to store:', error);
    }
  }

  // Note: getResourceIdFromType method removed since we no longer use TimelineMonth view

  public onEventClick(args: any): void {
    const eventGuid = args.event.Id;
    this.openCalendarEventModal(eventGuid);
  }

  private openCalendarEventModal(guid: string): void {
    this.componentDataSourceService.functionRegistryService.executeFunction(
      'openModal',
      'events_calendar_event',
      { guid, openEditInNewTab: true }
    );
  }

  /** "+n mere" in month view — show all events for that day in a dialog. */
  public onMoreEventsClick(args: any): void {
    if (args) {
      args.cancel = true;
    }
    const day = args?.startTime ? new Date(args.startTime) : null;
    if (!day || isNaN(day.getTime())) {
      return;
    }
    this.openDayEventsDialog(day);
  }

  /** Primary day-click handler via Angular event delegation on the calendar wrapper. */
  public onCalendarWrapperClick(event: MouseEvent): void {
    if (this.viewType !== 'calendar' || !this.canAddEvents()) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }
    if (
      target.closest('.e-header-container') ||
      target.closest('.e-toolbar') ||
      target.closest('.e-date-header-wrap')
    ) {
      return;
    }
    this.handleScheduleDayInteraction(target, undefined, event);
  }

  /** Click on a day cell — list events or open add-event when permitted. */
  public onCalendarCellClick(args: any): void {
    if (!args?.startTime) {
      return;
    }
    const target = (args.element as HTMLElement | undefined) ??
      (args.event?.target as HTMLElement | undefined);
    if (args.cancel !== undefined) {
      args.cancel = true;
    }
    this.handleScheduleDayInteraction(target, new Date(args.startTime));
  }

  /** Fallback when readonly schedule blocks cellClick but still opens the editor popup. */
  public onCalendarPopupOpen(args: any): void {
    if (!this.canAddEvents() || this.viewType !== 'calendar') {
      return;
    }
    if (args?.type !== 'Editor' && args?.type !== 'QuickInfo') {
      return;
    }
    const start =
      args?.data?.StartTime ??
      args?.data?.startTime ??
      args?.startTime ??
      args?.data?.start_date;
    if (!start) {
      return;
    }
    args.cancel = true;
    this.handleScheduleDayInteraction(undefined, new Date(start));
  }

  /** Attach per-cell click handlers when Syncfusion renders month view cells. */
  public onScheduleRenderCell(args: {
    date?: Date;
    element?: HTMLElement;
    elementType?: string;
  }): void {
    const element = args.element;
    const cellDate = args.date ? new Date(args.date) : null;
    if (!element || !cellDate || isNaN(cellDate.getTime())) {
      return;
    }

    const type = args.elementType;
    const supportedTypes = new Set(['workCells', 'monthCells', 'alldayCells']);
    if (!type || !supportedTypes.has(type)) {
      return;
    }

    const existing = (element as HTMLElement & { __dunaDayClickHandler?: (event: MouseEvent) => void })
      .__dunaDayClickHandler;
    if (existing) {
      element.removeEventListener('click', existing, true);
    }

    const handler = (event: MouseEvent) => {
      this.ngZone.run(() => {
        this.handleScheduleDayInteraction(event.target as HTMLElement, cellDate, event);
      });
    };
    (element as HTMLElement & { __dunaDayClickHandler?: (event: MouseEvent) => void }).__dunaDayClickHandler =
      handler;
    element.addEventListener('click', handler, true);

    if (this.canAddEvents()) {
      element.classList.add('duna-add-event-cell');
    } else {
      element.classList.remove('duna-add-event-cell');
    }
  }

  private bindCalendarDayClickHandler(): void {
    this.unbindCalendarDayClickHandler();
    if (!this.scheduleObj?.element) {
      return;
    }
    this.calendarDayClickHandler = (event: MouseEvent) => {
      this.ngZone.run(() => {
        this.handleScheduleDayInteraction(event.target as HTMLElement, undefined, event);
      });
    };
    this.scheduleObj.element.addEventListener('click', this.calendarDayClickHandler, true);
  }

  private unbindCalendarDayClickHandler(): void {
    if (this.scheduleObj?.element && this.calendarDayClickHandler) {
      this.scheduleObj.element.removeEventListener('click', this.calendarDayClickHandler, true);
    }
    this.calendarDayClickHandler = undefined;
  }

  private handleScheduleDayInteraction(
    target: HTMLElement | null | undefined,
    cellDate?: Date,
    nativeEvent?: MouseEvent
  ): void {
    if (!this.canAddEvents() || this.viewType !== 'calendar') {
      return;
    }

    if (target?.closest('.e-appointment') || target?.closest('.e-more-indicator')) {
      return;
    }

    let day = cellDate;
    if (!day || isNaN(day.getTime())) {
      const workCell = this.findWorkCell(target);
      if (!workCell) {
        return;
      }
      day = this.resolveDayFromWorkCell(workCell) ?? undefined;
    }
    if (!day || isNaN(day.getTime())) {
      return;
    }

    const events = this.getEventsForCalendarDay(day);
    const isDayNumberClick =
      !!target &&
      (target.classList.contains('e-navigate') || !!target.closest('.e-navigate'));

    if (isDayNumberClick && events.length > 0) {
      nativeEvent?.preventDefault();
      nativeEvent?.stopPropagation();
      this.openDayEventsDialog(day);
      return;
    }

    nativeEvent?.preventDefault();
    nativeEvent?.stopPropagation();
    this.goToAddEvent(day);
  }

  private findWorkCell(target: HTMLElement | null | undefined): HTMLElement | null {
    let el = target ?? null;
    while (el && !el.classList.contains('e-schedule')) {
      if (el.tagName === 'TD') {
        if (
          el.classList.contains('e-work-cells') ||
          el.classList.contains('e-work-days') ||
          el.classList.contains('e-alternate-cells') ||
          el.querySelector('.e-navigate')
        ) {
          return el;
        }
      }
      el = el.parentElement;
    }
    return null;
  }

  private resolveDayFromWorkCell(workCell: HTMLElement): Date | null {
    const schedule = this.scheduleObj as ScheduleComponent & {
      getCellDetails?: (element: Element) => { startTime?: Date };
    };
    if (schedule?.getCellDetails) {
      try {
        const details = schedule.getCellDetails(workCell);
        if (details?.startTime) {
          return new Date(details.startTime);
        }
      } catch {
        // Fall through to DOM parsing
      }
    }

    const navigate = workCell.querySelector('.e-navigate');
    const dayNum = navigate ? parseInt(navigate.textContent?.trim() || '', 10) : NaN;
    if (!Number.isFinite(dayNum)) {
      const ariaLabel = workCell.getAttribute('aria-label') || '';
      const ariaMatch = ariaLabel.match(/(?:^|\s)(\d{1,2})(?:\s|,|$)/);
      const fromAria = ariaMatch ? parseInt(ariaMatch[1], 10) : NaN;
      if (!Number.isFinite(fromAria)) {
        return null;
      }
      return this.buildDateFromDayNumber(workCell, fromAria);
    }

    return this.buildDateFromDayNumber(workCell, dayNum);
  }

  private buildDateFromDayNumber(workCell: HTMLElement, dayNum: number): Date {
    const base = this.scheduleObj?.selectedDate
      ? new Date(this.scheduleObj.selectedDate)
      : new Date(this.currentCalendarMonth);
    let month = base.getMonth();
    let year = base.getFullYear();
    if (workCell.classList.contains('e-other-month')) {
      if (dayNum > 20) {
        month -= 1;
      } else {
        month += 1;
      }
      if (month < 0) {
        month = 11;
        year -= 1;
      } else if (month > 11) {
        month = 0;
        year += 1;
      }
    }
    return new Date(year, month, dayNum);
  }

  private getEventsForCalendarDay(day: Date): EventDTO[] {
    if (!this.data?.length) {
      return [];
    }
    const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate());
    const dayEnd = new Date(dayStart);
    dayEnd.setDate(dayEnd.getDate() + 1);

    return this.data
      .filter((event: EventDTO) => {
        if (!event?.start_date) {
          return false;
        }
        const start = new Date(event.start_date);
        const end = event.end_date ? new Date(event.end_date) : start;
        if (isNaN(start.getTime())) {
          return false;
        }
        const endTime = isNaN(end.getTime()) ? start : end;
        return start < dayEnd && endTime >= dayStart;
      })
      .sort(
        (a, b) =>
          new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
      );
  }

  private openDayEventsDialog(day: Date): void {
    const events = this.getEventsForCalendarDay(day);
    if (events.length === 0) {
      return;
    }

    const dialogData: DunaCalendarDayEventsDialogData = {
      date: day,
      events,
      showAddEvent: this.canAddEvents(),
    };
    const ref = this.dialog.open(DunaCalendarDayEventsDialogComponent, {
      width: '440px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      panelClass: 'duna-calendar-day-events-dialog-panel',
      data: dialogData
    });

    ref.afterClosed().pipe(take(1)).subscribe((result: { guid?: string; action?: string } | undefined) => {
      if (result?.action === 'add') {
        this.goToAddEvent(day);
        return;
      }
      if (result?.guid) {
        this.openCalendarEventModal(result.guid);
      }
    });
  }

  // Note: ResourceDataSource removed since we no longer use TimelineMonth view

  // Helper method to update table data source from current data
  private updateTableDataSource() {
    
    if (!this.data || this.data.length === 0) {
      return;
    }
    
    // Log the first event in detail
    if (this.data.length > 0) {
      const firstEvent = this.data[0];
      
      // Check if the event has a description property
      if (firstEvent.description) {
        
        if (firstEvent.description.short_description) {
          
          if (firstEvent.description.short_description['da']) {
          }
        }
      } else {
      }
      
      // Test the transform function directly
      if (this.settings && this.settings.columns) {
        const titleColumn = this.settings.columns.find((col: any) => col.key === 'title');
        if (titleColumn && typeof titleColumn.transform === 'function') {
          
          // Test with the event object
          const transformedTitle = titleColumn.transform(firstEvent);
          
          // Test with the short description text directly
          if (firstEvent.description && firstEvent.description.short_description && firstEvent.description.short_description['da']) {
            const shortDesc = firstEvent.description.short_description['da'].translation_text;
            const transformedShortDesc = titleColumn.transform(shortDesc);
          }
        } else {
        }
        
        // Test the event_type transform function
        const eventTypeColumn = this.settings.columns.find((col: any) => col.key === 'event_type');
        if (eventTypeColumn && typeof eventTypeColumn.transform === 'function') {
          
          // Test with the event object
          const transformedEventType = eventTypeColumn.transform(firstEvent);
          
          // Test with the event_type string directly
          const transformedEventTypeString = eventTypeColumn.transform(firstEvent.event_type);
        } else {
        }
      }
    }
    
    // Make sure settings and columns exist before mapping
    if (!this.settings || !this.settings.columns) {
      console.error('[DUNA-EVENTS] Settings or columns not initialized');
      this.initializeTableSettings();
      return;
    }
    
    // Update the store with the current data
    this.updateStore(this.data);
    
    // Force change detection
    setTimeout(() => {
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }, 0);
  }

  // Force table initialization
  private forceTableInitialization() {
    
    // First verify we have data
    if (!this.data || this.data.length === 0) {
      if (this.hasAttemptedDataLoad) {
        // Still initialize the table with empty data to show "no events" message
        this.initializeTableWithEmptyData();
        return;
      } else {
        // Don't trigger a load here - let the view subscription handle it
        // This prevents duplicate loads
        this.initializeTableWithEmptyData();
        return;
      }
    }
    
    // Create a new settings object instead of modifying the existing one
    if (this.settings) {
      // Create a new settings object with searchable set to false
      const newSettings = {
        ...this.settings,
        searchable: false
      };
      
      // Update the settings property
      this.settings = newSettings;
      
      // Update the store with the new settings and current data
      try {
        // Ensure data has been properly preprocessed
        const processedData = this.preprocessEvents(this.data);
        
        
        this.store.dispatch(DataActions.loadDataSuccess({
          dataSourceKey: this.dataSourceKey,
          data: {
            items: processedData || [],
            totalCount: processedData?.length || 0,
            pageSize: newSettings?.pageSize || 10,
            pageIndex: 0
          },
          settings: newSettings
        }));
      } catch (error) {
        console.error('[DUNA-EVENTS] Error dispatching settings to store:', error);
      }
    } else {
      // Initialize settings if they don't exist
      this.initializeTableSettings();
    }
    
    // Force change detection
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  // Add a method to update the store with the data
  private updateStore(data: EventDTO[]) {
    
    // Ensure settings are initialized with searchable: false
    if (!this.settings) {
      this.initializeTableSettings();
      return; // Return early as initializeTableSettings will dispatch to the store
    } 
    
    if (this.settings.searchable !== false) {
      // Create a new settings object with searchable set to false
      const newSettings = {
        ...this.settings,
        searchable: false
      };
      
      // Update the settings property
      this.settings = newSettings;
    }
    
    // Filter out any undefined or null items
    const validData = Array.isArray(data) ? data.filter(item => item !== undefined && item !== null) : [];
    
    // Process the data to ensure each item has a title property
    const processedData = validData.map(event => {
      // Create a new event object with all the original properties
      const processedEvent = { ...event };
      
      // Extract title using the same logic as the transform function
      let title = 'Unnamed Event';
      
      // First check if there's a direct title property
      if ((event as any).title) {
        title = (event as any).title;
      }
      // Try to get the title from the description
      else if (event.description) {
        // Try short description first
        if (event.description.short_description) {
          // Try Danish translation first
          if (event.description.short_description['da'] && 
              event.description.short_description['da'].translation_text) {
            title = event.description.short_description['da'].translation_text;
          }
          // Try English if Danish is not available
          else if (event.description.short_description['en'] && 
              event.description.short_description['en'].translation_text) {
            title = event.description.short_description['en'].translation_text;
          }
          // Try any available language
          else {
            const languages = Object.keys(event.description.short_description);
            for (const lang of languages) {
              if (event.description.short_description[lang] && 
                  event.description.short_description[lang].translation_text) {
                title = event.description.short_description[lang].translation_text;
                break;
              }
            }
          }
        }
        
        // Only try long description if we still have the default title (meaning no short description was found)
        if (title === 'Unnamed Event' && event.description.long_description) {
          // Try Danish translation first
          if (event.description.long_description['da'] && 
              event.description.long_description['da'].translation_text) {
            title = event.description.long_description['da'].translation_text;
          }
          // Try English if Danish is not available
          else if (event.description.long_description['en'] && 
              event.description.long_description['en'].translation_text) {
            title = event.description.long_description['en'].translation_text;
          }
          // Try any available language
          else {
            const languages = Object.keys(event.description.long_description);
            for (const lang of languages) {
              if (event.description.long_description[lang] && 
                  event.description.long_description[lang].translation_text) {
                title = event.description.long_description[lang].translation_text;
                break;
              }
            }
          }
        }
      }
      
      // Set the title property directly on the event
      (processedEvent as any).title = title;
      
      // Ensure event_type is properly set
      if (event.event_type) {
        (processedEvent as any).event_type = event.event_type;
      }
      
      // Ensure start_date and end_date are properly set
      if (event.start_date) {
        (processedEvent as any).start_date = event.start_date;
      }
      
      if (event.end_date) {
        (processedEvent as any).end_date = event.end_date;
      }
      
      return processedEvent;
    });
    
    // Log the first processed item for debugging
    if (processedData.length > 0) {

      
      // Test the date_range transform function directly
      if (this.settings && this.settings.columns) {
        const dateRangeColumn = this.settings.columns.find((col: any) => col.key === 'date_range');
        if (dateRangeColumn && typeof dateRangeColumn.transform === 'function') {
          const transformedDateRange = dateRangeColumn.transform(processedData[0]);
        }
      }
    }
    
    // Dispatch an action to update the store with the data
    try {
      
      this.store.dispatch(DataActions.loadDataSuccess({
        dataSourceKey: this.dataSourceKey,
        data: {
          items: processedData,
          totalCount: processedData.length,
          pageSize: this.settings?.pageSize || 10,
          pageIndex: 0
        },
        settings: this.settings
      }));
    } catch (error) {
      console.error('[DUNA-EVENTS] Error dispatching data to store:', error);
    }
  }

  // Add a method to force table component refresh
  private forceTableRefresh() {
    
    if (this.tableComponent) {
      // Log table component state
      // Try to directly set the data on the table component
      if (this.data && this.data.length > 0) {
        try {
          // Try different ways to set the data
          if (this.tableComponent['dataSource']) {
            this.tableComponent['dataSource'] = {
              items: this.data,
              totalCount: this.data.length,
              pageSize: 10,
              pageIndex: 0
            };
          }
          
          // Try to trigger a refresh method if it exists
          if (typeof (this.tableComponent as any)['refresh'] === 'function') {
            (this.tableComponent as any)['refresh']();
          }
          
          // Try to trigger a reload method if it exists
          if (typeof (this.tableComponent as any)['reload'] === 'function') {
            (this.tableComponent as any)['reload']();
          }
        } catch (error) {
          console.error('[DUNA-EVENTS] Error setting data on table component:', error);
        }
      }
      
      // Force change detection on the table component
      if (this.tableComponent['cdr'] && typeof this.tableComponent['cdr'].detectChanges === 'function') {
        this.tableComponent['cdr'].detectChanges();
      }
      
      // Force change detection on the parent component as well
      this.cdr.detectChanges();
      
      // Additional debugging - check if table is actually visible
      setTimeout(() => {
        const tableElement = document.querySelector('app-dynamic-table');
        if (tableElement) {
          const tableRows = tableElement.querySelectorAll('tbody tr');
          
          // Check store state
          this.store.select(state => state.data).subscribe(storeData => {
            if (storeData && storeData[this.dataSourceKey]) {
            } else {
              console.warn('[DUNA-EVENTS] No store data found for key:', this.dataSourceKey);
            }
          }).unsubscribe();
        } else {
          console.warn('[DUNA-EVENTS] Table element not found in DOM');
        }
      }, 100);
    } else {
      // Table component is not used anymore (using card view instead)
      // console.warn('[DUNA-EVENTS] Table component not found for refresh');
    }
  }

  // Add a method to validate that date filters are present
  private hasValidDateFilters(filters?: any): boolean {
    if (!filters) {
      return false;
    }
    
    // Check for startDate and endDate - handle both Date objects and strings
    const hasStartDate = filters.startDate && (
      (typeof filters.startDate === 'string' && filters.startDate.trim() !== '') ||
      (filters.startDate instanceof Date && !isNaN(filters.startDate.getTime()))
    );
    
    const hasEndDate = filters.endDate && (
      (typeof filters.endDate === 'string' && filters.endDate.trim() !== '') ||
      (filters.endDate instanceof Date && !isNaN(filters.endDate.getTime()))
    );
    
    // Require at least one date filter
    return hasStartDate || hasEndDate;
  }

  // Add a method to initialize table with empty data (to show "no events" message)
  private initializeTableWithEmptyData() {
    
    // Ensure settings are initialized
    if (!this.settings) {
      this.initializeTableSettings();
    }
    
    // Create a "no events found" row in Danish
    const noEventsRow = {
      title: 'Ingen begivenheder fundet',
      periode: 'Ingen datoer',
      event_type: '', // Empty to hide the type column
      actions: null, // Null to prevent button rendering
      isNoEventsRow: true, // Flag to identify this as a special row
      isNoDataRow: true // DynamicTableComponent uses this flag to hide buttons
    };
    
    // Update the store with the "no events" row
    try {
      this.store.dispatch(DataActions.loadDataSuccess({
        dataSourceKey: this.dataSourceKey,
        data: {
          items: [noEventsRow],
          totalCount: 1,
          pageSize: this.settings?.pageSize || 10,
          pageIndex: 0
        },
        settings: this.settings
      }));
    } catch (error) {
      console.error('[DUNA-EVENTS] Error dispatching "no events" row to store:', error);
    }
    
    // Force change detection
    this.cdr.detectChanges();
    
    // Complete loading state
    this.isInitialLoading = false;
    this.cdr.detectChanges();
  }

  // Add a method to completely reinitialize the table component
  private reinitializeTableComponent() {
    
    if (this.tableComponent) {
      try {
        // Try to call ngOnInit if it exists
        if (typeof this.tableComponent['ngOnInit'] === 'function') {
          this.tableComponent['ngOnInit']();
        }
        
        // Try to call ngAfterViewInit if it exists
        if (typeof this.tableComponent['ngAfterViewInit'] === 'function') {
          this.tableComponent['ngAfterViewInit']();
        }
        
        // Try to trigger a complete re-render
        if (typeof (this.tableComponent as any)['ngOnChanges'] === 'function') {
          (this.tableComponent as any)['ngOnChanges']({});
        }
        
        // Force change detection
        if (this.tableComponent['cdr'] && typeof this.tableComponent['cdr'].detectChanges === 'function') {
          this.tableComponent['cdr'].detectChanges();
        }
        
        // Also force change detection on parent
        this.cdr.detectChanges();
        
      } catch (error) {
        console.error('[DUNA-EVENTS] Error reinitializing table component:', error);
      }
    } else {
      // Table component is not used anymore (using card view instead)
      // console.warn('[DUNA-EVENTS] Table component not found for reinitialization');
    }
  }

  // Add a method to directly disable search in the table component
  private disableTableSearch() {
    if (this.tableComponent) {
      
      // Access the settings directly on the table component
      if (this.tableComponent.settings) {
        // Create a new settings object with searchable set to false
        this.tableComponent.settings = {
          ...this.tableComponent.settings,
          searchable: false
        };
        
        // Force change detection on the table component
        if (this.tableComponent['cdr'] && typeof this.tableComponent['cdr'].detectChanges === 'function') {
          this.tableComponent['cdr'].detectChanges();
        }
      }
    }
  }

  // Add a getter for settings that ensures searchable is always false
  get tableSettings() {
    // Instead of initializing settings here, return a default value if settings is null
    if (!this.settings) {
      return {
        columns: [
          { 
            key: 'title', 
            name: 'title', 
            header: 'Titel',
            displayName: 'Titel', 
            type: 'text',
            transform: (value: any) => {
              // If the value is already a string, just return it
              if (typeof value === 'string') {
                return value;
              }
              
              // If the value is null or undefined, return default
              if (!value) {
                return 'Unnamed Event';
              }
              
              // If the value has a title property, use it
              if (value.title) {
                return value.title;
              }
              
              // If the value is an event object
              if (typeof value === 'object') {
                // Try to get the title from the description
                if (value.description) {
                  // Try short description first
                  let shortDesc = '';
                  if (value.description.short_description && value.description.short_description['da']) {
                    shortDesc = value.description.short_description['da'].translation_text;
                    if (shortDesc) {
                      return shortDesc;
                    }
                  }
                  
                  // Only try long description if short description was not available
                  if (!shortDesc && value.description.long_description && value.description.long_description['da']) {
                    const longDesc = value.description.long_description['da'].translation_text;
                    if (longDesc) {
                      return longDesc;
                    }
                  }
                }
              }
              
              // If we couldn't find a title, return a default
              return 'Unnamed Event';
            }
          },
          { 
            key: 'date_range', 
            name: 'date_range', 
            header: 'Periode',
            displayName: 'Periode', 
            type: 'text',
            transform: (event: any) => {
              if (!event) return '';
              
              try {
                const startDate = event.start_date ? new Date(event.start_date) : null;
                const endDate = event.end_date ? new Date(event.end_date) : null;
                
                if (!startDate || isNaN(startDate.getTime())) return '';
                
                const dateOptions: Intl.DateTimeFormatOptions = { 
                  day: '2-digit', 
                  month: '2-digit', 
                  year: 'numeric' 
                };
                const timeOptions: Intl.DateTimeFormatOptions = { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                };
                
                const startDateStr = startDate.toLocaleDateString('da-DK', dateOptions);
                const startTimeStr = startDate.toLocaleTimeString('da-DK', timeOptions);
                
                if (!endDate || isNaN(endDate.getTime())) {
                  return `${startDateStr} ${startTimeStr}`;
                }
                
                const endDateStr = endDate.toLocaleDateString('da-DK', dateOptions);
                const endTimeStr = endDate.toLocaleTimeString('da-DK', timeOptions);
                
                return `${startDateStr} ${startTimeStr} - ${endDateStr} ${endTimeStr}`;
              } catch (error) {
                console.error('[DUNA-EVENTS] Error formatting date range:', error);
                return '';
              }
            }
          },
          { 
            key: 'start_date', 
            name: 'start_date', 
            header: 'Dato',
            displayName: 'Dato', 
            type: 'text',
            transform: (event: any) => {
              
              if (!event) {
                return '';
              }
              
              // If the value is already a string, try to parse it as a date
              if (typeof event === 'string') {
                try {
                  const date = new Date(event);
                  if (!isNaN(date.getTime())) {
                    const dateOptions: Intl.DateTimeFormatOptions = { 
                      day: '2-digit', 
                      month: '2-digit', 
                      year: 'numeric' 
                    };
                    const timeOptions: Intl.DateTimeFormatOptions = { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    };
                    
                    const dateStr = date.toLocaleDateString('da-DK', dateOptions);
                    const timeStr = date.toLocaleTimeString('da-DK', timeOptions);
                    
                    return `${dateStr} ${timeStr}`;
                  }
                } catch (error) {
                  console.error('[DUNA-EVENTS] Error parsing date string:', error);
                }
                
                return event;
              }
              
              // For objects, try to get the start_date
              if (event.start_date) {
                try {
                  const date = new Date(event.start_date);
                  if (!isNaN(date.getTime())) {
                    const dateOptions: Intl.DateTimeFormatOptions = { 
                      day: '2-digit', 
                      month: '2-digit', 
                      year: 'numeric' 
                    };
                    const timeOptions: Intl.DateTimeFormatOptions = { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    };
                    
                    const dateStr = date.toLocaleDateString('da-DK', dateOptions);
                    const timeStr = date.toLocaleTimeString('da-DK', timeOptions);
                    
                    return `${dateStr} ${timeStr}`;
                  }
                } catch (error) {
                  console.error('[DUNA-EVENTS] Error formatting date:', error);
                }
                
                return event.start_date;
              }
              
              return '';
            }
          },
          { 
            key: 'event_type', 
            name: 'event_type', 
            header: 'Type',
            displayName: 'Type', 
            type: 'text',
            transform: (value: any, row?: any) => {
              if (!value) return '';
              
              // Hide event type for "no events" row
              if (row && row.isNoEventsRow) {
                return '';
              }
              
              // If the value is already a string, handle it directly
              if (typeof value === 'string') {
                switch(value) {
                  case 'education': return 'Uddannelse';
                  case 'exercise': return 'Øvelse';
                  case 'course': return 'Kursus';
                  default: return value;
                }
              }
              
              // If the value is an event object
              if (typeof value === 'object' && value.event_type) {
                switch(value.event_type) {
                  case 'education': return 'Uddannelse';
                  case 'exercise': return 'Øvelse';
                  case 'course': return 'Kursus';
                  default: return value.event_type;
                }
              }
              
              return '';
            }
          },
          {
            key: 'actions',
            header: '',
            displayName: '',
            type: 'button',
            buttonText: 'Vis',
            buttonColor: 'primary',
            variant: 'raised',
            action: (event: any) => {
              // Don't show action button for "no events" row
              if (event && event.isNoEventsRow) {
                return false; // Return false to prevent button rendering
              }
              if (event && event.guid) {
                this.openCalendarEventModal(event.guid);
              }
              return true; // Return true for normal events
            },
            transform: (value: any, row?: any) => {
              // Hide action button for "no events" row
              if (row && row.isNoEventsRow) {
                return { type: 'text', value: '' }; // Return text type instead of button
              }
              return value;
            }
          }
        ],
        enablePagination: true,
        enableSorting: true,
        enableFiltering: true,
        enableSearch: false,
        pageSize: 10,
        filters: {},
        searchable: false
      };
    }
    
    // Create a new settings object with searchable set to false
    return {
      ...this.settings,
      searchable: false
    };
  }

  private refreshEventCategoryInheritanceContext(): void {
    this.eventCategoryInheritanceContext = buildEventCategoryInheritanceContext(
      Array.isArray(this.data) ? this.data : []
    );
  }

  private updateCalendarData() {
    // CRITICAL: Only update calendar data if we're in calendar view
    // This prevents unnecessary updates when in table view
    if (this.viewType !== 'calendar') {
      return;
    }

    this.refreshEventCategoryInheritanceContext();
    
    // CRITICAL: Prevent concurrent updates, BUT never drop genuinely new data
    // Allow when: clearing, count changed, or event set (guids) changed
    const isClearing = !this.data || this.data.length === 0;
    const currentDataCount = this.data?.length || 0;
    const calendarDataCount = this.calendarData?.length || 0;
    const countChanged = currentDataCount !== calendarDataCount;
    const currentGuids = this.data?.map((e: any) => e?.guid).sort().join(',') || '';
    const calendarGuids = this.calendarData?.map((e: any) => e?.Id).sort().join(',') || '';
    const eventSetChanged = currentGuids !== calendarGuids;
    
    if (this.isUpdatingCalendar && this.calendarData && this.calendarData.length > 0 && !isClearing && !countChanged && !eventSetChanged) {
      return;
    }
    
    // CRITICAL: If count or event set changed, reset the flag so we apply this update
    if (countChanged || eventSetChanged) {
      this.isUpdatingCalendar = false;
    }
    
    
    if (!this.data || !Array.isArray(this.data)) {
      this.calendarData = [];
      // Still update eventSettings to clear calendar
      this.eventSettings = {
        ...this.eventSettings,
        dataSource: []
      };
      this.cdr.detectChanges();
      return;
    }
    
    // Transform data for calendar view
    const newCalendarData = this.data
      .filter((event: EventDTO) => event && event.guid) // Filter out invalid events
      .map((event: EventDTO) => {
        const startTime = new Date(event.start_date);
        const endTime = new Date(event.end_date);
        const hasValidStart = !isNaN(startTime.getTime());
        const hasValidEnd = !isNaN(endTime.getTime());
        const spansMultipleDates = hasValidStart && hasValidEnd &&
          (startTime.getFullYear() !== endTime.getFullYear() ||
           startTime.getMonth() !== endTime.getMonth() ||
           startTime.getDate() !== endTime.getDate());
        const calendarStartTime = new Date(startTime);
        const calendarEndTime = new Date(endTime);

        if (event.event_duration_type === 'multi_day' && !spansMultipleDates) {
          console.warn('[DUNA-EVENTS] multi_day event does not span multiple dates in payload:', {
            guid: event.guid,
            start_date: event.start_date,
            end_date: event.end_date
          });
        }

        // Syncfusion month view: only true multi_day events span calendar days as all-day blocks.
        const useMultiDayCalendarSpan =
          event.event_duration_type === 'multi_day' && spansMultipleDates;
        if (useMultiDayCalendarSpan) {
          calendarStartTime.setHours(0, 0, 0, 0);
          calendarEndTime.setHours(0, 0, 0, 0);
          calendarEndTime.setDate(calendarEndTime.getDate() + 1);
        }

        // Use the optimized title extraction helper
        const title = this.extractEventTitle(event);
        
        // Ensure is_active is properly handled
        const isActive = normalizeEventIsActive(event.is_active);
        const isShown = normalizeEventIsShown(event.is_shown);

        const categoryBg = resolveEventCategoryBackgroundColor(
          event,
          this.eventCategoryColors,
          this.eventCategoryInheritanceContext
        );
        const defaultActiveColor = isActive ? '#445E91' : '#8f8f8f';

        return {
          Id: event.guid,
          Subject: title,
          StartTime: calendarStartTime,
          EndTime: calendarEndTime,
          IsAllDay: useMultiDayCalendarSpan,
          Description: title,
          IsActive: isActive,
          IsShown: isShown,
          CategoryColor: categoryBg || defaultActiveColor,
          // Add additional properties for better event identification
          EventType: event.event_type,
          OrganizationId: event.organization?.id,
          Visibility: event.visibility,
          RegisteredCount: this.getRegisteredCount(event),
          MaxParticipants: this.getEventCapacity(event)
        };
      });
    
    // CRITICAL: Deep comparison to check if calendar data actually changed
    // Compare by Id (guid), dates, and titles to avoid unnecessary updates that cause flashing
    const currentIds = this.calendarData?.map((e: any) => e?.Id).sort().join(',') || '';
    const newIds = newCalendarData?.map((e: any) => e?.Id).sort().join(',') || '';
    const idsChanged = currentIds !== newIds || this.calendarData?.length !== newCalendarData.length;
    
    // CRITICAL: Log when IDs change to help debug update issues
    if (idsChanged) {
    }
    
    // CRITICAL: If going from empty to non-empty or vice versa, always update
    const goingToEmpty = newCalendarData.length === 0 && this.calendarData && this.calendarData.length > 0;
    const goingFromEmpty = newCalendarData.length > 0 && (!this.calendarData || this.calendarData.length === 0);
    const mustUpdateForEmpty = goingToEmpty || goingFromEmpty;
    
    // Also compare event properties to detect if events were modified (not just added/removed)
    let eventsModified = false;
    if (!idsChanged && this.calendarData && this.calendarData.length > 0 && newCalendarData.length > 0) {
      // Same events, but check if any properties changed
      const currentMap = new Map(this.calendarData.map((e: any) => [e.Id, e]));
      for (const newEvent of newCalendarData) {
        const currentEvent = currentMap.get(newEvent.Id);
        if (currentEvent) {
          // Compare key properties that would affect display
          if (currentEvent.Subject !== newEvent.Subject ||
              currentEvent.StartTime.getTime() !== newEvent.StartTime.getTime() ||
              currentEvent.EndTime.getTime() !== newEvent.EndTime.getTime() ||
              currentEvent.CategoryColor !== newEvent.CategoryColor ||
              currentEvent.IsActive !== newEvent.IsActive) {
            eventsModified = true;
            break;
          }
        }
      }
    }
    
    // CRITICAL: If new data is empty and we have data, always update to clear
    const mustClearCalendar = newCalendarData.length === 0 && this.calendarData && this.calendarData.length > 0;
    
    const calendarDataChanged = idsChanged || eventsModified || mustUpdateForEmpty || mustClearCalendar;
    
    // CRITICAL: Log the change detection result for debugging
    if (calendarDataChanged) {
    }
    
    // CRITICAL: Always skip if data is completely unchanged (unless we must update for empty transition)
    // BUT: Never skip if new data is empty - we must clear the calendar
    // This prevents flicker when switching views - if the calendar already shows the same events, don't update
    if (!calendarDataChanged && 
        this.calendarData && 
        this.calendarData.length > 0 && 
        newCalendarData.length > 0 && 
        this.eventSettings?.dataSource) {
      return;
    }
    
    // CRITICAL: If new data is empty, always update to clear the calendar
    if (mustClearCalendar) {
    }
    
    // Only update if data actually changed OR if calendar is truly empty (first ever load)
    const isTrulyEmpty = !this.calendarData || this.calendarData.length === 0;
    if (isTrulyEmpty && newCalendarData.length > 0) {
    } else if (calendarDataChanged) {
      if (mustUpdateForEmpty) {
      } else {
      }
    }
    
    // Clean up any existing tooltips before updating
    this.cleanupTooltips();
    
    // CRITICAL: Check if we're adding events BEFORE updating calendarData
    // This allows us to detect when events are added (e.g., going from 1 to 2 events)
    const isAddingEvents = newCalendarData.length > (this.calendarData?.length || 0);
    const isRemovingEvents = newCalendarData.length < (this.calendarData?.length || 0);
    
    // Update calendar data
    this.calendarData = newCalendarData;
    
    // CRITICAL: If data is empty, explicitly clear the calendar data source immediately
    // This ensures the calendar is cleared even if the update below doesn't trigger properly
    if (newCalendarData.length === 0 && this.eventSettings) {
      this.eventSettings = {
        ...this.eventSettings,
        dataSource: [] // Explicitly set to empty array
      };
      // Also clear the schedule component directly if available
      if (this.scheduleObj && this.scheduleObj.eventSettings) {
        this.scheduleObj.eventSettings.dataSource = [];
        this.scheduleObj.eventSettings = {
          ...this.scheduleObj.eventSettings,
          dataSource: []
        };
        // CRITICAL: Force refresh to clear the calendar display
        // Use requestAnimationFrame to batch the update and reduce flickering
        requestAnimationFrame(() => {
          try {
            // Only use dataBind (more efficient than refresh) to clear
            if (typeof (this.scheduleObj as any).dataBind === 'function') {
              (this.scheduleObj as any).dataBind();
            }
            this.scheduleReapplyAllCompactMonthLayouts();
            this.cdr.markForCheck();
          } catch (e) {
            console.warn('[DUNA-EVENTS] Error refreshing schedule when clearing:', e);
          }
        });
      }
    }
    
    // Log the calendar data for debugging
    
    // CRITICAL: Always update eventSettings when data changed OR when clearing OR when adding/removing events
    // Keep the same array reference when possible to prevent Syncfusion from re-rendering
    // BUT: Always update when clearing or when data count changes to ensure calendar reflects current state
    // Note: isAddingEvents and isRemovingEvents are checked BEFORE calendarData is updated (see above)
    if (calendarDataChanged || newCalendarData.length === 0 || isAddingEvents || isRemovingEvents) {
      // Update calendar settings - create a completely new object to ensure change detection
      this.eventSettings = {
        ...this.eventSettings,
        dataSource: [...this.calendarData] // Create a new array reference only when data changed
      };
      
      
      // CRITICAL: Update Syncfusion schedule component efficiently to prevent flickering
      // Only update once per filter change, and batch all updates together
      if (this.scheduleObj && this.viewType === 'calendar') {
        // Use requestAnimationFrame to batch DOM updates and reduce flickering
        requestAnimationFrame(() => {
          try {
            if (this.scheduleObj.eventSettings) {
              // Create a new array reference to ensure change detection
              // CRITICAL: Use empty array if calendarData is empty to ensure clearing
              const dataSourceArray = this.calendarData.length === 0 ? [] : [...this.calendarData];
              
              // Update both the dataSource property and the object reference in one go
              // This reduces the number of re-renders
              this.scheduleObj.eventSettings = {
                ...this.scheduleObj.eventSettings,
                dataSource: dataSourceArray
              };
              
              // Only call dataBind OR refresh, not both, to reduce flickering
              // dataBind is more efficient as it only rebinds data without full refresh
              if (typeof (this.scheduleObj as any).dataBind === 'function') {
                (this.scheduleObj as any).dataBind();
              }
              this.scheduleReapplyAllCompactMonthLayouts();

              if (this.calendarData.length === 0) {
              } else {
              }
            }
          } catch (e) {
            console.warn('[DUNA-EVENTS] Error updating schedule component:', e);
          }
        });
      } else {
        // If scheduleObj not available, use change detection as fallback
        // But only mark for check, don't force detectChanges to reduce flickering
        this.cdr.markForCheck();
      }
    } else {
      // Data unchanged - keep existing eventSettings reference to prevent re-render
    }
    
    // Note: Store updates are handled elsewhere - this method only updates calendar display
    
    this.cdr.detectChanges();
  }

  /** Organizations are loaded via GET /events/filter-metadata — no event-derived fallback. */
  private updateFilterOrganizations(_events: EventDTO[]): void {
    // no-op
  }

  // Add a method to update the filter result count
  private updateFilterResultCount(count: number): void {
    this.filterState.setResultCount(count);
  }

  // Add a method to handle the filter event with filtered events
  private handleFilterEvent = (event: any) => {
    const startTime = performance.now();
    
    // Skip processing during initial loading, but allow user-initiated filter changes
    if (this.isInitialLoading && !this.hasLoadedInitialData) {
      return;
    }
    
    // Skip processing if we're loading fresh data to prevent overriding
    if (this.isLoadingFreshData) {
      return;
    }
    
    if (event && event.detail) {
      // Update count if available
      if (typeof event.detail.count === 'number') {
      }
      
      // If the event contains the filter value, we know it's a direct update from the filter component
      // In that case, we should avoid the subscribeToFilterChanges update to prevent double processing
      const isDirectUpdate = event.detail.filterValue !== undefined;
      
      // Update with filtered events if available
      if (event.detail.filteredEvents && Array.isArray(event.detail.filteredEvents)) {
        
        // Update our data with the filtered events
        this.data = event.detail.filteredEvents;

        this.loadOrgSettingsForCourseCapacity(event.detail.filteredEvents);
        
        // Update card view data
        this.updateCardData();
        
        // Update both calendar and table views
        const calendarUpdateStart = performance.now();
        this.updateCalendarData();
        const calendarUpdateTime = performance.now() - calendarUpdateStart;
        
        if (this.viewType === 'table') {
          const tableUpdateStart = performance.now();
          this.updateTableDataSource();
          const tableUpdateTime = performance.now() - tableUpdateStart;
        }
        
        // If this was a direct update, temporarily disable the filter subscription
        // to prevent double processing when the filter service emits its change event
        if (isDirectUpdate && this.filterSubscription) {
          
          // Store current filters to avoid duplicate loadData calls
          this.currentFilters = event.detail.filterValue ? 
            this.convertFilterToParams(event.detail.filterValue) : this.currentFilters;
        }
        
        this.cdr.detectChanges();
      }
    }
    
    const totalTime = performance.now() - startTime;
  };

  // Method to clean up all tooltips
  private cleanupTooltips() {
    // Remove all existing tooltips from the DOM
    const existingTooltips = document.querySelectorAll('.duna-event-tooltip');
    existingTooltips.forEach(tooltip => {
      if (tooltip.parentNode) {
        tooltip.parentNode.removeChild(tooltip);
      }
    });

    // Clean up tooltip event listeners on calendar events
    if (this.scheduleObj && this.scheduleObj.element) {
      const eventElements = this.scheduleObj.element.querySelectorAll('.e-appointment');
      eventElements.forEach((element: any) => {
        if (element._tooltipCleanup && typeof element._tooltipCleanup === 'function') {
          element._tooltipCleanup();
        }
      });
    }
    
    // Calendar legend cleanup removed
  }

  // Add a method to complete the initial loading state
  private completeInitialLoading() {
    this.isInitialLoading = false;
    this.cdr.detectChanges(); // Force change detection
    
    // Additional safety: ensure loading state is completely cleared
    setTimeout(() => {
      if (this.isInitialLoading) {
        console.warn('[DUNA-EVENTS] Safety: Force clearing isInitialLoading after timeout');
        this.isInitialLoading = false;
        this.cdr.detectChanges();
      }
    }, 100);
  }

  // Helper method to extract title from event with caching
  private extractEventTitle(event: any): string {
    // First check if there's a direct title property
    if ((event as any).title) {
      return (event as any).title;
    }
    
    // Try to get the title from the description
    if (event.description) {
      // Try short description first
      if (event.description.short_description) {
        // Try Danish translation first
        if (event.description.short_description['da'] && 
            event.description.short_description['da'].translation_text) {
          return event.description.short_description['da'].translation_text;
        }
        // Try English if Danish is not available
        else if (event.description.short_description['en'] && 
            event.description.short_description['en'].translation_text) {
          return event.description.short_description['en'].translation_text;
        }
        // Try any available language
        else {
          const languages = Object.keys(event.description.short_description);
          for (const lang of languages) {
            if (event.description.short_description[lang] && 
                event.description.short_description[lang].translation_text) {
              return event.description.short_description[lang].translation_text;
            }
          }
        }
      }
      
      // Only try long description if we still have the default title (meaning no short description was found)
      if (event.description.long_description) {
        // Try Danish translation first
        if (event.description.long_description['da'] && 
            event.description.long_description['da'].translation_text) {
          return event.description.long_description['da'].translation_text;
        }
        // Try English if Danish is not available
        else if (event.description.long_description['en'] && 
            event.description.long_description['en'].translation_text) {
          return event.description.long_description['en'].translation_text;
        }
        // Try any available language
        else {
          const languages = Object.keys(event.description.long_description);
          for (const lang of languages) {
            if (event.description.long_description[lang] && 
                event.description.long_description[lang].translation_text) {
              return event.description.long_description[lang].translation_text;
            }
          }
        }
      }
    }
    
    return 'Unnamed Event';
  }

  // New methods for responsive filter panel
  public toggleFilterPanel(): void {
    this.isFilterPanelOpen = !this.isFilterPanelOpen;
    
    if (this.isFilterPanelOpen) {
      // When opening, focus the first input in the filter panel after a delay
      setTimeout(() => {
        const firstInput = document.querySelector('.filter-panel input, .filter-panel mat-select') as HTMLElement;
        if (firstInput) {
          firstInput.focus();
        }
      }, 300);
    }
    
    this.cdr.detectChanges();
  }

  public closeFilterPanel(): void {
    const wasOpen = this.isFilterPanelOpen;
    this.isFilterPanelOpen = false;
    
    // Return focus to the filter button when closing
    if (wasOpen) {
      setTimeout(() => {
        const filterButton = document.querySelector('.filter-fab') as HTMLElement;
        if (filterButton) {
          filterButton.focus();
        }
      }, 100);
    }
    
    this.cdr.detectChanges();
  }

  public hasActiveFilters(): boolean {
    // Check if any filters are active (not default state)
    // We can check the current filters or the active filter count
    if (this.activeFilterCount > 0) {
      return true;
    }
    
    // Also check if we have any current filters that aren't default
    if (this.currentFilters && Object.keys(this.currentFilters).length > 0) {
      // Check if any of the filters have non-default values
      const hasNonDefaultFilters = 
        (this.currentFilters.search && this.currentFilters.search.trim()) ||
        (this.currentFilters.organizationIds && this.currentFilters.organizationIds.length > 0) ||
        (this.currentFilters.eventTypes && this.currentFilters.eventTypes.length > 0 && 
         this.currentFilters.eventTypes.length < 3); // Not all types selected
      
      return !!hasNonDefaultFilters;
    }
    
    return false;
  }

  // Method to update active filter count (called from filter component)
  public updateActiveFilterCount(count: number): void {
    this.activeFilterCount = count;
    this.cdr.detectChanges();
  }

  // Setup keyboard navigation for accessibility
  private setupKeyboardNavigation(): void {
    // Create and store the event handler for proper cleanup
    this.keydownHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && this.isFilterPanelOpen) {
        this.closeFilterPanel();
        event.preventDefault();
      }
    };
    
    // Listen for escape key to close filter panel
    document.addEventListener('keydown', this.keydownHandler);
  }

  // Check if user can add events based on their roles
  public canAddEvents(): boolean {
    const userRoles = this.authService.getUserRoles();
    const allowedRoles = ['superadmin', 'orgadmin', 'orgbruger', 'uddansvarlig'];
    
    return userRoles.some(role => allowedRoles.includes(role.toLowerCase()));
  }

  // Check if user can see inactive events based on their roles
  public canSeeInactiveEvents(): boolean {
    const userRoles = this.authService.getUserRoles();
    const allowedRoles = ['superadmin', 'orgadmin', 'orgbruger', 'uddansvarlig'];
    
    return userRoles.some(role => allowedRoles.includes(role.toLowerCase()));
  }

  // Helper method to navigate to a month with events
  public navigateToMonthWithEvents(): void {
    const may2025 = new Date(2025, 4, 1); // May 2025
    this.currentCalendarMonth = may2025;
    this.currentCalendarYear = 2025;
    this.selectedDate = may2025;
    
    // Update the calendar's selected date
    if (this.scheduleObj) {
      this.scheduleObj.selectedDate = may2025;
    }
    
    // Load events for May 2025
    this.dispatchLoadCalendarEvents();
  }

  // Test method to manually trigger month change for debugging
  public testMonthChange(): void {
    const testDate = new Date(2024, 8, 1); // September 2024
    
    // Simulate the month change event
    const mockEvent = {
      currentDate: testDate,
      requestType: 'dateNavigate'
    };
    
    this.onCalendarMonthChange(mockEvent);
  }

  // Test method to load events without date filter
  public testLoadAllEvents(): void {
    
    // Load with empty filters (no date range)
    this.dispatchLoadCalendarEvents({});
  }

  // Test method to load events for different years
  public testLoadDifferentYears(): void {
    
    const years = [2023, 2024, 2025];
    years.forEach(year => {
      const testDate = new Date(year, 8, 1); // September
      this.dispatchLoadCalendarEvents();
    });
  }

  openFilterOverlay() {
    if (this.currentMode === 2 || this.currentMode === 3) {
      this.filterOverlay.open({
        showSearch: true,
        showEventTypes: true,
        showOrganizations: true,
        showResultCount: true,
        showResetButton: true,
        title: 'Filtrer begivenheder',
      });
    } else {
      this.isFilterPanelOpen = !this.isFilterPanelOpen;
    }
  }

  // Date range methods for list view
  onDateRangeChange(): void {
    this.dateRangeChange$.next();
  }

  get listViewStartDateValue(): Date | null {
    return this.isoStringToDate(this.listViewStartDate);
  }

  get listViewEndDateValue(): Date | null {
    return this.isoStringToDate(this.listViewEndDate);
  }

  onListViewStartDateChange(value: Date | null | undefined): void {
    const iso = this.dateToIsoString(value);
    if (!iso) {
      return;
    }
    this.listViewStartDate = iso;
    if (this.viewType === 'table') {
      this.onDateRangeChange();
    }
  }

  onListViewEndDateChange(value: Date | null | undefined): void {
    const iso = this.dateToIsoString(value);
    if (!iso) {
      return;
    }
    this.listViewEndDate = iso;
    if (this.viewType === 'table') {
      this.onDateRangeChange();
    }
  }

  private isoStringToDate(value: string): Date | null {
    if (!value) {
      return null;
    }
    const parsed = new Date(`${value}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private dateToIsoString(value: Date | null | undefined): string | null {
    if (!value || Number.isNaN(value.getTime())) {
      return null;
    }
    return this.formatIsoDate(value);
  }

  applyDatePreset(presetId: string): void {
    if (this.viewType !== 'table') {
      return;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const year = today.getFullYear();

    let start: Date;
    let end: Date;

    switch (presetId) {
      case 'thisYear':
        start = new Date(year, 0, 1);
        end = new Date(year, 11, 31);
        break;
      case 'nextYear':
        start = new Date(year + 1, 0, 1);
        end = new Date(year + 1, 11, 31);
        break;
      case '3months':
        start = new Date(today);
        end = new Date(today);
        end.setMonth(end.getMonth() + 3);
        break;
      case '6months':
        start = new Date(today);
        end = new Date(today);
        end.setMonth(end.getMonth() + 6);
        break;
      case '12months':
        start = new Date(today);
        end = new Date(today);
        end.setMonth(end.getMonth() + 12);
        break;
      case 'restOfYear':
        start = new Date(today);
        end = new Date(year, 11, 31);
        break;
      default:
        return;
    }

    this.listViewStartDate = this.formatIsoDate(start);
    this.listViewEndDate = this.formatIsoDate(end);
    this.hasInitializedDateRange = true;
    this.applyDateRange();
  }

  private formatIsoDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private buildViewAwareParams(base?: any): any {
    const params: any = { ...(base ?? this.currentFilters ?? {}) };
    if (this.viewType === 'calendar') {
      const { startDate, endDate } = this.calculateCalendarViewDateRange();
      params.startDate = startDate;
      params.endDate = endDate;
    } else if (this.viewType === 'table') {
      this.ensureListViewDateRange();
      params.startDate = this.listViewStartDate;
      params.endDate = this.listViewEndDate;
    }
    return params;
  }

  private reloadFacetsForCurrentView(): void {
    if (this.hasLoadedInitialData) {
      this.reapplyLoadedDataAndFacets();
    }
  }

  resetDateRange(): void {
    this.listViewStartDate = '';
    this.listViewEndDate = '';
    this.applyDateRange();
  }

  applyDateRange(): void {
    // List-view date pickers stay in the DOM (hidden in calendar view) and can emit
    // (change) on init — never apply list date ranges while the calendar view is active.
    if (this.viewType !== 'table') {
      return;
    }

    // Reset the attempt flag for new date range
    this.hasAttemptedDataLoad = false;
    
    // Apply date range filter for list view
    const params: any = { ...this.currentFilters };
    
    if (this.listViewStartDate) {
      params.startDate = this.listViewStartDate;
    } else {
      delete params.startDate;
    }
    
    if (this.listViewEndDate) {
      params.endDate = this.listViewEndDate;
    } else {
      delete params.endDate;
    }
    
    // Only initialize defaults if this is the first time (on component initialization)
    // Don't reset user's custom dates when they click "Apply"
    if (!this.hasInitializedDateRange && (!params.startDate && !params.endDate)) {
      this.initializeDefaultDateRange();
      params.startDate = this.listViewStartDate;
      params.endDate = this.listViewEndDate;
    } else if (!params.startDate && !params.endDate) {
      // If dates are empty but already initialized, use current values (don't reset)
      if (this.listViewStartDate) {
        params.startDate = this.listViewStartDate;
      }
      if (this.listViewEndDate) {
        params.endDate = this.listViewEndDate;
      }
    }
    
    if (!this.listViewDateRangeMatchesLoadedData()) {
      this.prepareListViewTransition(true);
      this.cdr.detectChanges();
    }

    this.dispatchLoadCalendarEvents(params);

    // Update filter service and URL with the new date range (only when user changes it)
    this.updateFilterServiceWithDateRange();
  }

  // Calendar month navigation methods
  onCalendarMonthChange(event: any): void {
    
    // Handle different event structures
    let newDate: Date;
    if (event && event.currentDate) {
      newDate = new Date(event.currentDate);
    } else if (event && event.date) {
      newDate = new Date(event.date);
    } else if (event && typeof event === 'object' && event.getTime) {
      newDate = new Date(event);
    } else {
      console.error('[DUNA-EVENTS] Could not extract date from calendar event:', event);
      return;
    }
    
    this.currentCalendarMonth = newDate;
    this.currentCalendarYear = newDate.getFullYear();
    
    
    // Check if we're looking at a reasonable year (not too far in the future)
    if (newDate.getFullYear() > 2024) {
      console.warn(`[DUNA-EVENTS] Looking at future year ${newDate.getFullYear()} - there may be no events`);
    }
    
    // Load events for the new month
    this.dispatchLoadCalendarEvents();
  }

  // Additional calendar event handlers for debugging
  onCalendarActionBegin(event: any): void {
    if (event && event.requestType === 'dateNavigate') {
    }
  }

  onCalendarActionComplete(event: any): void {
    if (event && event.requestType === 'dateNavigate') {
      
      // Get the current date from the schedule component since event doesn't have currentDate
      if (this.scheduleObj && this.scheduleObj.selectedDate) {
        const newDate = new Date(this.scheduleObj.selectedDate);
        this.currentCalendarMonth = newDate;
        this.currentCalendarYear = newDate.getFullYear();
        this.dispatchLoadCalendarEvents();
      } else {
        console.warn('[DUNA-EVENTS] Could not get current date from schedule component');
      }
    }
    setTimeout(() => this.bindCalendarDayClickHandler(), 0);
    this.scheduleReapplyAllCompactMonthLayouts();
  }

  // Add a method to create a calendar legend
  private createCalendarLegend(): void {
    // Remove existing legend if it exists
    const existingLegend = document.querySelector('.calendar-legend');
    if (existingLegend) {
      existingLegend.remove();
    }
    
    // Create legend container
    const legend = document.createElement('div');
    legend.className = 'calendar-legend';
    
    // Apply styles to the legend
    Object.assign(legend.style, {
      position: 'absolute',
      top: '10px',
      right: '10px',
      background: 'rgba(255, 255, 255, 0.95)',
      border: '1px solid #ddd',
      borderRadius: '8px',
      padding: '12px',
      fontSize: '12px',
      fontFamily: 'Pathway Extreme, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      zIndex: '1000',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      minWidth: '200px'
    });
    
    // Create legend title
    const title = document.createElement('div');
    title.textContent = 'Event Legend';
    Object.assign(title.style, {
      fontWeight: '600',
      marginBottom: '8px',
      fontSize: '14px',
      color: '#333'
    });
    
    // Create legend items
    const activeItem = this.createLegendItem('Aktive Events', '#1aaa55', 'solid');
    const inactiveItem = this.createLegendItem('Inaktive Events', '#8f8f8f', 'dashed');
    
    // Add items to legend
    legend.appendChild(title);
    legend.appendChild(activeItem);
    legend.appendChild(inactiveItem);
    
    // Add legend to the schedule container
    if (this.scheduleObj && this.scheduleObj.element) {
      this.scheduleObj.element.appendChild(legend);
    }
  }
  
  private createLegendItem(text: string, color: string, borderStyle: string): HTMLElement {
    const item = document.createElement('div');
    Object.assign(item.style, {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '6px',
      fontSize: '12px'
    });
    
    const indicator = document.createElement('div');
    Object.assign(indicator.style, {
      width: '16px',
      height: '16px',
      backgroundColor: color,
      border: `2px ${borderStyle} ${color === '#1aaa55' ? color : '#666666'}`,
      borderRadius: '3px',
      marginRight: '8px',
      opacity: color === '#8f8f8f' ? '0.8' : '1'
    });
    
    const label = document.createElement('span');
    label.textContent = text;
    Object.assign(label.style, {
      color: '#333'
    });
    
    item.appendChild(indicator);
    item.appendChild(label);
    
    return item;
  }

  /**
   * Parse URL parameters immediately on component initialization
   */
  private parseUrlParameters(): void {
    const urlParams = this.route.snapshot.queryParams;

    const parsedView = parseEventsCalendarViewFromUrl(urlParams['view'] as string | undefined);
    this.viewType = parsedView;
    const listView = parsedView === 'table';
    this.lastToggleState = listView;
    this.componentStateService.setState('events_calendar_toggle', listView);
    
    // Parse date parameters from URL
    if (urlParams['startDate']) {
      this.listViewStartDate = urlParams['startDate'];
    }
    if (urlParams['endDate']) {
      this.listViewEndDate = urlParams['endDate'];
    }
    
    // List-view defaults (today → +3 months) are applied in ensureListViewDateRange()
    // when the user opens list view — not here. Initializing here caused hidden list
    // date pickers to emit (change) and reload calendar data with list dates only.
    if (this.listViewStartDate || this.listViewEndDate) {
      this.hasInitializedDateRange = true;
    }

    if (this.viewType === 'table') {
      this.ensureListViewDateRange();
    }
    
    // Force change detection to ensure date picker updates
    this.cdr.detectChanges();
  }

  private syncViewToUrl(): void {
    const view = eventsCalendarViewToUrlParam(this.viewType);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { view },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /**
   * Update filter service with URL dates after filter is ready
   * CRITICAL: These dates are ONLY for list view, NOT for calendar view
   * The filter's dateRange should only be used by list view
   */
  private updateFilterServiceWithUrlDates(): void {
    const filter = this.filterService.getFilter('events-filter');
    if (filter) {
      filter.getFilter().pipe(take(1)).subscribe((filterValue: EventsFilter) => {
        // Only update dateRange if we have URL dates AND we're in list view
        // For calendar view, we should NOT set dateRange in the filter
        // Calendar view calculates its own dates
        if (this.viewType === 'table' && (this.listViewStartDate || this.listViewEndDate)) {
          const updatedFilter: EventsFilter = {
            ...filterValue,
            dateRange: {
              start: this.listViewStartDate ? new Date(this.listViewStartDate) : null,
              end: this.listViewEndDate ? new Date(this.listViewEndDate) : null
            }
          };
          filter.setFilter(updatedFilter);
        } else {
        }
      });
    }
  }
  private updateFilterServiceWithDateRange(): void {
    const filter = this.filterService.getFilter('events-filter');
    if (filter) {
      filter.getFilter().pipe(take(1)).subscribe((filterValue: EventsFilter) => {
        // Update the date range in the filter service
        const updatedFilter: EventsFilter = {
          ...filterValue,
          dateRange: {
            start: this.listViewStartDate ? new Date(this.listViewStartDate) : null,
            end: this.listViewEndDate ? new Date(this.listViewEndDate) : null
          }
        };
        
        // Update the filter service
        filter.setFilter(updatedFilter);
        
        // Update URL with the new date range
        this.updateUrlWithDateRange();
      });
    }
  }
  
  /**
   * Update URL with current date range parameters
   */
  private updateUrlWithDateRange(): void {
    const queryParams: Record<string, string> = {
      view: eventsCalendarViewToUrlParam(this.viewType),
    };
    
    if (this.listViewStartDate) {
      queryParams['startDate'] = this.listViewStartDate;
    }
    
    if (this.listViewEndDate) {
      queryParams['endDate'] = this.listViewEndDate;
    }
    
    // Navigate with query params to update URL
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  // ===== Card View Methods =====
  
  /**
   * Get current search text from filter service
   */
  public getCurrentSearchText(): string {
    const filter = this.filterService.getFilter('events-filter');
    if (filter) {
      let searchText = '';
      filter.getFilter().pipe(take(1)).subscribe((filterValue: EventsFilter) => {
        if (filterValue && filterValue.searchText) {
          searchText = filterValue.searchText;
        }
      });
      return searchText;
    }
    return '';
  }
  
  private ensureListViewDateRange(): void {
    if (!this.listViewStartDate || !this.listViewEndDate) {
      if (!this.hasInitializedDateRange) {
        this.initializeDefaultDateRange();
      }
    }
  }

  private listViewDateRangeMatchesLoadedData(): boolean {
    if (!this.listViewStartDate || !this.listViewEndDate || !this.lastLoadedDateRange) {
      return false;
    }
    return this.lastLoadedDateRange.startDate === this.listViewStartDate &&
      this.lastLoadedDateRange.endDate === this.listViewEndDate;
  }

  private finishListViewLoadState(): void {
    this.finalizeListViewAfterLoad();
  }

  private onDuplicateLoadSkipped(): void {
    if (this.isInitialLoading && !this.hasLoadedInitialData) {
      return;
    }
    this.finalizeListViewAfterLoad();
  }

  private finalizeListViewAfterLoad(): void {
    this.isListViewRefreshing = false;
    this.isInitialLoading = false;
    this.hasLoadedInitialData = true;

    this.store.select(EventsSelectors.selectCalendarData).pipe(take(1)).subscribe((storeData) => {
      const count = storeData?.length ?? 0;
      this.lastLoadResultWasEmpty = count === 0;
      if (this.viewType === 'table' && count === 0) {
        this.data = [];
        this.filteredCardData = [];
        this.paginatedCardData = [];
      }
      this.cdr.markForCheck();
    });
  }

  private prepareListViewTransition(needsReload: boolean): void {
    if (needsReload) {
      this.isListViewRefreshing = true;
      this.filteredCardData = [];
      this.paginatedCardData = [];
      this.lastLoadResultWasEmpty = false;
    } else {
      this.isListViewRefreshing = false;
      this.updateCardData();
    }
  }

  private filterEventsByListDateRange(events: EventDTO[]): EventDTO[] {
    if (!this.listViewStartDate || !this.listViewEndDate) {
      return events;
    }

    const start = new Date(this.listViewStartDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(this.listViewEndDate);
    end.setHours(23, 59, 59, 999);

    return events.filter(event => {
      if (!event.start_date) {
        return false;
      }
      const eventDate = new Date(event.start_date);
      return eventDate >= start && eventDate <= end;
    });
  }

  /**
   * Update card data based on current data and filters
   */
  private updateCardData(): void {
    let filtered = [...this.data];

    if (this.viewType === 'table') {
      filtered = this.filterEventsByListDateRange(filtered);
    }

    const searchText = (this.filterState.criteria.searchText || '').trim().toLowerCase();
    if (searchText) {
      filtered = filtered.filter((event) => this.eventMatchesSearchText(event, searchText));
    }

    filtered.sort((a, b) => {
      const dateA = a.start_date ? new Date(a.start_date).getTime() : 0;
      const dateB = b.start_date ? new Date(b.start_date).getTime() : 0;
      return dateA - dateB; // Ascending order (earliest first)
    });

    this.filteredCardData = filtered;
    this.updatePaginatedCardData();
  }

  /**
   * Update paginated card data based on current page
   */
  private updatePaginatedCardData(): void {
    const startIndex = this.cardPageIndex * this.cardPageSize;
    const endIndex = startIndex + this.cardPageSize;
    this.paginatedCardData = this.filteredCardData.slice(startIndex, endIndex);
  }

  /**
   * Handle card page change
   */
  public onCardPageChange(newPageIndex: number): void {
    this.cardPageIndex = newPageIndex;
    this.updatePaginatedCardData();
    
    // Scroll to top of cards container
    const container = document.querySelector('.events-cards-container');
    if (container) {
      container.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /**
   * Handle card page size change
   */
  public onCardPageSizeChange(): void {
    this.cardPageIndex = 0; // Reset to first page
    this.updatePaginatedCardData();
  }

  /**
   * Get total number of card pages
   */
  public getCardTotalPages(): number {
    return Math.ceil(this.filteredCardData.length / this.cardPageSize);
  }

  /**
   * Get event type class for styling
   */
  public getEventTypeClass(eventType: string): string {
    return `event-type-${eventType || 'other'}`;
  }

  /**
   * Get event type display name
   */
  public getEventTypeName(eventType: string): string {
    const typeNames: { [key: string]: string } = {
      'education': 'Uddannelse',
      'course': 'Kursus',
      'exercise': 'Øvelse',
      'activity': 'Aktivitet',
      'training': 'Træning',
      'meeting': 'Møde',
      'conference': 'Konference',
      'workshop': 'Workshop',
      'seminar': 'Seminar',
      'other': 'Andet'
    };
    return typeNames[eventType] || 'Begivenhed';
  }

  /**
   * Format event period for display
   */
  public formatEventPeriod(event: any): string {
    if (!event.start_date) {
      return 'Ingen dato';
    }

    const startDate = new Date(event.start_date);
    const endDate = event.end_date ? new Date(event.end_date) : null;

    const formatDate = (date: Date) => {
      const day = date.getDate();
      const month = date.toLocaleDateString('da-DK', { month: 'short' });
      const year = date.getFullYear();
      return `${day}. ${month} ${year}`;
    };

    if (endDate && startDate.getTime() !== endDate.getTime()) {
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }

    return formatDate(startDate);
  }

  /**
   * Format event date and time for display in a single line
   */
  public formatEventDateTime(event: any): string {
    try {
      const startDate = event.start_date ? new Date(event.start_date) : null;
      const endDate = event.end_date ? new Date(event.end_date) : null;
      
      if (!startDate || isNaN(startDate.getTime())) {
        return '';
      }
      
      const dateOptions: Intl.DateTimeFormatOptions = { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
      };
      const timeOptions: Intl.DateTimeFormatOptions = { 
        hour: '2-digit', 
        minute: '2-digit' 
      };
      
      const startDateStr = startDate.toLocaleDateString('da-DK', dateOptions);
      const startTimeStr = startDate.toLocaleTimeString('da-DK', timeOptions);
      
      if (!endDate || isNaN(endDate.getTime())) {
        return `${startDateStr} kl. ${startTimeStr}`;
      }
      
      const endDateStr = endDate.toLocaleDateString('da-DK', dateOptions);
      const endTimeStr = endDate.toLocaleTimeString('da-DK', timeOptions);
      
      // If same date, show: "15. jan 2025 kl. 09:00 - 17:00"
      if (startDateStr === endDateStr) {
        return `${startDateStr} kl. ${startTimeStr} - ${endTimeStr}`;
      }
      
      // If different dates, show: "15. jan 2025 kl. 09:00 - 16. jan 2025 kl. 17:00"
      return `${startDateStr} kl. ${startTimeStr} - ${endDateStr} kl. ${endTimeStr}`;
    } catch (error) {
      console.error('[DUNA-EVENTS] Error formatting event date/time:', error);
      return '';
    }
  }

  /**
   * Truncate description for card display
   */
  public truncateDescription(description: string): string {
    if (!description) return '';
    
    const maxLength = 120;
    if (description.length <= maxLength) {
      return description;
    }
    
    return description.substring(0, maxLength).trim() + '...';
  }

  /**
   * Handle event card click - open modal with event details
   */
  public onEventCardClick(event: any): void {
    if (event && event.guid) {
      this.openCalendarEventModal(event.guid);
    } else {
      console.warn('[DUNA-EVENTS] Event card clicked but no GUID found:', event);
    }
  }

  /**
   * When false, list/calendar must not show spot counts for kursus (course) for this event's org.
   * Non-course events always return true. If org setting is not loaded yet, returns true.
   */
  public showCourseCapacityForEvent(event: any): boolean {
    if (!event || event.event_type !== 'course') {
      return true;
    }
    const raw = event.organization?.id ?? event.organizations_id;
    if (raw == null) {
      return true;
    }
    const numId = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
    if (Number.isNaN(numId)) {
      return true;
    }
    return this.hideCourseCapacityByOrgId.get(numId) !== true;
  }

  private loadOrgSettingsForCourseCapacity(events: any[]): void {
    if (!this.authService.isAuthenticated) {
      return;
    }
    if (!events || !Array.isArray(events)) {
      return;
    }
    const orgIds = new Set<number>();
    for (const e of events) {
      if (e?.event_type === 'course') {
        const raw = e.organization?.id ?? e.organizations_id;
        if (raw != null) {
          const numId = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
          if (!Number.isNaN(numId)) {
            orgIds.add(numId);
          }
        }
      }
    }
    const toFetch = [...orgIds].filter((id) => !this.courseCapacityOrgSettingsFetched.has(id));
    if (toFetch.length === 0) {
      return;
    }

    const requests = toFetch.map((orgId) => {
      const obs = this.componentDataSourceService.executeDataSource('org_settings', { organizationId: orgId });
      if (!obs) {
        return of({ orgId, data: {} as Record<string, unknown> });
      }
      return obs.pipe(
        take(1),
        map((res: { data?: Record<string, unknown> }) => ({ orgId, data: res?.data ?? {} })),
        catchError(() => of({ orgId, data: {} as Record<string, unknown> }))
      );
    });

    forkJoin(requests)
      .pipe(takeUntil(this.destroy$))
      .subscribe((results) => {
        for (const { orgId, data } of results) {
          const v = data['events_hide_course_capacity_public'];
          this.hideCourseCapacityByOrgId.set(orgId, v === true || v === 'true' || v === 1);
          this.courseCapacityOrgSettingsFetched.add(orgId);
        }
        this.cdr.markForCheck();
      });
  }

  /**
   * Get event capacity from room or manual capacity.
   * Manual room size overrides predefined room capacity when both exist.
   */
  public getEventCapacity(event: any): number {
    if (!event) {
      return 0;
    }
    // Manual capacity overrides room capacity when set
    if (event.manual_location_room_capacity !== undefined && event.manual_location_room_capacity !== null) {
      return this.toNonNegativeInt(event.manual_location_room_capacity);
    }
    if (event.predefined_room?.capacity !== undefined && event.predefined_room?.capacity !== null) {
      return this.toNonNegativeInt(event.predefined_room.capacity);
    }
    if (event.MaxParticipants !== undefined && event.MaxParticipants !== null) {
      return this.toNonNegativeInt(event.MaxParticipants);
    }
    return 0;
  }

  /**
   * Get registered participant count
   */
  public getRegisteredCount(event: any): number {
    if (!event) {
      return 0;
    }
    // When the API sends a participant list (including empty), derive the count so
    // waitlist, cancellations, and non-participating rows never consume a seat.
    if (event.participants && Array.isArray(event.participants)) {
      return event.participants.filter((p: any) => {
        if (p.role === 'instructor') return false;
        const st = p.status != null ? String(p.status).toLowerCase() : '';
        if (
          st === 'waitlist' ||
          st === 'not_participant' ||
          st === 'cancelled' ||
          st === 'cancellation'
        ) {
          return false;
        }
        return true;
      }).length;
    }

    if (event.registered_count !== undefined && event.registered_count !== null) {
      return this.toNonNegativeInt(event.registered_count);
    }
    if (event.RegisteredCount !== undefined && event.RegisteredCount !== null) {
      return this.toNonNegativeInt(event.RegisteredCount);
    }

    return 0;
  }

  public getAvailableSpots(event: any): number {
    const capacity = this.getEventCapacity(event);
    const registered = this.getRegisteredCount(event);
    return Math.max(0, capacity - registered);
  }

  /**
   * Get waitlist count
   */
  public getWaitlistCount(event: any): number {
    // First check if we have a pre-calculated count
    if (event.waitlist_count !== undefined) {
      return event.waitlist_count;
    }
    
    // Otherwise count participants with waitlist status, excluding instructors
    if (event.participants && Array.isArray(event.participants)) {
      return event.participants.filter((p: any) => 
        p.status === 'waitlist' && 
        p.role !== 'instructor'
      ).length;
    }
    
    return 0;
  }

  /**
   * Calculate capacity percentage
   */
  public getCapacityPercentage(event: any): number {
    const capacity = this.getEventCapacity(event);
    const registered = this.getRegisteredCount(event);
    
    if (capacity === 0) return 0;
    
    return Math.min((registered / capacity) * 100, 100);
  }

  /**
   * Get capacity status class for styling
   */
  public getCapacityStatusClass(event: any): string {
    const capacity = this.getEventCapacity(event);
    const registered = this.getRegisteredCount(event);

    if (capacity > 0 && registered > capacity) return 'overbooked';

    const percentage = this.getCapacityPercentage(event);
    
    if (percentage >= 100) return 'full';
    if (percentage >= 80) return 'almost-full';
    if (percentage >= 50) return 'filling';
    return 'available';
  }

  /**
   * Get capacity status text
   */
  public getCapacityStatusText(event: any): string {
    const capacity = this.getEventCapacity(event);
    const registered = this.getRegisteredCount(event);

    if (capacity <= 0) {
      return '';
    }

    if (registered > capacity) {
      const extra = registered - capacity;
      return extra === 1 ? '1 over kapacitet' : `${extra} over kapacitet`;
    }

    const available = this.getAvailableSpots(event);
    
    if (available <= 0) return 'Fuldt booket';
    if (available <= 5) return `Kun ${available} pladser tilbage`;
    if (available <= capacity * 0.2) return 'Næsten fuldt';
    return `${available} pladser tilgængelige`;
  }

  private toNonNegativeInt(value: unknown): number {
    const parsed = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(parsed)) {
      return 0;
    }
    return Math.max(0, Math.floor(parsed));
  }

  /**
   * Get catering text in Danish
   */
  public getCateringText(catering: string): string {
    const cateringMap: { [key: string]: string } = {
      'catering_included': 'Forplejning inkluderet',
      'catering_not_included': 'Forplejning ikke inkluderet',
      'self_catering': 'Selvforplejning'
    };
    return cateringMap[catering] || catering;
  }

  /**
   * Get visibility icon
   */
  public getVisibilityIcon(visibility: string): string {
    const iconMap: { [key: string]: string } = {
      'public': 'public',
      'private': 'lock',
      'shared': 'group'
    };
    return iconMap[visibility] || 'visibility';
  }

  /**
   * Get visibility text in Danish
   */
  public getVisibilityText(visibility: string): string {
    const visibilityMap: { [key: string]: string } = {
      'public': 'Offentlig',
      'private': 'Privat',
      'shared': 'Delt'
    };
    return visibilityMap[visibility] || visibility;
  }

  public shouldShowVisibilityLabel(visibility: string | undefined | null): boolean {
    return shouldShowEventVisibilityLabel(visibility, this.authService.isAuthenticated);
  }
}

